import { initializeApp } from './vendor/firebase-app.js';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from './vendor/firebase-auth.js';
import {
  Timestamp,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  connectFirestoreEmulator,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from './vendor/firebase-firestore.js';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from './vendor/firebase-functions.js';
import {
  connectStorageEmulator,
  getBlob,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from './vendor/firebase-storage.js';
import { resolveFirebaseRuntime } from './firebase-runtime.mjs?v=20260806.100';
import { policyForRole } from './role-policy.mjs?v=20260806.100';
import {
  addLauncherItemsForRole,
  bulkAddAvailableForRole,
  mountTopAddLauncher,
  navigationItemsForRole,
  topGlassNavigationMarkup,
} from './top-glass-navigation.mjs?v=20260806.100';
import {
  MISSING_FILTER_CHIPS,
  dashboardQueryControlsMarkup,
} from './dashboard-query-controls.mjs?v=20260806.100';
import {
  assignmentErrorCode,
  assignmentOpeningState,
  buildDispatchCancellationRequest,
  buildDispatchAssignmentRequest,
  buildDispatchEditRequest,
  cancellationErrorCode,
  cancellationReasonOptions,
  createCancellationRequestId,
  createCancellationRetryController,
  createSingleSubmissionGuard,
  dispatchListLabelKey,
  dispatchEditErrorCode,
  isDispatchAssignmentAllowed,
  setCancellationFormPending,
} from './dispatch-assignment-ui.mjs?v=20260806.100';
import { mountAdminAuditLog } from './audit-log.mjs?v=20260806.100';
import { auditBoundary, compareAuditBoundariesDesc } from './audit-utils.mjs?v=20260806.100';
import {
  createCustomerDashboardSource,
  mountCustomerContainerDetail,
  mountCustomerDashboard,
} from './customer-dashboard.mjs?v=20260806.100';
import { enhanceResponsiveUi, mountResponsiveShell } from './responsive-shell.mjs?v=20260806.100';
import {
  clearUnsavedLocale,
  createSessionLoadCoordinator,
  detectLocale,
  formatLocalizedCurrency,
  formatLocalizedDate,
  formatLocalizedDateOnly,
  getLocale,
  getLocalePreferenceRevision,
  localizeDom,
  persistLocalePreference,
  readUnsavedLocale,
  resolveUnsavedLocale,
  setLocale,
  shouldApplyProfileLocale,
  subscribeLocale,
  t,
} from './i18n.mjs?v=20260806.100';
import {
  OCEAN_STATUS_VALUES,
  WorkflowUiError,
  buildCustomerTaskRequest,
  buildFinanceLineRequest,
  buildFinanceSummaryRequest,
  buildStatusCustomerIdentityPatch,
  buildWorkflowActionValues,
  callableErrorCode,
  canOpenWorkflowAction,
  canonicalWorkflowRole,
  evidenceKindForLeg,
  evidenceObjectPath,
} from './workflow-ui.mjs?v=20260806.100';
import {
  buildTodayWorkQueueViewModel,
  createTodayWorkQueueCallbackContract,
} from './today-work-queue.mjs?v=20260806.100';
import {
  loadAllRoleDashboardRows,
  isCanonicalContainerComplete,
  normalizeRoleDashboardRow,
  normalizeRoleDashboardSummary,
  roleDashboardColumnPolicy,
  sortRoleDashboardRows,
} from './role-dashboard-ui.mjs?v=20260806.100';
import {
  DASHBOARD_PAGE_LIMIT,
  createDashboardDataController,
} from './dashboard-data-controller.mjs?v=20260806.100';
import {
  createDashboardBulkCopyController,
  createDashboardClipboard,
} from './dashboard-bulk-copy.mjs?v=20260806.100';
import {
  createDashboardFieldAutosaveController,
  dashboardAutosaveErrorCode,
} from './dashboard-field-autosave.mjs?v=20260806.100';
import { createDashboardPreferenceStore } from './dashboard-preferences.mjs?v=20260806.100';
import { enhanceAuthenticatedInputs } from './authenticated-inputs.mjs?v=20260806.100';
import { createOverlayManager } from './dashboard-overlay-manager.mjs?v=20260806.100';
import { mountGlobalBusyButtons } from './busy-button.mjs?v=20260806.100';
import { hideRouteLoading, showRouteLoading } from './route-loading.mjs?v=20260806.100';
import { mountDashboardFilterPopover, setFilterFacets } from './dashboard-filter-popover.mjs?v=20260806.100';
import { mountDashboardCopyMenu } from './dashboard-copy-menu.mjs?v=20260806.100';
import { mountDashboardBulkEditBar } from './dashboard-bulk-edit.mjs?v=20260806.100';
import {
  appointmentDaysMarkup,
  appointmentQuery,
  appointmentSelectionBounds,
  appointmentSelectionLabel,
  appointmentSelectionOf,
} from './dashboard-appointment-filter.mjs?v=20260806.100';
import { mountBulkAddContainers } from './bulk-add-containers.mjs?v=20260806.100';
import { TYPED_DATE_TIME_FIELDS, isLocalMidnight } from './typed-date.mjs?v=20260806.100';
import {
  BROWSE_SPLIT_EMPTY_STATUSES,
  browseSplitIsEmpty,
  browseSplitShouldBeOpen,
  createBrowseSplit,
  freshestRow,
  partitionRowsForBrowseSplit,
} from './dashboard-browse-split.mjs?v=20260806.100';
import { createWholeQuerySource } from './whole-query-source.mjs?v=20260806.100';
import { blankPaneFilter, mountPaneToolbar, paneChipCounts } from './dashboard-pane-toolbar.mjs?v=20260806.100';
import {
  containerRowById,
  containerRowByNumber,
  rememberContainerRows,
} from './container-row-index.mjs?v=20260806.100';
import {
  columnEditableForRole,
  registryColumnsForRole,
  registryDateFieldsForRole,
  setLearnedFieldOptions,
} from './container-field-registry.mjs?v=20260806.100';
import { containerStatusLabel, containerStatusPresentation } from './container-status-presentation.mjs?v=20260806.100';
import { buildAddressCatalog } from './address-catalog.mjs?v=20260806.100';
import { mountPageEntrance } from './page-entrance.mjs?v=20260806.100';
import { isWorkspaceTaskRoute, mountTaskWorkspace } from './task-workspace.mjs?v=20260806.100';
import { mountAdaptiveContainerTable } from './adaptive-container-table.mjs?v=20260806.100';
import { mountWorkspaceTier } from './adaptive-workspace.mjs?v=20260806.100';
import { mountShellProfileMenu } from './shell-profile-menu.mjs?v=20260806.100';
import {
  applyInitialApplicationMotion,
  createApplicationMotion,
} from './application-motion.mjs?v=20260806.100';
import { mountProfilePage } from './profile-page.mjs?v=20260806.100';
import {
  mountQuoteLocationAdmin,
  quoteLocationAdminMarkup,
  quoteLocationRows,
} from './quote-location-admin.mjs?v=20260806.100';
import {
  emailSyntaxState,
  loginFailureKey,
  loginOrbMarkup,
  mountLoginOrb,
  setLoginFieldState,
} from './login-orb.mjs?v=20260806.100';
import {
  mountStatusTimeline,
  statusOperationsMarkup,
  statusPageModel,
} from './status-operations-page.mjs?v=20260806.100';
import {
  companyOverviewBarMarkup,
  companyOverviewMarkup,
  companyOverviewModel,
} from './company-overview-page.mjs?v=20260806.100';
import { containerKpiMonthlySeries } from './container-kpi-series.mjs?v=20260806.100';
import {
  trendSectionMarkup,
  weeklyFinancialsMarkup,
  weeklyMoney,
} from './dashboard-operations-overview.mjs?v=20260806.100';
import {
  kpiSparklineMarkup,
  monthlyPercentChange,
} from './kpi-sparkline.mjs?v=20260806.100';
// dashboard-enterprise-header.mjs and dashboard-kpi-summary.mjs used to be
// imported here for zero bindings. Removing the KPI band emptied the import
// lists but left the statements, so every page load still fetched and parsed
// two modules nothing called. The modules keep their own tests and stay on
// disk; app.js just no longer asks for them.
import {
  mountSettingsDrivers,
  mountSettingsRoleChanges,
  mountSettingsUserSearch,
  settingsPageMarkup,
} from './settings-page.mjs?v=20260806.100';
import {
  buildDriverProfileUpdatePayload,
  driverProfileSnapshot,
  mountTeamAttendancePage,
  teamAttendancePageMarkup,
  teamDriverProfileMarkup,
  teamOperationalSnapshot,
} from './team-attendance-page.mjs?v=20260806.100';
import {
  mountWorkspaceChoiceControls,
  workspaceChoiceMarkup,
} from './workspace-choice-control.mjs?v=20260806.100';
import {
  buildCreateRecordRequest,
  taskExtraFieldWrites,
  createRecordWorkspaceMarkup,
  mountCustomerQuoteLocationControl,
  mountOperationsDeliveryAddressControl,
} from './workspace-create-records.mjs?v=20260806.100';
import {
  containerInspectionMarkup,
  parseInspectionParam,
} from './container-inspection-workspace.mjs?v=20260806.100';
import {
  DELIVERY_ORDER_CONTENT_TYPES,
  DELIVERY_ORDER_MAX_BYTES,
  deliveryOrderLabel,
  deliveryOrderObjectPath,
  readDeliveryOrderDocument,
} from './delivery-order-upload.mjs?v=20260806.100';
import { workspaceStandardActionFields } from './workspace-standard-action-fields.mjs?v=20260806.100';
import {
  financeCompletionWorkspaceMarkup,
  financeCompletionWorkspaceState,
} from './workspace-finance-completion.mjs?v=20260806.100';
import {
  buildDispatchPlanWorkspaceRequest,
  dispatchPlanWorkspaceMarkup,
  emptyReturnPlanWorkspaceMarkup,
} from './workspace-dispatch-plan.mjs?v=20260806.100';
import {
  dashboardDemoPodEvidence,
  shouldUseDemoPodEvidence,
} from './dashboard-pod-viewer.mjs?v=20260806.100';
import { createContainerTaskModal } from './container-task-modal.mjs?v=20260806.100';
import { batchSearchReport, batchSearchText, dashboardSearchListTerms } from './dashboard-search-list.mjs?v=20260806.100';
import {
  dashboardDateInputValue,
  dashboardEditorValue,
} from './dashboard-cell-interactions.mjs?v=20260806.100';
import { CONTAINER_FIELD_REGISTRY } from './container-field-registry.mjs?v=20260806.100';
import {
  chassisHasLiveMovement,
  chassisCopy,
  chassisMapBarMarkup,
  chassisMapPageMarkup,
  chassisPageModel,
  mountChassisMapPage,
} from './chassis-map-page.mjs?v=20260806.100';
import {
  canEditDashboardPod,
  createPodUploadDraft,
  podUploadEditorMarkup,
} from './dashboard-pod-editor.mjs?v=20260806.100';
import {
  ContainerTaskDetailUiError,
  buildChangeDecisionRequest,
  buildContainerPrefillRequest,
  buildCustomerChangeRequest,
  customerChangeValueControl,
  renderContainerTaskDetailMarkup,
} from './container-task-detail.mjs?v=20260806.100';

const firebaseRuntime = await resolveFirebaseRuntime(globalThis.location);
if (firebaseRuntime.mode === 'blocked') {
  document.getElementById('app').innerHTML = `
    <main class="boot" role="alert">
      Local Firebase access is blocked. Start the loopback demo-tms emulators and open this page with <code>?emulator=1</code>.
    </main>`;
  throw new Error(`Firebase runtime blocked: ${firebaseRuntime.reason}`);
}
const app = initializeApp(firebaseRuntime.firebaseConfig);
const auth = initializeAuth(app, { persistence: browserLocalPersistence });
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');
const storage = getStorage(app);
if (firebaseRuntime.mode === 'emulator') {
  connectAuthEmulator(auth, firebaseRuntime.endpoints.auth, { disableWarnings: true });
  connectFirestoreEmulator(db, ...firebaseRuntime.endpoints.firestore);
  connectFunctionsEmulator(functions, ...firebaseRuntime.endpoints.functions);
  connectStorageEmulator(storage, ...firebaseRuntime.endpoints.storage);
}
const setUserRole = httpsCallable(functions, 'setUserRole');
const createDispatchAssignment = httpsCallable(functions, 'createDispatchAssignment');
const createDispatchPlan = httpsCallable(functions, 'createDispatchPlan');
const cancelDispatchAssignment = httpsCallable(functions, 'cancelDispatchAssignment');
const listEligibleDispatchDrivers = httpsCallable(functions, 'listEligibleDispatchDrivers');
const listCustomerContainerViews = httpsCallable(functions, 'listCustomerContainerViews');
const listMyContainerDashboard = httpsCallable(functions, 'listMyContainerDashboard');
const getContainerInspectionDetail = httpsCallable(functions, 'getContainerInspectionDetail');
const updateMyContainerDashboardField = httpsCallable(functions, 'updateMyContainerDashboardField');
const releaseContainerDispatch = httpsCallable(functions, 'releaseContainerDispatch');
const listMyWorkQueue = httpsCallable(functions, 'listMyWorkQueue');
const confirmContainerActionTodo = httpsCallable(functions, 'confirmContainerActionTodo');
const getTeamTodoTotals = httpsCallable(functions, 'getTeamTodoTotals');
const getMyContainerTaskDetail = httpsCallable(functions, 'getMyContainerTaskDetail');
const prefillContainerInformation = httpsCallable(functions, 'prefillContainerInformation');
const submitCustomerContainerChangeRequest = httpsCallable(functions, 'submitCustomerContainerChangeRequest');
const reviewCustomerContainerChangeRequest = httpsCallable(functions, 'reviewCustomerContainerChangeRequest');
const editDispatchTask = httpsCallable(functions, 'editDispatchTask');
const createCustomerContainerTasks = httpsCallable(functions, 'createCustomerContainerTasks');
const listMyCustomerQuoteLocations = httpsCallable(functions, 'listMyCustomerQuoteLocations');
const manageCustomerQuoteLocation = httpsCallable(functions, 'manageCustomerQuoteLocation');
const listCustomerQuoteLocationsForCustomer = httpsCallable(functions, 'listCustomerQuoteLocationsForCustomer');
const createOperationsContainerTasks = httpsCallable(functions, 'createOperationsContainerTasks');
const createCustomerLoginAccount = httpsCallable(functions, 'createCustomerLoginAccount');
const createBusinessCustomerRecord = httpsCallable(functions, 'createBusinessCustomerRecord');
const createBusinessDriverRecord = httpsCallable(functions, 'createBusinessDriverRecord');
const updateBusinessDriverRecord = httpsCallable(functions, 'updateBusinessDriverRecord');
const createDriverAccount = httpsCallable(functions, 'createDriverAccount');
const completeContainerAction = httpsCallable(functions, 'completeContainerAction');
const mutateContainerFinance = httpsCallable(functions, 'mutateContainerFinance');
const completeDriverTask = httpsCallable(functions, 'completeDriverTask');
const reviewEmptyReturnEvidence = httpsCallable(functions, 'reviewEmptyReturnEvidence');
const submitEmptyReturnCorrection = httpsCallable(functions, 'submitEmptyReturnCorrection');
const manuallyCompleteEmptyReturn = httpsCallable(functions, 'manuallyCompleteEmptyReturn');
const createBusinessContainerRecord = httpsCallable(functions, 'createBusinessContainerRecord');
const getAnytrekChassisSnapshot = httpsCallable(functions, 'getAnytrekChassisSnapshot');
const attendanceGateway = httpsCallable(functions, 'attendanceGateway');

const root = document.getElementById('app');
root.querySelector('.boot')?.setAttribute('hidden', '');
// Every form in the product, including the login page: the button pressed shows
// an open ring while its request runs (Alan, 2026-08-10). Wired once here rather
// than per page, so a page added later gets it without knowing about it.
mountGlobalBusyButtons(document);
applyInitialApplicationMotion({ root: document.documentElement, storage: localStorage });
const activeStatuses = [
  'picked_up_from_terminal',
  'arrived_at_warehouse',
  'out_for_delivery',
];
const normalStatuses = [
  'pending',
  'dispatched',
  'in_progress',
  'loaded_delivered',
  'picked_up_from_terminal',
  'arrived_at_warehouse',
  'out_for_delivery',
  'empty_ready',
  'returning_empty',
  'empty_returned',
  'empty_returned_to_terminal',
  'cancelled',
  'past_due',
];
const quickContainerStatuses = [
  { translationKey: 'dashboard.stagePickedUp', status: 'picked_up_from_terminal' },
  { translationKey: 'dashboard.stageWarehouseArrived', status: 'arrived_at_warehouse' },
  { translationKey: 'dashboard.stageReturned', status: 'empty_returned_to_terminal' },
];
const legTypes = ['load_out_to_customer', 'load_out_to_yard', 'pickup_empty', 'return_empty'];
const userRoles = ['admin', 'dispatcher', 'clerk', 'customer', 'finance'];
const RETURNED_DASHBOARD_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Records the summary and hands the menus what the fleet has taught them.
 *
 * Two callers - the controller's onChange and the opening load - and the open
 * menus have to see the same list whichever ran, so the two steps live together
 * rather than beside each other twice.
 */
function rememberDashboardSummary(summary) {
  state.dashboardSummary = summary;
  setLearnedFieldOptions(summary?.fieldOptions);
  // Only a full summary carries them; a page without one keeps the last set.
  if (Array.isArray(summary?.filterFacets)) setFilterFacets(summary.filterFacets);
  // Which Containers are on a driver's phone, for the dispatcher's row mark
  // (Alan, 2026-08-21). The table repaints itself on the setter, so this arrives
  // with the first page rather than waiting for the next render.
  state.adaptiveTableController?.setActiveDispatchIds(summary?.activeDispatchContainerIds ?? null);
  // ...and both panes of the browse split, which are table instances of their own.
  state.browseSplit?.forEachPane((_, pane) => (
    pane.setActiveDispatchIds?.(summary?.activeDispatchContainerIds ?? null)
  ));
  return summary;
}

const state = {
  user: null,
  role: null,
  // One Firestore listener for "a driver changed something", fanned out to every
  // surface that should react. Created on sign-in, destroyed on sign-out.
  liveRefresh: null,
  claims: {},
  userProfile: null,
  containers: [],
  containerDashboardTruncated: false,
  customers: [],
  customerQuoteLocations: [],
  addressCatalog: null,
  shipments: [],
  drivers: [],
  terminals: [],
  pageCleanup: null,
  pageEntranceCleanup: null,
  customerDashboardController: null,
  responsiveShellController: null,
  profileMenuController: null,
  shellAbortController: null,
  pendingLoginErrorKey: null,
  taskWorkspaceController: null,
  workspaceCaptureHost: null,
  workspaceCaptureLifecycle: null,
  adaptiveWorkspaceController: null,
  adaptiveTableController: null,
  dashboardDataController: null,
  dashboardAutosaveController: null,
  dashboardSummary: null,
  containerInspectionDetails: new Map(),
  applicationMotion: null,
  topAddLauncherController: null,
};
let profileLocaleWrite = Promise.resolve();
const sessionLoadCoordinator = createSessionLoadCoordinator();

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function toast(message) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();
  const el = document.createElement('div');
  el.className = 'toast';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.textContent = message;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 3600);
}

/**
 * The reason a dispatch is being taken back, asked for right where the operator
 * pressed Cancel Dispatch (Alan, 2026-08-16).
 *
 * Its own showModal() on purpose: the Container's page is already a modal dialog,
 * and only another one reaches the top layer above it. Anything less becomes inert
 * the moment it is drawn - which is the same trap the trip panel's release button
 * was built to get around.
 *
 * Resolves null when the operator backs out, and backing out has to be cheap: this
 * button sits beside Release, so it will be pressed by mistake.
 */
function askCancellationReason(task) {
  return new Promise((resolve) => {
    const reasons = cancellationReasonOptions(getLocale());
    const dialog = document.createElement('dialog');
    dialog.className = 'cancel-dispatch-dialog';
    dialog.innerHTML = `<form method="dialog" class="cancel-dispatch-dialog__form">
      <h2>取消派单 Cancel Dispatch</h2>
      <p class="cancel-dispatch-dialog__who">${escapeHtml(task?.driver_name || t('common.unknown'))}</p>
      <label>
        <span>原因 Reason</span>
        <select data-cancel-reason>${reasons.map(({ code, label }) => `<option value="${escapeHtml(code)}">${escapeHtml(label)}</option>`).join('')}</select>
      </label>
      <label data-cancel-explanation-row hidden>
        <span>说明 Explanation</span>
        <textarea data-cancel-explanation rows="2" maxlength="500"></textarea>
      </label>
      <div class="cancel-dispatch-dialog__buttons">
        <button type="button" data-cancel-dismiss class="secondary">返回 Back</button>
        <button type="button" data-cancel-confirm>确认取消 Cancel Dispatch</button>
      </div>
    </form>`;
    const select = dialog.querySelector('[data-cancel-reason]');
    const explanationRow = dialog.querySelector('[data-cancel-explanation-row]');
    const explanation = dialog.querySelector('[data-cancel-explanation]');
    // "Other reason" is the one code the callable will not take on its own - it
    // requires 1..500 characters of explanation, and refuses the whole
    // cancellation without them.
    const syncExplanation = () => {
      const isOther = select.value === 'other';
      explanationRow.hidden = !isOther;
      // display beats [hidden] on anything with its own display rule, so the
      // attribute alone is not enough for a flex/grid row.
      explanationRow.style.display = isOther ? '' : 'none';
    };
    select.addEventListener('change', syncExplanation);
    syncExplanation();
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      dialog.close();
      dialog.remove();
      resolve(value);
    };
    dialog.querySelector('[data-cancel-dismiss]').addEventListener('click', () => finish(null));
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); finish(null); });
    dialog.querySelector('[data-cancel-confirm]').addEventListener('click', () => {
      const code = select.value;
      const text = String(explanation.value ?? '').trim();
      if (code === 'other' && !text) {
        explanation.focus();
        return;
      }
      finish({ code, explanation: code === 'other' ? text : null });
    });
    document.body.appendChild(dialog);
    dialog.showModal();
    select.focus();
  });
}

function routeTo(hash) {
  if (isWorkspaceTaskRoute(hash) && state.taskWorkspaceController) {
    void state.taskWorkspaceController.open(hash);
    return;
  }
  if (window.location.hash === hash) {
    void renderRoute();
    return;
  }
  window.location.hash = hash;
}

async function logoutToLogin() {
  window.history.replaceState(null, '', '#/login');
  await signOut(auth);
}

function hashParams() {
  const queryString = (window.location.hash.split('?')[1] || '').split('#')[0];
  return new URLSearchParams(queryString);
}

function currentRolePolicy() {
  return policyForRole(state.role);
}

function activeRestoreConstraint() {
  return where('archived_by_restore', '==', false);
}

function canManage() {
  const mutations = currentRolePolicy()?.mutations;
  return mutations?.createContainers === true
    && mutations.assignDrivers === true
    && mutations.manageCustomers === true;
}

function canManageContainers() {
  return currentRolePolicy()?.mutations.updateContainers === true;
}

function canUpdateDispatchTasks() {
  return currentRolePolicy()?.mutations.updateDispatchTasks === true;
}

function isCustomer() {
  return state.role === 'customer';
}

function refPath(ref) {
  return ref?.path ?? '';
}

function docIdFromRef(ref) {
  return ref?.id ?? '';
}

function dateValue(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function formatDate(value) {
  const date = dateValue(value);
  if (!date) return '';
  return formatLocalizedDate(date);
}

function createAdminAuditSource() {
  return {
    async loadPage({
      limit: pageLimit,
      cursor = null,
      rangeStartMillis = null,
      rangeEndMillis = null,
    }) {
      const constraints = [];
      if (Number.isFinite(rangeStartMillis)) {
        constraints.push(where('performed_at', '>=', Timestamp.fromMillis(rangeStartMillis)));
      }
      if (Number.isFinite(rangeEndMillis)) {
        constraints.push(where('performed_at', '<=', Timestamp.fromMillis(rangeEndMillis)));
      }
      constraints.push(orderBy('performed_at', 'desc'));
      constraints.push(orderBy(documentId(), 'desc'));
      if (cursor
        && Number.isFinite(cursor.seconds)
        && Number.isFinite(cursor.nanoseconds)
        && cursor.id) {
        constraints.push(startAfter(
          new Timestamp(cursor.seconds, cursor.nanoseconds),
          cursor.id,
        ));
      }
      constraints.push(limit(pageLimit));
      const snap = await getDocs(query(collection(db, 'audit_logs'), ...constraints));
      return {
        logs: snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() })),
        hasOlder: snap.size === pageLimit,
      };
    },
    subscribeNewer(boundary, callback, range = null) {
      const boundaryMillis = boundary.seconds * 1000 + Math.floor(boundary.nanoseconds / 1_000_000);
      const lowerBound = Number.isFinite(range?.startMillis) && range.startMillis > boundaryMillis
        ? Timestamp.fromMillis(range.startMillis)
        : new Timestamp(boundary.seconds, boundary.nanoseconds);
      const constraints = [
        where('performed_at', '>=', lowerBound),
      ];
      if (Number.isFinite(range?.endMillis)) {
        constraints.push(where('performed_at', '<=', Timestamp.fromMillis(range.endMillis)));
      }
      constraints.push(orderBy('performed_at', 'desc'));
      constraints.push(orderBy(documentId(), 'desc'));
      constraints.push(limit(100));
      return onSnapshot(
        query(collection(db, 'audit_logs'), ...constraints),
        (snap) => {
          const logs = snap.docs
            .map((item) => ({ id: item.id, ref: item.ref, ...item.data() }))
            .filter((log) => {
              const candidate = auditBoundary(log);
              return compareAuditBoundariesDesc(candidate, boundary) < 0;
            });
          callback(logs);
        },
        (error) => console.warn('Bounded audit listener stopped.', error?.code || 'unknown'),
      );
    },
  };
}

function timestampFromInput(value) {
  if (!value) return null;
  return Timestamp.fromDate(new Date(value));
}

// A datetime-local input has no timezone, so it only means anything alongside
// the browser that produced it. Resolve it here, where the offset is known, and
// send the instant -- the callable refuses a bare local string rather than
// guessing UTC on the operator's behalf.
function isoFromInput(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function datetimeLocalValue(value) {
  const date = dateValue(value);
  if (!date) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function cents(value) {
  return formatLocalizedCurrency(value);
}

function localizedDateMarkup(value) {
  const date = dateValue(value);
  if (!date) return '';
  return `<span data-i18n-date="${date.getTime()}">${escapeHtml(formatLocalizedDate(date))}</span>`;
}

function localizedCurrencyMarkup(value) {
  const numeric = Number(value);
  const integerCents = Number.isFinite(numeric) ? Math.round(numeric) : 0;
  return `<span data-i18n-cents="${integerCents}">${escapeHtml(formatLocalizedCurrency(integerCents))}</span>`;
}

function centsFromInput(value) {
  const number = Number(String(value ?? '').replaceAll(',', '').replace('$', ''));
  if (!Number.isFinite(number)) return 0;
  return Math.round(number * 100);
}

function uppercaseTrim(value) {
  return String(value ?? '').trim().toUpperCase();
}

function normalizeEmail(value) {
  return String(value ?? '').trim().toLowerCase();
}

async function findCustomerUidByEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const snap = await getDocs(query(
    collection(db, 'users'),
    activeRestoreConstraint(),
    where('email', '==', normalized),
  ));
  const customerDoc = snap.docs.find((item) => item.data().role === 'customer');
  return customerDoc?.id ?? null;
}

function customerLinkBadge(container) {
  return container?.customer_uid
    ? '<span class="badge badge-linked">Linked</span>'
    : '<span class="badge badge-missing">Customer not linked</span>';
}

function customerAccountEmail(customer) {
  return normalizeEmail(
    customer?.account_email
    || customer?.customer_email
    || customer?.email
    || customer?.contact_email
  );
}

function customerAccountUid(customer) {
  return String(customer?.account_uid || customer?.customer_uid || '').trim();
}

function customerOptionLabel(customer) {
  const account = customerAccountEmail(customer) || customerAccountUid(customer) || 'account not linked';
  return `${customer?.name ?? 'Customer'} (${account})`;
}

function customerSelectOptions(selectedId = '') {
  const customers = state.customers
    .filter((customer) => customer.active !== false)
    .sort((a, b) => customerOptionLabel(a).localeCompare(customerOptionLabel(b)));

  return [
    '<option value="">Select customer account</option>',
    ...customers.map((customer) => (
      `<option value="${customer.id}" ${customer.id === selectedId ? 'selected' : ''}>${escapeHtml(customerOptionLabel(customer))}</option>`
    )),
  ].join('');
}

function findCustomerSelection(customerId) {
  return state.customers.find((customer) => customer.id === customerId);
}

function activeDrivers() {
  return state.drivers
    .filter((driver) => driver.active !== false)
    .sort((a, b) => String(a.full_name || '').localeCompare(String(b.full_name || '')));
}

function driverLabel(driver) {
  const truck = driver?.truck_no || driver?.tractor_no;
  return `${driver?.full_name ?? 'Driver'}${truck ? ` - ${truck}` : ''}`;
}

function driverSelectOptions(selectedId = '') {
  return [
    '<option value="">No driver selected</option>',
    ...activeDrivers().map((driver) => (
      `<option value="${driver.id}" ${driver.id === selectedId ? 'selected' : ''}>${escapeHtml(driverLabel(driver))}</option>`
    )),
  ].join('');
}

async function resolveCustomerAccount(customer) {
  if (!customer) return null;

  let accountEmail = customerAccountEmail(customer);
  let accountUid = customerAccountUid(customer);

  if (!accountEmail && accountUid) {
    const userSnap = await getDoc(doc(db, 'users', accountUid));
    if (userSnap.exists()) {
      accountEmail = normalizeEmail(userSnap.data().email);
    }
  }

  if (!accountUid && accountEmail) {
    accountUid = await findCustomerUidByEmail(accountEmail);
  }

  if (accountUid && accountEmail && (
    customer.account_uid !== accountUid ||
    customerAccountEmail(customer) !== accountEmail
  )) {
    await updateDoc(customer.ref, {
      account_email: accountEmail,
      account_uid: accountUid,
      updated_at: serverTimestamp(),
    });
    customer.account_email = accountEmail;
    customer.account_uid = accountUid;
  }

  return {
    customer,
    accountEmail,
    accountUid,
  };
}

function matchingCustomerId(container) {
  return docIdFromRef(container?.customer_ref)
    || state.customers.find((customer) => customerAccountUid(customer) === container?.customer_uid)?.id
    || '';
}

function statusLabel(status) {
  return containerStatusPresentation(status, getLocale()).label;
}

function isComplete(container) {
  return isCanonicalContainerComplete(container);
}

function returnedDate(container) {
  return dateValue(container?.closed_at)
    || dateValue(container?.returned_at)
    || dateValue(container?.empty_returned_at)
    || dateValue(container?.updated_at);
}

function isRecentReturn(container) {
  if (!isComplete(container)) return false;
  const date = returnedDate(container);
  if (!date) return true;
  return Date.now() - date.getTime() <= RETURNED_DASHBOARD_WINDOW_MS;
}

function isDashboardVisible(container) {
  if (container?.status === 'cancelled') return false;
  if (!isComplete(container)) return true;
  return isRecentReturn(container);
}

function isStatsComplete(container) {
  return isComplete(container);
}

function isActiveContainer(container) {
  return activeStatuses.includes(container?.status) && !isComplete(container);
}

function currentStatusBucket(container) {
  if (isComplete(container)) return 'empty_returned';
  if (container?.warehouse_arrived_at || container?.status === 'arrived_at_warehouse') return 'warehouse_arrived';
  if (container?.picked_up_at || container?.status === 'picked_up_from_terminal') return 'picked_up';
  if (container?.status === 'out_for_delivery') return 'active';
  if (container?.urgent_handle || isPastDue(container)) return 'urgent';
  return 'inactive';
}

function stageProgressBucket(container) {
  if (isComplete(container)) return 'empty_returned';
  if (container?.warehouse_arrived_at || container?.status === 'arrived_at_warehouse') return 'warehouse_arrived';
  if (container?.picked_up_at || container?.status === 'picked_up_from_terminal') return 'picked_up';
  return null;
}

function bucketCounts(containers) {
  return containers.reduce((counts, container) => {
    const bucket = currentStatusBucket(container);
    counts[bucket] = (counts[bucket] || 0) + 1;
    return counts;
  }, {});
}

function bestDueDate(container) {
  return dateValue(container?.due_at) || dateValue(container?.lfd_at);
}

function isPastDue(container) {
  if (isComplete(container)) return false;
  if (container?.is_past_due || container?.status === 'past_due') return true;
  const due = bestDueDate(container);
  if (!due) return false;
  const today = new Date();
  return new Date(due.getFullYear(), due.getMonth(), due.getDate()) <
    new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

function isDueToday(container) {
  const due = bestDueDate(container);
  if (!due) return false;
  const now = new Date();
  return due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate();
}

function dueWithinDays(container, days) {
  if (isComplete(container)) return false;
  const due = bestDueDate(container);
  if (!due) return false;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const target = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diff = Math.floor((target.getTime() - start.getTime()) / 86400000);
  return diff >= 0 && diff <= days;
}

function rowClass(container) {
  if (isPastDue(container)) return 'row-red';
  if (container?.urgent_handle || dueWithinDays(container, 2)) return 'row-orange';
  if (isComplete(container) && isRecentReturn(container)) return 'row-green';
  if (isActiveContainer(container)) return 'row-blue';
  if (['pending', 'available'].includes(container?.status)) return 'row-yellow';
  return '';
}

// The Filters panel decides the order when it names a timeline; otherwise the
// approved Ocean Status order stands (Alan, 2026-08-08).
let dashboardTimelineField = '';

// The 可提 chip's state lives beside the other chips' (filterState.appointment);
// these two are what the day strip is drawn from - the last summary's days, and
// the selection it should show pressed.
let dashboardAppointmentSummary = { total: 0, days: {} };
let dashboardAppointmentSelection = null;

// While the split is up the toolbar sits over the left pane, which never shows a
// reported empty, but the server counts its chips over the whole fleet. So the
// chips there count the fleet less the empties - which the right pane's own chips
// count (2026-09-27). Null when there is no split, or the empties have not yet been
// seen whole.
let dashboardSplitCountExclusion = null;

function leftPaneChipCount(count, excluded) {
  return Number.isSafeInteger(count) && Number.isSafeInteger(excluded)
    ? Math.max(0, count - excluded) : count;
}

function renderDashboardAppointmentDays() {
  const strip = root.querySelector('[data-appointment-days]');
  if (!strip) return;
  // [hidden] on its own would lose to the strip's display rule; see
  // .dashboard-appointment-days[hidden] in adaptive-workspace.css.
  strip.hidden = !dashboardAppointmentSelection;
  strip.innerHTML = dashboardAppointmentSelection ? appointmentDaysMarkup({
    days: dashboardAppointmentSummary.days,
    total: dashboardAppointmentSummary.total,
    selected: dashboardAppointmentSelection,
    locale: getLocale(),
  }) : '';
}

function sortDashboardContainers(containers) {
  return sortRoleDashboardRows(containers, { timeline: dashboardTimelineField });
}

function quickStatusTimestamp(container, status) {
  if (status === 'picked_up_from_terminal') return dateValue(container?.picked_up_at);
  if (status === 'arrived_at_warehouse') return dateValue(container?.warehouse_arrived_at);
  if (status === 'empty_returned_to_terminal') return dateValue(container?.empty_returned_at);
  return null;
}

function allQuickStatusesComplete(container) {
  return Boolean(container?.picked_up_at && container?.warehouse_arrived_at && container?.empty_returned_at);
}

function quickStatusButtons(container) {
  if (!canManageContainers()) return '';
  return `
    <div class="quick-status-actions">
      ${quickContainerStatuses.map((item) => {
        const completedAt = quickStatusTimestamp(container, item.status);
        const label = t(item.translationKey);
        if (completedAt) {
          return `<span class="quick-status-done"><span data-i18n="${item.translationKey}">${escapeHtml(label)}</span> ${localizedDateMarkup(completedAt)}</span>`;
        }
        return `<span class="quick-status-pending"><span data-i18n="${item.translationKey}">${escapeHtml(label)}</span> — Pending</span>`;
      }).join('')}
      <span class="quick-status-readonly">Progress is recorded by Dispatch from the external transport task. Empty Returned requires Dispatch review.</span>
    </div>
  `;
}

function editContainerButton(containerId) {
  if (!canManageContainers()) return '';
  const container = state.containers.find((item) => item.id === containerId);
  if ((container?.closed_at || container?.locked) && state.role !== 'admin') {
    return `<span class="muted" data-i18n="dashboard.locked">${escapeHtml(t('dashboard.locked'))}</span>`;
  }
  return `
    <a
      class="edit-container-link"
      href="#/edit-status/${escapeHtml(containerId)}"
      title="${escapeHtml(t('dashboard.editContainer'))}"
      aria-label="${escapeHtml(t('dashboard.editContainer'))}"
      data-i18n-aria-label="dashboard.editContainer"
    >&#9998;</a>
  `;
}

function attachQuickStatusHandlers(rootEl = document) {
  // Kept as a no-op while legacy table renderers are migrated. Status chips
  // are intentionally read-only; lifecycle writes belong to Transport Tasks and
  // the Dispatch Empty Return review flow.
  void rootEl;
}

function chargeTotal(container) {
  return [
    'pier_pass_cents',
    'ctf_cents',
    'demurrage_cents',
    'exam_fee_cents',
    'service_fee_cents',
    'per_diem_cents',
    'other_charge_cents',
  ].reduce((sum, field) => sum + Number(container?.[field] ?? 0), 0);
}

// The language picker is gone from the shell (Alan, 2026-08-08). One office, one
// language: a control that silently reworded every screen was a way to end up
// looking at a console nobody else in the room could read. The locale itself is
// untouched - t() still resolves, the stored preference still applies, and
// Profile still sets it through setLocalePreference - so this removes the switch,
// not the ability to switch.

function persistAuthenticatedLocale(locale) {
  const uid = state.user?.uid;
  if (!uid) return Promise.resolve(false);
  // Captured before the write so a superseded choice can be recognised, and so
  // the marker records which profile value the failure was measured against.
  const revisionAtStart = getLocalePreferenceRevision();
  const baseline = state.userProfile?.preferred_locale ?? null;
  profileLocaleWrite = profileLocaleWrite
    .catch(() => false)
    .then(() => persistLocalePreference({
      locale,
      baseline,
      write: () => updateDoc(doc(db, 'users', uid), {
        preferred_locale: locale,
        updated_at: serverTimestamp(),
      }),
      isCurrent: () => state.user?.uid === uid
        && getLocalePreferenceRevision() === revisionAtStart,
      onPersisted: () => {
        if (state.user?.uid === uid && state.userProfile) {
          state.userProfile.preferred_locale = locale;
        }
      },
      onExhausted: () => {
        // Non-blocking and non-modal: the language did change on this device,
        // and the marker keeps it that way, so this is information rather than
        // an error the user must act on.
        toast(t('language.notSaved'));
      },
    }));
  return profileLocaleWrite;
}

function selectLocale(locale) {
  const nextLocale = setLocale(locale, { source: 'user' });
  void persistAuthenticatedLocale(nextLocale);
  return nextLocale;
}

function bindLocaleSelectors(rootElement = root) {
  rootElement.querySelectorAll('[data-locale-select]').forEach((select) => {
    if (select.dataset.localeBound === 'true') return;
    select.dataset.localeBound = 'true';
    select.addEventListener('change', () => selectLocale(select.value));
  });
}

function refreshLocalizedDom(locale = getLocale()) {
  localizeDom(document, locale);
  document.querySelectorAll('[data-locale-select]').forEach((select) => {
    select.value = locale;
    select.setAttribute('aria-label', t('language.label', {}, locale));
  });
  refreshDashboardLocale(locale);
  state.customerDashboardController?.setLocale(locale);
  state.adaptiveTableController?.setLocale(locale);
}

const SHELL_ICON_PATHS = Object.freeze({
  dashboard: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  containers: '<rect x="4" y="6" width="16" height="14" rx="2"/><path d="M8 6V4h8v2M8 11h8m-8 4h8"/>',
  workQueue: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 3.5h6V6H9zM8 11l2 2 5-5m-7 9h8"/>',
  drivers: '<circle cx="12" cy="7" r="3"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/>',
  customers: '<path d="M4 21V7h10v14M14 11h6v10M8 11h2m-2 4h2m-2 4h2m8-4h1m-1 4h1"/>',
  finance: '<circle cx="12" cy="12" r="9"/><path d="M15.5 8.5c-.8-.8-2-1.2-3.5-1.2-2 0-3.2.9-3.2 2.2 0 3.4 6.4 1.5 6.4 4.8 0 1.4-1.3 2.4-3.4 2.4-1.5 0-2.9-.5-3.8-1.4M12 5.5v13"/>',
  reports: '<path d="M4 21V10m5 11V5m6 16v-8m5 8V3"/>',
  team: '<circle cx="9" cy="8" r="3"/><circle cx="17" cy="10" r="2.5"/><path d="M3.5 20v-1.5A5.5 5.5 0 0 1 9 13h.5a5.5 5.5 0 0 1 5.5 5.5V20m.2-5.5a4.5 4.5 0 0 1 5.3 4.4V20"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4v-.2a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9A1.7 1.7 0 0 0 21 10h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/>',
  add: '<path d="M12 5v14M5 12h14"/>',
  shipment: '<path d="M3 7h12v10H3zM15 10h3l3 3v4h-6M7 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"/>',
  chassis: '<path d="M12 21s6.5-5.6 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15.4 12 21 12 21Z"/><circle cx="12" cy="10.5" r="2.5"/>',
});

function shellIcon(name) {
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${SHELL_ICON_PATHS[name] || SHELL_ICON_PATHS.containers}</svg>`;
}

function navLink(hash, translationKey, displayLabel = '', iconName = '') {
  const active = window.location.hash === hash ? 'active' : '';
  const resolvedIcon = iconName || {
    'nav.dashboard': 'dashboard',
    'nav.containers': 'containers',
    'nav.addContainer': 'add',
    'nav.shipment': 'shipment',
    'nav.customers': 'customers',
    'nav.drivers': 'drivers',
    'nav.status': 'reports',
    'nav.team': 'team',
    'nav.customerPortal': 'containers',
    'nav.addTask': 'add',
    'nav.actions': 'workQueue',
    'nav.tasks': 'workQueue',
    'nav.users': 'settings',
  }[translationKey];
  const label = displayLabel || t(translationKey);
  return `<a class="${active}" href="${hash}" data-nav-key="${translationKey}" aria-label="${escapeHtml(label)}"><span class="nav-icon" aria-hidden="true">${shellIcon(resolvedIcon)}</span><span class="nav-label"${displayLabel ? '' : ` data-i18n="${translationKey}"`}>${escapeHtml(label)}</span></a>`;
}

function topbarIcon(name) {
  const paths = {
    calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4m8-4v4M3 10h18"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
    copy: '<rect x="8" y="8" width="11" height="11" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    chevron: '<path d="m8 10 4 4 4-4"/>',
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths[name]}</svg>`;
}

function cleanupActivePage() {
  if (typeof state.pageCleanup === 'function') {
    state.pageCleanup();
  }
  state.pageCleanup = null;
  if (typeof state.pageEntranceCleanup === 'function') {
    state.pageEntranceCleanup();
  }
  state.pageEntranceCleanup = null;
}

function registerActivePageCleanup(cleanup) {
  if (state.workspaceCaptureLifecycle) {
    state.workspaceCaptureLifecycle.pageCleanup?.();
    state.workspaceCaptureLifecycle.pageCleanup = typeof cleanup === 'function' ? cleanup : null;
    return;
  }
  state.pageCleanup = typeof cleanup === 'function' ? cleanup : null;
}

function registerActivePageEntranceCleanup(cleanup) {
  if (state.workspaceCaptureLifecycle) {
    state.workspaceCaptureLifecycle.pageEntranceCleanup?.();
    state.workspaceCaptureLifecycle.pageEntranceCleanup = typeof cleanup === 'function' ? cleanup : null;
    return;
  }
  state.pageEntranceCleanup = typeof cleanup === 'function' ? cleanup : null;
}

function cleanupAdaptiveWorkspace() {
  state.adaptiveTableController?.destroy();
  state.adaptiveTableController = null;
  state.adaptiveWorkspaceController?.destroy();
  state.adaptiveWorkspaceController = null;
}

function shell(content) {
  if (state.workspaceCaptureHost) {
    state.workspaceCaptureHost.innerHTML = content;
    enhanceResponsiveUi(state.workspaceCaptureHost);
    registerActivePageEntranceCleanup(mountPageEntrance({
      container: state.workspaceCaptureHost,
      window,
    }));
    return;
  }
  cleanupActivePage();
  cleanupAdaptiveWorkspace();
  state.responsiveShellController?.destroy();
  state.responsiveShellController = null;
  state.profileMenuController?.destroy();
  state.profileMenuController = null;
  state.applicationMotion?.destroy();
  state.applicationMotion = null;
  state.topAddLauncherController?.destroy();
  state.topAddLauncherController = null;
  state.shellAbortController?.abort();
  state.shellAbortController = new AbortController();
  const navigationItems = navigationItemsForRole(state.role);
  const addItems = addLauncherItemsForRole(state.role);
  const nav = [
    ...navigationItems.map((item) => navLink(item.route, `nav.${item.icon}`, item.label, item.icon)),
    ...addItems.map((item) => navLink(item.route, 'nav.addTask', item.label, item.icon)),
  ];
  const desktopNavigation = topGlassNavigationMarkup({
    role: state.role,
    currentHash: window.location.hash || '#/dashboard',
    iconFor: shellIcon,
    locale: getLocale(),
  });
  const mobileNavItems = nav.slice(0, 4);
  const mobileMoreControl = nav.length > mobileNavItems.length
    ? `<button type="button" data-mobile-nav-toggle aria-expanded="false" aria-controls="primary-navigation"><span aria-hidden="true">&#8943;</span><span>${escapeHtml(t('shell.openNavigation'))}</span></button>`
    : '';

  const userName = state.userProfile?.display_name || state.user?.displayName || state.user?.email?.split('@')[0] || 'User';
  const userInitial = userName.trim().charAt(0).toUpperCase() || 'U';
  root.innerHTML = `
    <div class="app-shell operations-shell authenticated-terminal-shell">
      <aside class="shell-sidebar shell-navigation" id="primary-navigation" data-mobile-nav-panel aria-hidden="true">
        <div class="mobile-nav-heading"><strong><span>AY</span> TMS</strong><button type="button" data-mobile-nav-close aria-label="${escapeHtml(t('shell.closeNavigation'))}" data-i18n-aria-label="shell.closeNavigation"><span aria-hidden="true">&#10005;</span></button></div>
        <div class="brand"><span>AY</span> TMS</div>
        <nav class="nav" aria-label="${escapeHtml(t('shell.primaryNavigation'))}" data-i18n-aria-label="shell.primaryNavigation">
          ${nav.join('')}
        </nav>
        <div class="sidebar-userbar">
          <span class="user-avatar" aria-hidden="true">${escapeHtml(userInitial)}</span>
          <span class="user-identity"><strong>${escapeHtml(userName)}</strong><small>${state.role ? escapeHtml(state.role) : escapeHtml(t('shell.noRole'))}</small></span>
          <button class="secondary" type="button" data-shell-logout data-i18n="shell.logout">${escapeHtml(t('shell.logout'))}</button>
        </div>
      </aside>
      <div class="app-main">
        <header class="topbar">
          <div class="mobile-brand"><img src="./assets/ay-tms-logo.svg?v=20260806.100-2" alt="AY TMS" width="560" height="180"></div>
          <button class="mobile-nav-toggle" type="button" data-mobile-nav-toggle aria-expanded="false" aria-controls="primary-navigation" aria-label="${escapeHtml(t('shell.openNavigation'))}" data-i18n-aria-label="shell.openNavigation"><span aria-hidden="true">&#9776;</span></button>
          <div class="desktop-top-brand" aria-label="AY TMS">
            <img class="desktop-top-brand__mark" src="./assets/ay-tms-logo.svg?v=20260806.100-2" alt="AY TMS" width="560" height="180">
          </div>
          ${desktopNavigation}
          <div class="topbar-task-shelf" data-task-workspace-dock-host aria-live="polite"></div>
          <div class="topbar-spacer"></div>
          <div class="topbar-page-controls" data-page-controls></div>
          <div class="shell-profile-menu">
            <div class="shell-profile-stack">
              <button class="shell-profile-avatar" type="button" data-profile-menu-toggle data-network-state="${navigator.onLine ? 'online' : 'offline'}" aria-expanded="false" aria-controls="shell-profile-menu" aria-label="Open profile menu for ${escapeHtml(userName)}">
                <span class="user-avatar" aria-hidden="true">${escapeHtml(userInitial)}</span><span class="shell-profile-network-dot" aria-hidden="true"></span>
              </button>
              <span class="shell-profile-caption"><strong>${escapeHtml(userName)}</strong><small>${escapeHtml(state.role || t('shell.noRole'))}</small></span>
            </div>
            <div class="shell-profile-popover" id="shell-profile-menu" data-profile-menu hidden>
              <a class="shell-profile-link" href="#/profile" data-profile-link data-i18n="shell.profile">${escapeHtml(t('shell.profile'))}</a>
              <button class="secondary user-menu-logout" type="button" data-shell-logout data-i18n="shell.logout">${escapeHtml(t('shell.logout'))}</button>
            </div>
          </div>
        </header>
        <main class="page">${content}</main>
      </div>
      <button class="mobile-nav-backdrop" type="button" data-mobile-nav-backdrop aria-label="${escapeHtml(t('shell.closeNavigation'))}" data-i18n-aria-label="shell.closeNavigation" hidden></button>
      <nav class="mobile-bottom-nav" aria-label="${escapeHtml(t('shell.primaryNavigation'))}">
        ${mobileNavItems.join('')}
        ${mobileMoreControl}
      </nav>
    </div>
  `;
  state.adaptiveWorkspaceController = mountWorkspaceTier({
    element: root.querySelector('.app-main'),
  });
  document.querySelectorAll('[data-shell-logout]').forEach((button) => {
    button.addEventListener('click', async () => {
      state.taskWorkspaceController?.closeAll();
      state.taskWorkspaceController?.destroy();
      state.taskWorkspaceController = null;
      await logoutToLogin();
    });
  });
  bindLocaleSelectors(root);
  state.profileMenuController = mountShellProfileMenu({ root, document });
  state.applicationMotion = createApplicationMotion({
    root: document.documentElement,
    storage: localStorage,
    userKey: `${state.user.uid}:${state.role}`,
  });
  const profileAvatar = root.querySelector('.shell-profile-avatar');
  const syncNetworkState = () => {
    if (profileAvatar) profileAvatar.dataset.networkState = navigator.onLine ? 'online' : 'offline';
  };
  window.addEventListener('online', syncNetworkState, { signal: state.shellAbortController.signal });
  window.addEventListener('offline', syncNetworkState, { signal: state.shellAbortController.signal });
  enhanceResponsiveUi(root);
  state.responsiveShellController = mountResponsiveShell({ root });
  const workspaceDockHost = root.querySelector('[data-task-workspace-dock-host]');
  if (state.taskWorkspaceController) {
    state.taskWorkspaceController.setDockHost(workspaceDockHost);
  } else {
    state.taskWorkspaceController = mountTaskWorkspace({
      root,
      mountRoot: document.body,
      dockHost: workspaceDockHost,
      document,
      renderRoute: renderTaskWorkspaceRoute,
      async onDone({ panel, target, route }) {
        if (String(route).startsWith('#/inspect/')) return true;
        if (target?.matches('form')) {
          target.requestSubmit?.();
          return false;
        }
        if (target?.matches('[type="submit"]')) {
          target.closest('form')?.requestSubmit?.(target);
          return false;
        }
        if (target) {
          target.click?.();
          return false;
        }
        const submit = [...panel.querySelectorAll('form')]
          .map((form) => form.querySelector('[type="submit"]:not([disabled])'))
          .find(Boolean);
        if (submit) {
          submit.closest('form')?.requestSubmit?.(submit);
          return false;
        }
        const action = panel.querySelector('[data-complete-finance]:not([disabled]), [data-review-decision="confirm"]:not([disabled]), [data-submit-correction]:not([disabled])');
        action?.click();
        return false;
      },
      storageKey: `ay-tms-open-task-workspace:${state.user.uid}`,
    });
  }
  state.topAddLauncherController = mountTopAddLauncher(root, {
    onSelect(route) {
      void state.taskWorkspaceController?.open(route);
    },
  });
  // This used to be skipped when the shell was arriving from the login wash,
  // which no longer exists - the entrance always runs now. The flag it tested
  // went with the wash, and the reference left behind is what threw
  // ReferenceError on every render and reported itself as a permissions failure.
  state.pageEntranceCleanup = mountPageEntrance({
    container: root.querySelector('.page'),
    window,
  });
  const topSearch = root.querySelector('#dashboardSearch');
  // Clicking into a search box selects what is in it, so the next paste replaces the last list instead of joining
  // it (Alan, 2026-10-17: "点击搜索框会直接勾选整个内容因为会经常重复复制粘贴"). Only the click that brings focus
  // selects; a second click places the cursor as usual. Delegated: the empties pane's box comes and goes with 分屏.
  let searchJustFocused = null;
  document.addEventListener('focusin', (event) => {
    if (event.target?.matches?.('#dashboardSearch, [data-pane-search]')) searchJustFocused = event.target;
  }, { signal: state.shellAbortController.signal });
  document.addEventListener('click', (event) => {
    if (event.target !== searchJustFocused) return;
    searchJustFocused = null;
    event.target.select();
  }, { signal: state.shellAbortController.signal });
  document.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && topSearch?.isConnected) {
      event.preventDefault();
      topSearch.focus();
    }
  }, { signal: state.shellAbortController.signal });
}

async function renderTaskWorkspaceRoute(route, host) {
  const [path, param] = String(route).replace('#/', '').split('?')[0].split('/');
  const savedCaptureHost = state.workspaceCaptureHost;
  const savedLifecycle = state.workspaceCaptureLifecycle;
  const lifecycle = { pageCleanup: null, pageEntranceCleanup: null };
  state.workspaceCaptureHost = host;
  state.workspaceCaptureLifecycle = lifecycle;
  try {
    if (path === 'action') await renderContainerActionDetail(param);
    else if (path === 'driver-task') await renderDriverTaskDetail(param);
    else if (path === 'pod-edit') await renderDashboardPodEditor(param);
    else if (path === 'create' && param === 'bulk') await renderBulkAddWorkspace(host);
    else if (path === 'create') await renderCreateRecordWorkspace(param, route, host);
    else if (path === 'inspect') await renderContainerInspectionWorkspace(param, host);
    else throw new Error('Unsupported task route.');
    return () => {
      lifecycle.pageCleanup?.();
      lifecycle.pageEntranceCleanup?.();
    };
  } finally {
    state.workspaceCaptureHost = savedCaptureHost;
    state.workspaceCaptureLifecycle = savedLifecycle;
  }
}

function queueActionDateTimeValue(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 16);
}

async function renderWorkQueueActionWorkspace(param, host) {
  const [containerId, actionType] = String(param || '').split('~');
  if (!containerId || actionType !== 'return_empty_lfd_at_risk') {
    throw new Error('This Work Queue action is invalid.');
  }
  if (state.role !== 'dispatcher' && state.role !== 'admin') {
    throw new Error('Your role cannot update this Work Queue action.');
  }
  const row = state.dashboardDataController?.snapshot?.().rows
    ?.find((item) => item.id === containerId)
    || state.containers.find((item) => item.id === containerId);
  if (!row) throw new Error('This Container is not available in the current role view.');
  const dueAt = dateValue(row.return_empty_lfd_at);
  const dueLabel = dueAt
    ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(dueAt)
    : 'No Return Empty LFD recorded';
  host.innerHTML = `<section class="workflow-page workflow-page--workspace-action">
    <span data-workspace-container-number hidden>${escapeHtml(row.container_no || containerId)}</span>
    <form class="panel workflow-form" data-work-queue-action-form novalidate>
      <h2>Return Appt</h2>
      <p class="workflow-action-due">Return Empty LFD: <strong>${escapeHtml(dueLabel)}</strong></p>
      <div class="workflow-form-grid">
        <label class="workspace-date-field"><span>Return Appt Date</span>
          <input name="actual_empty_return_date" type="date" value="${escapeHtml(queueActionDateTimeValue(row.actual_empty_returned_at).slice(0, 10))}" required>
          <small data-field-error></small>
        </label>
        <label class="workspace-date-field"><span>Time (optional)</span>
          <input name="actual_empty_return_time" type="time" value="${escapeHtml(row.actual_empty_returned_precision === 'date_time' ? queueActionDateTimeValue(row.actual_empty_returned_at).slice(11) : '')}">
        </label>
      </div>
      <p class="workflow-form-error" data-work-queue-action-error role="alert"></p>
      <button type="submit" hidden>Update</button>
    </form>
  </section>`;
  applySystemInputGlow(host);
  const form = host.querySelector('[data-work-queue-action-form]');
  const errorHost = host.querySelector('[data-work-queue-action-error]');
  let pending = false;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    errorHost.textContent = '';
    if (!form.reportValidity()) return;
    pending = true;
    try {
      const value = {
        date: form.elements.actual_empty_return_date.value,
        time: form.elements.actual_empty_return_time.value || '',
      };
      await state.dashboardAutosaveController?.save({
        containerId,
        field: 'actual_empty_returned_at',
        value,
      });
      form.classList.add('is-confirmed');
      toast('Return Appt saved.');
      await new Promise((resolve) => window.setTimeout(resolve, 260));
      await state.taskWorkspaceController?.confirmRoute(
        `#/queue-action/${encodeURIComponent(containerId)}~return_empty_lfd_at_risk`,
      );
    } catch (error) {
      pending = false;
      errorHost.textContent = error?.message || 'The action could not be completed.';
    }
  });
}

async function renderContainerInspectionWorkspace(param, host) {
  const parsed = parseInspectionParam(param, state.role);
  if (!parsed) throw new Error('This Container information window is invalid.');
  const row = state.containers.find((container) => container.id === parsed.id);
  if (!row) throw new Error('This Container is not available in the current role view.');
  const columns = registryColumnsForRole(state.role);
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const cacheKey = `${state.user.uid}:${state.role}:${row.id}`;
  let related;
  if (!state.containerInspectionDetails.has(cacheKey)) {
    related = getContainerInspectionDetail({ container_id: row.id })
      .then((result) => result?.data || null)
      .catch(() => null);
    state.containerInspectionDetails.set(cacheKey, related);
  } else {
    related = state.containerInspectionDetails.get(cacheKey);
  }
  related = await related;
  state.containerInspectionDetails.set(cacheKey, related);
  host.innerHTML = containerInspectionMarkup({
    row,
    section: parsed.section,
    role: state.role,
    columns,
    related,
    formatValue(key, item) {
      const column = byKey.get(key);
      if (!column) return '';
      return formatAdaptiveContainerCell(column, item);
    },
  });
}

// 批量新增 (Alan, 2026-09-26). The page is web/bulk-add-containers.mjs; this hands
// it the four things that need Firebase. Existence is asked of the rows the
// Dashboard has already loaded, because the rules let only Admin and Dispatch read
// a raw Container - and the callable refuses a taken number anyway, by name.
async function renderBulkAddWorkspace(host) {
  if (!bulkAddAvailableForRole(state.role)) throw new Error('This account cannot add Containers in bulk.');
  const customers = state.customers.length ? state.customers : await loadCustomers();
  const page = mountBulkAddContainers({
    host,
    role: state.role,
    locale: () => getLocale(),
    customers,
    registry: CONTAINER_FIELD_REGISTRY,
    checkExisting: async (numbers) => new Set(numbers.filter((number) => containerRowByNumber(number))),
    // The customer's approved delivery addresses, the way Add Task lists them (by account).
    loadAddresses: async (customerName) => {
      const wanted = String(customerName ?? '').trim().toLowerCase();
      const uid = customers.find((customer) => String(customer?.name ?? '').trim().toLowerCase() === wanted)?.account_uid;
      if (!uid) return [];
      const response = await listCustomerQuoteLocationsForCustomer({ customer_uid: uid });
      return Array.isArray(response?.data?.locations) ? response.data.locations : [];
    },
    createTask: async (payload) => (await createOperationsContainerTasks(payload))?.data,
    updateField: async (payload) => (await updateMyContainerDashboardField(payload))?.data,
  });
  // The Dashboard under the tab holds the fleet as it was; the new rows appear
  // on its next load rather than on a guess.
  registerActivePageCleanup(() => {
    page.destroy();
    invalidateContainerCache();
  });
}

async function renderCreateRecordWorkspace(type, route, host) {
  // What the plus OFFERS and what a role MAY CREATE are two different questions,
  // and this used to ask the first one for both. The plus adds a Task and nothing
  // else (Alan, 2026-08-09: "这个加号只能加任务"), so once Customer left that menu
  // the route refused it too - #/create/customer answered "This account cannot
  // create that record" for Admin, and there was no link to it anyway (Alan,
  // 2026-08-21: "我在adminpage 根本没看见加顾客的选项").
  //
  // Customers and Drivers ask the capability instead, which is the same pair the
  // callables accept: createBusinessCustomerRecord and createBusinessDriverRecord
  // both take Admin and Dispatcher. Task keeps reading the launcher, because the
  // launcher is where Add Task's own role list was opened up.
  const allowed = type === 'customer' || type === 'driver'
    ? state.role === 'admin'
    : addLauncherItemsForRole(state.role).some((item) => item.type === type);
  if (!allowed) throw new Error('This account cannot create that record.');
  // Every role that gets the Customer picker needs the list behind it. This was
  // still admin and dispatcher after Add Task opened to clerk and finance
  // (Alan, 2026-08-08), so those two saw an empty datalist - and worse, the
  // Delivery Address control resolves a typed name to an account through the same
  // list, so their address dropdown could never load for any customer at all.
  // Firestore already allows the read: the customers rule is isActiveStaff().
  if (type === 'task' && state.role !== 'customer') {
    await loadCustomers();
  }
  if (type === 'task' && state.role === 'customer') {
    const response = await listMyCustomerQuoteLocations({});
    state.customerQuoteLocations = Array.isArray(response?.data?.locations)
      ? response.data.locations : [];
  }
  host.innerHTML = createRecordWorkspaceMarkup({
    type,
    role: state.role,
    customers: state.customers,
    quoteLocations: state.customerQuoteLocations,
  });
  const form = host.querySelector('[data-create-record-form]');
  const errorHost = host.querySelector('[data-create-record-error]');
  const choices = mountWorkspaceChoiceControls(host);
  const quoteLocationControl = mountCustomerQuoteLocationControl(host);
  // Internal roles pick Delivery Address from the named Customer's approved list
  // rather than typing it (Alan, 2026-08-02). The addresses are not known until that
  // Customer is chosen, so the control fetches them itself.
  const deliveryAddressControl = type === 'task' && state.role !== 'customer'
    ? mountOperationsDeliveryAddressControl(host, {
      customers: state.customers,
      listLocations: async (customerUid) => {
        const response = await listCustomerQuoteLocationsForCustomer({ customer_uid: customerUid });
        return Array.isArray(response?.data?.locations) ? response.data.locations : [];
      },
    })
    : { destroy() {} };
  let pending = false;
  let requestId = crypto.randomUUID();
  const clearFieldError = (event) => {
    event.target.classList?.remove('is-invalid');
    event.target.closest?.('.create-record-field, .workspace-choice')
      ?.querySelector('[data-field-error]')?.replaceChildren();
  };
  form.addEventListener('input', clearFieldError);
  form.addEventListener('change', () => {
    requestId = crypto.randomUUID();
    if (errorHost) errorHost.textContent = '';
  });
  form.addEventListener('invalid', (event) => {
    event.target.classList.add('is-invalid');
    event.target.closest?.('.create-record-field, .workspace-choice')
      ?.querySelector('[data-field-error]')?.replaceChildren(document.createTextNode('Required'));
  }, true);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !form.reportValidity()) return;
    const values = Object.fromEntries(new FormData(form).entries());
    try {
      pending = true;
      form.setAttribute('aria-busy', 'true');
      if (errorHost) errorHost.textContent = '';
      // FormData hands back a File for the DO input. The callable wants the four
      // keys of the document contract, so the file is read here rather than in the
      // pure request builder, which must stay synchronous and testable.
      const deliveryOrderFile = form.elements.do_document?.files?.[0] || null;
      values.do_document = deliveryOrderFile
        ? await readDeliveryOrderDocument(deliveryOrderFile) : null;
      const payload = buildCreateRecordRequest(type, state.role, values, requestId);
      let response;
      if (type === 'customer') {
        // A password typed here means "make the login too" (Alan, 2026-08-21).
        // Blank means the email must already be an active Customer login, which
        // is what createBusinessCustomerRecord has always required and the only
        // thing this form could do before - making one meant the Firebase console
        // and a script.
        //
        // Three calls, each an existing path: create the account, let setUserRole
        // make the role real - it owns the profile document, the tenant and the
        // claims_revision bump - and only then link the Customer. The order
        // matters, because the third refuses an email that is not already an
        // active customer login, which is exactly what the first two produce.
        //
        // Not folded into one callable deliberately: a second definition of what
        // a role IS would drift from setUserRole's, and that is the definition
        // sign-in reads. If a later step fails the account exists and pressing
        // Save again finishes the job - the account creation refuses a duplicate
        // by name, so the retry is safe.
        const accountPassword = String(values.account_password || '');
        if (accountPassword) {
          const created = await createCustomerLoginAccount({
            email: String(values.account_login_email || '').trim().toLowerCase(),
            password: accountPassword,
            display_name: String(values.name || '').trim(),
          });
          const uid = created?.data?.uid;
          if (!uid) throw new Error('The Customer login was not created.');
          await setUserRole({ uid, role: 'customer' });
        }
        response = await createBusinessCustomerRecord(payload);
      }
      else if (type === 'driver') response = await createBusinessDriverRecord(payload);
      else if (state.role === 'customer') response = await createCustomerContainerTasks(payload);
      else response = await createOperationsContainerTasks(payload);
      if (type === 'customer' && !response?.data?.customerId) throw new Error('Customer creation was not confirmed.');
      if (type === 'driver' && !response?.data?.driverId) throw new Error('Driver creation was not confirmed.');
      if (type === 'task' && !response?.data?.containerIds?.length) throw new Error('Task creation was not confirmed.');
      if (type === 'customer') state.customers = [];
      if (type === 'driver') state.drivers = [];
      // SSL / Terminal / Status / ETA / LFD / Note, typed on the Task, written now that
      // the Container exists - each the way a cell edit writes it, so each keeps its own
      // permission check, revision and audit line. Nothing here can undo or block the
      // add: what cannot be saved is named and left for the table (Alan, 2026-09-30:
      // "不要有太多的限制").
      let notSaved = [];
      if (type === 'task' && state.role !== 'customer') {
        const extras = taskExtraFieldWrites({ role: state.role, values });
        notSaved = [...extras.skipped];
        let revision = 0;
        for (const { field, value, label } of extras.writes) {
          try {
            const result = await updateMyContainerDashboardField({
              containerId: response.data.containerIds[0], field, value,
              expectedRevision: revision, requestId: crypto.randomUUID(),
            });
            const next = result?.data?.informationRevision;
            revision = Number.isSafeInteger(next) ? next : revision + 1;
          } catch (error) {
            // A value the Container already holds is the answer asked for, not a failure.
            if (dashboardAutosaveErrorCode(error) !== 'NO_CHANGE') notSaved.push(label);
          }
        }
      }
      toast(type === 'task'
        ? `Container Task created.${notSaved.length ? ` \u672a\u4fdd\u5b58 not saved: ${notSaved.join(', ')} \u2014 set in the table.` : ''}`
        : `${type === 'customer' ? 'Customer' : 'Driver'} added.`);
      await state.taskWorkspaceController?.confirmRoute(route);
    } catch (error) {
      if (errorHost) errorHost.textContent = error?.message || 'The record could not be saved.';
    } finally {
      pending = false;
      form.removeAttribute('aria-busy');
    }
  });
  applySystemInputGlow(form);
  registerActivePageCleanup(() => {
    quoteLocationControl.destroy();
    deliveryAddressControl.destroy();
    choices.destroy();
  });
}

function renderLogin() {
  cleanupActivePage();
  cleanupAdaptiveWorkspace();
  state.taskWorkspaceController?.closeAll();
  state.taskWorkspaceController?.destroy();
  state.taskWorkspaceController = null;
  state.responsiveShellController?.destroy();
  state.responsiveShellController = null;
  state.profileMenuController?.destroy();
  state.profileMenuController = null;
  state.shellAbortController?.abort();
  state.shellAbortController = null;
  root.innerHTML = loginOrbMarkup();

  state.pageCleanup = null;
  state.pageEntranceCleanup = null;

  if (state.pendingLoginErrorKey) {
    const pendingError = document.getElementById('loginError');
    pendingError.dataset.i18n = state.pendingLoginErrorKey;
    pendingError.textContent = t(state.pendingLoginErrorKey);
  }

  const loginForm = document.getElementById('loginForm');
  const loginPage = root.querySelector('[data-login-orb-page]');
  const loginMotionCleanup = mountLoginOrb({ root: loginPage });
  registerActivePageCleanup(loginMotionCleanup);
  const emailInput = document.getElementById('email');
  const password = document.getElementById('password');
  const submitButton = loginForm.querySelector('button[type="submit"]');
  const syncLoginReadiness = () => {
    const ready = emailSyntaxState(emailInput.value) === 'ready' && password.value.length > 0;
    loginForm.dataset.loginReady = ready ? 'true' : 'false';
    submitButton.disabled = !ready;
  };
  // The orb no longer answers each keystroke (Alan, 2026-08-07). Typing is the
  // one moment the operator is not looking at the decoration, and animating it
  // put work on the main thread on every character.
  emailInput.addEventListener('input', syncLoginReadiness);
  emailInput.addEventListener('change', syncLoginReadiness);
  password.addEventListener('input', syncLoginReadiness);
  password.addEventListener('change', syncLoginReadiness);
  root.querySelector('[data-login-password-toggle]')?.addEventListener('click', (event) => {
    const visible = password.type === 'text';
    password.type = visible ? 'password' : 'text';
    event.currentTarget.setAttribute('aria-label', t(visible ? 'login.showPassword' : 'login.hidePassword'));
  });
  let submitting = false;
  syncLoginReadiness();
  loginForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submitting) return;
    state.pendingLoginErrorKey = null;
    const email = document.getElementById('email').value.trim();
    const password = document.getElementById('password').value;
    const error = document.getElementById('loginError');
    delete error.dataset.i18n;
    error.textContent = '';

    if (!email || !password) {
      error.dataset.i18n = 'login.invalidCredentials';
      error.textContent = t('login.invalidCredentials');
      toast(t('login.invalidCredentials'));
      return;
    }

    submitting = true;
    submitButton.disabled = true;
    loginForm.setAttribute('aria-busy', 'true');
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      if (!credential.user) {
        throw new Error('No Firebase user returned.');
      }
    } catch (authError) {
      setLoginFieldState(loginPage, 'password', 'invalid');
      const errorKey = loginFailureKey(authError);
      error.dataset.i18n = errorKey;
      error.textContent = t(errorKey);
      toast(t(errorKey));
      submitting = false;
      submitButton.disabled = false;
      loginForm.removeAttribute('aria-busy');
    }
  });

}

function applySystemInputGlow(rootElement = root) {
  return enhanceAuthenticatedInputs(rootElement, { login: rootElement.closest?.('.login-static-page') !== null });
}

async function loadSession(user, forceRefresh, session) {
  const loaded = await sessionLoadCoordinator.load(session, {
    getPreferenceRevision: getLocalePreferenceRevision,
    loadToken: () => user.getIdTokenResult(forceRefresh),
    loadProfile: (uid) => getDoc(doc(db, 'users', uid)),
  });
  if (!loaded
    || !sessionLoadCoordinator.isCurrent(session)
    || state.user?.uid !== session.uid) return false;

  const claims = loaded.token.claims ?? {};
  const userSnap = loaded.profile;
  const userProfile = userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
  const profileLocale = userProfile?.preferred_locale ?? null;
  const applyProfileLocale = shouldApplyProfileLocale({
    sessionPreferenceRevision: session.preferenceRevisionAtStart,
    preferenceRevisionAtLoad: loaded.preferenceRevisionAtLoad,
    currentPreferenceRevision: getLocalePreferenceRevision(),
  });
  if (!sessionLoadCoordinator.isCurrent(session) || state.user?.uid !== session.uid) return false;

  state.claims = claims;
  state.role = claims.role ?? null;
  state.userProfile = userProfile;
  // The live refresh listener is gone with the Work Queue (Alan, 2026-08-11).
  //
  // It watched a work_queue_signals document to auto-refresh the dashboard when
  // the server moved something. That collection belonged to the Work Queue, which
  // he deleted, and the rules refuse the read - so the listener has never once
  // fired. What it actually did in production was log "Live refresh signal
  // unavailable. Missing or insufficient permissions" on a retry loop, in every
  // session, next to the listMyWorkQueue 500s from the same dead feature.
  //
  // Nothing is lost that was working: the dashboard already refreshes on its own
  // writes, and both consumers of state.liveRefresh are null-safe
  // (`?.subscribe(...) ?? (() => {})` and `?.destroy()`), so leaving it null is
  // the whole removal. If server-driven refresh is wanted back, it needs a
  // collection that exists and a rule that permits it - not this listener.
  state.liveRefresh?.destroy();
  state.liveRefresh = null;
  if (applyProfileLocale) {
    // A language the server never accepted outranks the profile, but only while
    // the profile has not moved since that failure. resolveUnsavedLocale owns
    // that rule; here we either honour the marker and finish the write, or drop
    // it because the server has the newer value.
    const unsaved = resolveUnsavedLocale({
      marker: readUnsavedLocale(),
      profileLocale,
    });
    setLocale(detectLocale({ profileLocale, unsavedLocale: unsaved?.locale ?? null }), {
      persist: false,
      source: 'profile',
    });
    if (unsaved?.shouldRetry) void persistAuthenticatedLocale(unsaved.locale);
    else clearUnsavedLocale();
  } else {
    void persistAuthenticatedLocale(getLocale());
  }
  return sessionLoadCoordinator.isCurrent(session) && state.user?.uid === session.uid;
}

async function routeAfterLogin() {
  const policy = currentRolePolicy();
  if (policy) {
    routeTo(policy.landingRoute);
    return;
  }
  state.pendingLoginErrorKey = 'login.noRole';
  await signOut(auth);
  toast(t('shell.noRoleAssigned'));
  renderLogin();
}

/**
 * How long a loaded fleet is reused before it is fetched again.
 *
 * Four pages call loadContainers() and every one of them refetched the whole
 * fleet, so switching between Dashboard, Company Overview and Driver Profile
 * paid six sequential round trips each time for rows already in memory (Alan,
 * 2026-08-11: "每次切换界面都需要等好几秒"). Thirty seconds is long enough that
 * moving between pages is instant and short enough that a stale fleet cannot
 * survive anything but the switch itself. Every write path calls
 * invalidateContainerCache(), so this never hides an edit.
 */
const CONTAINER_CACHE_MS = 30_000;
let containersLoadedAt = 0;
let containersInFlight = null;

/** Drop the cached fleet, so the next page load goes back to the server. */
function invalidateContainerCache() {
  containersLoadedAt = 0;
  containersInFlight = null;
}

async function loadContainers({ force = false } = {}) {
  if (isCustomer()) throw new Error('Customer raw Container access is disabled.');
  const fresh = !force
    && Array.isArray(state.containers) && state.containers.length > 0
    && Date.now() - containersLoadedAt < CONTAINER_CACHE_MS;
  if (fresh) return state.containers;
  // Two pages rendering at once share one request rather than racing.
  if (containersInFlight && !force) return containersInFlight;
  containersInFlight = fetchContainers().finally(() => { containersInFlight = null; });
  return containersInFlight;
}

async function fetchContainers() {
  // 100 a page, not 20. This walks the whole fleet before its page can render -
  // Company Overview and Driver Profile both need every row - and each page is a
  // sequential callable round trip, so 452 containers meant 23 of them one after
  // another (Alan, 2026-08-08). 100 is what the server already scans per call, so
  // the reads are the same and there are five requests instead of twenty-three.
  //
  // Left at 100 on 2026-08-11. Raising it to 300 needs ROLE_DASHBOARD_SCAN_LIMIT
  // raised with it or the server returns 100 regardless, and that changes how
  // many documents one call reads - a separate change with its own measurement.
  // The cold start above it was 2,832ms against 78ms warm, so the round trips
  // were never the expensive part.
  // The SECOND pager. This one feeds Company Overview and Driver Profile - pages
  // that need every row before they can render anything - and it is not the one
  // the Dashboard table uses. Both are kept on the same page size deliberately:
  // raising one and not the other is how the fleet quietly stayed at two round
  // trips through two deploys on 2026-08-11.
  const result = await loadAllRoleDashboardRows(listMyContainerDashboard, {
    pageLimit: DASHBOARD_PAGE_LIMIT,
    maxRows: 1000,
  });
  state.containers = result.rows;
  state.containerDashboardTruncated = result.truncated;
  containersLoadedAt = Date.now();
  return state.containers;
}

async function loadCustomers() {
  const snap = await getDocs(query(
    collection(db, 'customers'),
    activeRestoreConstraint(),
    orderBy('name', 'asc'),
  ));
  state.customers = snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
  return state.customers;
}

async function loadShipments() {
  const snap = await getDocs(query(
    collection(db, 'shipments'),
    activeRestoreConstraint(),
    orderBy('job_no', 'asc'),
  ));
  state.shipments = snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
  return state.shipments;
}

async function loadDrivers() {
  const snap = await getDocs(query(
    collection(db, 'drivers'),
    activeRestoreConstraint(),
    orderBy('full_name', 'asc'),
  ));
  state.drivers = snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
  return state.drivers;
}

async function loadTerminals() {
  const snap = await getDocs(query(
    collection(db, 'terminals'),
    activeRestoreConstraint(),
    orderBy('name', 'asc'),
  ));
  state.terminals = snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
  return state.terminals;
}

const ADDRESS_CATALOG_CUSTOMER_LIMIT = 80;

async function loadCatalogQuoteLocations(rows) {
  if (state.role === 'customer') {
    if (state.customerQuoteLocations.length) return state.customerQuoteLocations;
    const response = await listMyCustomerQuoteLocations({});
    state.customerQuoteLocations = Array.isArray(response?.data?.locations)
      ? response.data.locations : [];
    return state.customerQuoteLocations;
  }
  // The clerk reads this list too (Alan, 2026-08-21). Opening the Delivery Address
  // column to them was two gates - the control and the callable's edit_roles - and
  // this was a silent third: the callable has ACCEPTED a clerk reader since
  // 2026-08-07, and its own comment says why ("without the read a clerk's menu was
  // empty for every customer"), but this loader still turned them away. A clerk
  // would have got the editor Alan asked for with nothing in it to tick.
  //
  // Finance stays out: they cannot edit an address, so the calls would buy nothing.
  if (!['admin', 'dispatcher', 'clerk'].includes(state.role)) return [];
  // Every customer's list, each location stamped with its customer's name (Alan, 2026-10-30: "每一个货单的送仓地址的下拉
  // 选项请只包含那个顾客有的地址"). The server's location view carries no customer name, so without the stamp no option
  // belonged to anybody and the narrowing to the row's customer never switched on: every row was offered every
  // customer's addresses. And not only the customers on the first page - a row from the thirteenth customer had no list.
  const customers = state.customers.length ? state.customers : await loadCustomers();
  const withLogin = customers
    .filter((customer) => customer.account_uid && String(customer.name ?? '').trim())
    .slice(0, ADDRESS_CATALOG_CUSTOMER_LIMIT);
  const settled = await Promise.allSettled(withLogin.map(
    (customer) => listCustomerQuoteLocationsForCustomer({ customer_uid: customer.account_uid }),
  ));
  return settled.flatMap((result, index) => (result.status === 'fulfilled'
    && Array.isArray(result.value?.data?.locations)
    ? result.value.data.locations.map((location) => ({ ...location, customer_name: String(withLogin[index].name).trim() }))
    : []));
}

// Known addresses for every address dropdown. Values already on a record join the
// catalog as extras so the current value never disappears from the list.
async function loadAddressCatalog(rows = [], columns = []) {
  const addressKeys = columns
    .filter((column) => column.editor === 'address')
    .map((column) => column.key);
  const extras = rows.flatMap((row) => addressKeys.map((key) => row?.[key]));
  try {
    const [quoteLocations, terminals] = await Promise.all([
      loadCatalogQuoteLocations(rows).catch(() => []),
      (state.terminals.length ? Promise.resolve(state.terminals) : loadTerminals()).catch(() => []),
    ]);
    state.addressCatalog = buildAddressCatalog({ quoteLocations, terminals, extras });
  } catch (error) {
    console.warn('Address catalog unavailable.', error);
    state.addressCatalog = buildAddressCatalog({});
  }
  return state.addressCatalog;
}

function filteredContainers(context = 'containers') {
  const search = uppercaseTrim(document.getElementById('containerSearch')?.value ?? '');
  const filter = document.getElementById('containerFilter')?.value ?? (context === 'dashboard' ? 'active' : 'all');
  const rows = state.containers.filter((container) => {
    if (container.status === 'cancelled' && filter !== 'all') return false;
    if (search) {
      const haystack = [
        container.container_no,
        container.dc_container_no,
        container.job_no,
        container.shipment_name,
        container.customer_name,
        container.dispatch_summary?.driverName,
        ...(container.search_keywords || []),
      ].map(uppercaseTrim).join(' ');
      if (!haystack.includes(search)) return false;
    }
    if (context === 'dashboard' && filter === 'all') return isDashboardVisible(container);
    if (filter === 'active') return isActiveContainer(container);
    if (filter === 'urgent') return Boolean(container.urgent_handle);
    if (filter === 'due_today') return isDueToday(container) && !isComplete(container);
    if (filter === 'past_due') return isPastDue(container);
    if (filter === 'empty_returned') return isComplete(container);
    if (filter === 'picked_up') return stageProgressBucket(container) === 'picked_up';
    if (filter === 'warehouse_arrived') return stageProgressBucket(container) === 'warehouse_arrived';
    if (filter === 'completed_on_time') return isComplete(container) && !container.urgent_handle;
    if (filter === 'inactive') return currentStatusBucket(container) === 'inactive';
    return true;
  });
  return context === 'dashboard' ? sortDashboardContainers(rows) : rows;
}

function dashboardColumnCount() {
  const policy = roleDashboardColumnPolicy(state.role);
  return 14 + (policy.showFinance ? 1 : 0) + (canManageContainers() ? 2 : 0);
}

function containerRows(containers) {
  const columnPolicy = roleDashboardColumnPolicy(state.role);
  if (containers.length === 0) {
    return `<tr><td colspan="${dashboardColumnCount()}" class="muted" data-i18n="dashboard.noContainers">${escapeHtml(t('dashboard.noContainers'))}</td></tr>`;
  }
  return containers.map((container) => `
    <tr class="${rowClass(container)}">
      <td><a href="#/container/${container.id}">${escapeHtml(container.container_no)}</a></td>
      <td>${escapeHtml(container.dc_container_no)}</td>
      <td>${escapeHtml(container.job_no || container.shipment_name)}</td>
      <td>${escapeHtml(container.customer_name)}</td>
      <td>${escapeHtml(container.terminal_name)}</td>
      <td>${escapeHtml(container.ssl)}</td>
      <td>${localizedDateMarkup(container.eta_at)}</td>
      <td>${localizedDateMarkup(container.lfd_at)}</td>
      <td>${localizedDateMarkup(container.due_at)}</td>
      <td>${localizedDateMarkup(container.pickup_appointment_at)}</td>
      <td>${localizedDateMarkup(container.delivery_appointment_at)}</td>
      <td><span class="status">${escapeHtml(statusLabel(container.status))}</span></td>
      <td>${container.dispatch_summary?.driverName
        ? escapeHtml(container.dispatch_summary.driverName)
        : `<span data-i18n="${escapeHtml(dispatchListLabelKey(container.dispatch_summary))}">${escapeHtml(t(dispatchListLabelKey(container.dispatch_summary)))}</span>`}</td>
      <td>${container.urgent_handle ? `<span data-i18n="dashboard.yes">${escapeHtml(t('dashboard.yes'))}</span>` : ''}</td>
      ${columnPolicy.showFinance ? `<td>${localizedCurrencyMarkup(chargeTotal(container))}</td>` : ''}
      ${canManageContainers() ? `<td>${quickStatusButtons(container)}</td>` : ''}
      ${canManageContainers() ? `<td class="edit-container-cell">${editContainerButton(container.id)}</td>` : ''}
    </tr>
  `).join('');
}

function dashboardTableHeadings() {
  const columnPolicy = roleDashboardColumnPolicy(state.role);
  return `
    <th data-i18n="dashboard.container">${escapeHtml(t('dashboard.container'))}</th>
    <th data-i18n="dashboard.dcContainer">${escapeHtml(t('dashboard.dcContainer'))}</th>
    <th data-i18n="dashboard.job">${escapeHtml(t('dashboard.job'))}</th>
    <th data-i18n="dashboard.customer">${escapeHtml(t('dashboard.customer'))}</th>
    <th data-i18n="dashboard.terminal">${escapeHtml(t('dashboard.terminal'))}</th>
    <th data-i18n="dashboard.ssl">${escapeHtml(t('dashboard.ssl'))}</th>
    <th data-i18n="dashboard.eta">${escapeHtml(t('dashboard.eta'))}</th>
    <th data-i18n="dashboard.lfd">${escapeHtml(t('dashboard.lfd'))}</th>
    <th data-i18n="dashboard.due">${escapeHtml(t('dashboard.due'))}</th>
    <th data-i18n="dashboard.pickupAppointment">${escapeHtml(t('dashboard.pickupAppointment'))}</th>
    <th data-i18n="dashboard.deliveryAppointment">${escapeHtml(t('dashboard.deliveryAppointment'))}</th>
    <th data-i18n="dashboard.status">${escapeHtml(t('dashboard.status'))}</th>
    <th data-i18n="dashboard.driver">${escapeHtml(t('dashboard.driver'))}</th>
    <th data-i18n="dashboard.urgent">${escapeHtml(t('dashboard.urgent'))}</th>
    ${columnPolicy.showFinance ? `<th data-i18n="dashboard.charges">${escapeHtml(t('dashboard.charges'))}</th>` : ''}
    ${canManageContainers() ? `<th data-i18n="dashboard.quickStatus">${escapeHtml(t('dashboard.quickStatus'))}</th>` : ''}
    ${canManageContainers() ? `<th class="edit-container-heading" data-i18n="dashboard.edit">${escapeHtml(t('dashboard.edit'))}</th>` : ''}
  `;
}

function containerTableHtml(title, context = 'containers') {
  const requestedFilter = hashParams().get('filter');
  const defaultFilter = requestedFilter || 'all';
  return `
    <h1>${escapeHtml(title)}</h1>
    ${state.containerDashboardTruncated ? '<p class="notice" role="status">Showing the first 1,000 authorized Containers. Refine filters to narrow the result.</p>' : ''}
    <div class="toolbar">
      <label>
        <span>Search container, DC container, job, customer, or driver</span>
        <input id="containerSearch" placeholder="Search">
      </label>
      <label>
        <span>Filter</span>
        <select id="containerFilter">
          <option value="active" ${defaultFilter === 'active' ? 'selected' : ''}>Active / 运输中</option>
          <option value="urgent" ${defaultFilter === 'urgent' ? 'selected' : ''}>Urgent</option>
          <option value="due_today" ${defaultFilter === 'due_today' ? 'selected' : ''}>Due Today</option>
          <option value="past_due" ${defaultFilter === 'past_due' ? 'selected' : ''}>Past Due</option>
          <option value="empty_returned" ${defaultFilter === 'empty_returned' ? 'selected' : ''}>Empty Returned</option>
          <option value="picked_up" ${defaultFilter === 'picked_up' ? 'selected' : ''}>已提柜</option>
          <option value="warehouse_arrived" ${defaultFilter === 'warehouse_arrived' ? 'selected' : ''}>到仓库</option>
          <option value="completed_on_time" ${defaultFilter === 'completed_on_time' ? 'selected' : ''}>Completed On Time</option>
          <option value="all" ${defaultFilter === 'all' ? 'selected' : ''}>All</option>
        </select>
      </label>
      ${canManage() ? '<button id="addContainerBtn">Add Container</button>' : ''}
    </div>
    <div id="containerWorkspace" data-review-id="containers.container-table"></div>
  `;
}

function dashboardContainerTableHtml() {
  return `
    <section class="container-operations-panel" aria-label="Container data table">
      ${state.containerDashboardTruncated ? '<p class="notice" role="status">This local preview is limited to the authorized fixture. Refine the Dashboard query to narrow the result.</p>' : ''}
      <!-- The command row lives HERE, above the table, not in the topbar (Alan,
           2026-08-13: "有的电脑屏幕会显示不全。能不能把这些西东下来在和表格放在一起").
           In the topbar it shared one line with the logo, the nav, the task shelf
           and the profile menu, so it got whatever width those left over - and on
           a narrower screen that is not enough for fifteen controls. Down here it
           has the full page width and belongs to the table it controls. -->
      <div class="container-operations-controls" data-table-controls></div>
      <div id="dashboardContainerWorkspace" data-review-id="dashboard.container-table"></div>
      <div class="dashboard-load-status" id="dashboardLoadStatus" aria-live="polite"></div>
    </section>
  `;
}

function statusSlug(value) {
  return String(value || 'pending').toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function humanizeLabel(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// Customer Chassis answers "does the CUSTOMER bring their own chassis?", and the
// column was showing the opposite of the stored fact (Alan, 2026-08-01, confirmed
// against production: all 68 containers storing "yes" carry a chassis number, and
// all 342 storing "no" carry none).
//
// The stored value records what WE did, not what the customer has:
//   "yes"          -> we assigned a chassis -> the customer had none -> CHS "No"
//   "no"           -> we assigned nothing   -> the customer brought their own -> "Yes"
//   "own_chassis"  -> the create form's wording for the same thing -> "Yes"
// So a row reading CHS "Yes" is exactly a row that should have no Chassis # - which
// is what customerSuppliesOwnChassis below gates the Chassis # editor on.
/**
 * Three answers, not two: yes, no, and nobody has said (Alan, 2026-08-10: "chassis
 * 和各种根本没有选择或者填写，可是还是在系统上面显示一个值").
 *
 * This used to be `!== 'yes'`, which quietly made "unanswered" mean "the customer
 * brings their own". A Task booked without the question being asked displayed a
 * confident Yes, and the Chassis # editor closed on it, because the code could not
 * tell silence from an answer. It can now: blank returns null and the column shows
 * N/A.
 */
export function customerChassisState(value) {
  const token = String(value ?? '').trim().toLowerCase();
  if (!token) return null;
  // Yes means own chassis, no means not (Alan, 2026-08-10: "yes 就是 own chassis,
  // no 就是不是 own chassis").
  //
  // It used to mean the opposite: "yes" was the record of what WE did - we assigned
  // a chassis, therefore the customer had none - so the column printed the inverse
  // of the token underneath it and everyone reading the code had to hold the
  // inversion in their head. All 498 rows were cleared to blank first, so no stored
  // value carries the old meaning and nothing needed migrating.
  if (token === 'no') return false;
  return true;
}

export function customerSuppliesOwnChassis(value) {
  return customerChassisState(value) === true;
}

// An unanswered question reads N/A. It is the one honest thing the column can say
// about a value nobody has entered, and it is what every blank choice will show.
function customerChassisLabel(value) {
  const state = customerChassisState(value);
  if (state === null) return 'N/A';
  return state ? 'Yes' : 'No';
}

// Forecast Status has exactly two values and the column only has to distinguish
// them, so the table shows the initials (Alan, 2026-07-31). Every other surface
// keeps the full wording.
const COMPACT_FORECAST_STATUS = Object.freeze({
  'pre-alert': 'P',
  'pre-alert + do': 'P+DO',
});

function compactForecastStatus(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return 'N/A';
  return COMPACT_FORECAST_STATUS[normalized.toLowerCase()] || normalized;
}

/**
 * `compact` is the Container table's presentation: no year on any date and
 * abbreviated Forecast Status. Detail surfaces call this without it and keep the
 * full text.
 */
function formatAdaptiveContainerCell(column, container, { compact = false } = {}) {
  const dateOptions = { includeYear: !compact };
  if (column.key === 'status') return statusLabel(container.status);
  if (column.key === 'next_action') return container.current_action_type
    ? workflowActionLabel(container.current_action_type)
    : (isComplete(container) ? 'Complete — no action' : 'Monitor');
  if (column.key === 'critical_deadline') return formatLocalizedDate(bestDueDate(container), getLocale(), dateOptions);
  if (column.key === 'responsible_role') return humanizeLabel(container.current_responsible_role || 'Operations');
  if (column.key === 'assigned_driver_name') return container.dispatch_summary?.driverName || 'Not assigned';
  if (column.key === 'charges') return formatLocalizedCurrency(chargeTotal(container), getLocale());
  const value = container[column.key];
  if (column.key === 'customer_chassis') return customerChassisLabel(value);
  // Size and Forecast say N/A when nobody has filled them in, the same as Chassis
  // (Alan, 2026-08-10: "那些预报和 size 的选项需要 N/A 的选项如果没有填写的话"). A Task
  // can now be booked on a container number alone, so an unanswered field is the
  // normal state of a fresh row rather than a fault - and an em dash reads as "no
  // value", where N/A reads as "not answered yet", which is what it is.
  if (column.key === 'size_type' || column.key === 'pre_alert_status') {
    if (!String(value ?? '').trim()) return 'N/A';
  }
  // An uploaded delivery order is stored as a gs:// URI. A bucket path is not
  // something to put in front of an operator, so show what it points at
  // (Alan, 2026-08-03: the DO is a file again, and the Task should say so).
  if (column.key === 'do_ref') return deliveryOrderLabel(value) || '—';
  if (column.key === 'pre_alert_status' && compact) return compactForecastStatus(value);
  if (column.key === 'actual_empty_returned_at' && container.actual_empty_returned_precision !== 'date_time') {
    return value ? formatLocalizedDateOnly(value, getLocale(), dateOptions) : '—';
  }
  if (column.format === 'date-only') {
    return value ? formatLocalizedDateOnly(value, getLocale(), dateOptions) : '—';
  }
  // Pickup Appt with no time is that day at local midnight (Alan, 2026-10-10): show the date alone.
  if (TYPED_DATE_TIME_FIELDS.has(column.key) && value && isLocalMidnight(value)) {
    return formatLocalizedDateOnly(value, getLocale(), dateOptions);
  }
  if (column.format === 'date-time' || column.key.endsWith('_at')) {
    return value ? formatLocalizedDate(value, getLocale(), dateOptions) : '—';
  }
  if (column.format === 'currency') {
    return Number.isSafeInteger(value) ? formatLocalizedCurrency(value, getLocale()) : '—';
  }
  if (column.format === 'number') {
    return Number.isFinite(value) ? Number(value).toLocaleString(getLocale()) : '—';
  }
  if (column.format === 'pod') {
    return Number.isSafeInteger(value) && value > 0 ? `${value} photo${value === 1 ? '' : 's'}` : 'View';
  }
  if (column.key === 'ocean_status') {
    return value ? containerStatusPresentation(value, getLocale()).label : '—';
  }
  if (column.key === 'remittance_status') {
    return value ? humanizeLabel(value) : '—';
  }
  if (value === 0 || value === false) return String(value);
  return container[column.key] || '—';
}

function renderAdaptiveContainerActions(container) {
  if (!['admin', 'dispatcher'].includes(state.role)) return '';
  const actions = [];
  const taskId = container.dispatch_summary?.taskId || null;
  if (taskId) {
    actions.push(`<a class="button-link secondary" data-review-id="container.edit-dispatch" href="#/edit-dispatch-task/${encodeURIComponent(taskId)}">Edit / Reassign</a>`);
  } else {
    actions.push(`<a class="button-link" data-review-id="container.assign-driver" href="#/assign-driver/${encodeURIComponent(container.id)}">Plan / Assign</a>`);
  }
  actions.push(editContainerButton(container.id));
  return `<div class="adaptive-row-actions">${actions.join('')}</div>`;
}

function dashboardOperationsRows(containers) {
  if (!containers.length) return '<tr><td colspan="10" class="muted">No Containers match this view.</td></tr>';
  return containers.map((container) => {
    const nextAction = container.current_action_type
      ? workflowActionLabel(container.current_action_type)
      : (isComplete(container) ? 'Complete — no action' : 'Monitor');
    const driver = container.dispatch_summary?.driverName || '—';
    return `
      <tr data-dashboard-container-row="${escapeHtml(container.id)}" tabindex="0" aria-label="View ${escapeHtml(container.container_no)} summary">
        <td><a href="#/container/${encodeURIComponent(container.id)}">${escapeHtml(container.container_no)}</a></td>
        <td>${escapeHtml(container.customer_name || '—')}</td>
        <td>${escapeHtml(container.terminal_name || '—')}</td>
        <td>${localizedDateMarkup(container.eta_at)}</td>
        <td>${localizedDateMarkup(container.lfd_at)}</td>
        <td>${escapeHtml(driver)}</td>
        <td><span class="status dashboard-status dashboard-status--${escapeHtml(statusSlug(container.status))}">${escapeHtml(statusLabel(container.status))}</span></td>
        <td>${escapeHtml(nextAction)}</td>
        <td>${localizedDateMarkup(container.updated_at)}</td>
        <td><a href="#/container/${encodeURIComponent(container.id)}">POD · View</a></td>
      </tr>`;
  }).join('');
}

function dashboardQuickViewMarkup(container) {
  if (!container) {
    return '<div class="dashboard-quickview__empty"><h2>No Container selected</h2><p>Select a row to view its current operational summary.</p></div>';
  }
  const nextAction = container.current_action_type
    ? workflowActionLabel(container.current_action_type)
    : (isComplete(container) ? 'Complete — no action' : 'Monitor');
  const responsible = container.current_responsible_role || (isComplete(container) ? 'Complete' : 'Operations');
  return `
    <header><div><h2>${escapeHtml(container.container_no)}</h2><p>${escapeHtml(container.size_type || 'Container')} &nbsp;&middot;&nbsp; ${escapeHtml(container.customer_name || 'Customer')}</p></div><div class="dashboard-quickview__header-actions"><button type="button" class="dashboard-quickview__close" aria-label="Close selected Container">&times;</button><button type="button" class="dashboard-quickview__star" aria-label="Favorite Container"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z"/></svg></button></div></header>
    <dl>
      <div><dt>Current Stage</dt><dd><span class="status dashboard-status dashboard-status--${escapeHtml(statusSlug(container.status))}">${escapeHtml(statusLabel(container.status))}</span></dd></div>
      <div><dt>Responsible Role</dt><dd>${escapeHtml(humanizeLabel(responsible))}</dd></div>
      <div><dt>Next Action</dt><dd><strong>${escapeHtml(nextAction)}</strong></dd></div>
      <div><dt>Deadline</dt><dd>${localizedDateMarkup(bestDueDate(container))}</dd></div>
      <div><dt>Last Updated</dt><dd>${localizedDateMarkup(container.updated_at)}</dd></div>
      <div><dt>Driver</dt><dd>${escapeHtml(container.dispatch_summary?.driverName || 'Not assigned')}</dd></div>
    </dl>
    <div class="dashboard-quickview__actions">
      <a class="button-link secondary" href="#/container/${encodeURIComponent(container.id)}">View Container</a>
    </div>`;
}

function attachContainerFilters(context = 'containers') {
  const refresh = () => {
    state.adaptiveTableController?.setRows(filteredContainers(context));
  };
  document.getElementById('containerSearch')?.addEventListener('input', refresh);
  document.getElementById('containerFilter')?.addEventListener('change', refresh);
  document.getElementById('addContainerBtn')?.addEventListener('click', () => routeTo('#/add-container'));
}

function thisMonthContainers() {
  const now = new Date();
  return state.containers.filter((container) => {
    const date = returnedDate(container) || bestDueDate(container) || dateValue(container?.updated_at);
    return date && date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  });
}

function donutPath(cx, cy, radius, startAngle, endAngle) {
  const start = {
    x: cx + radius * Math.cos(startAngle),
    y: cy + radius * Math.sin(startAngle),
  };
  const end = {
    x: cx + radius * Math.cos(endAngle),
    y: cy + radius * Math.sin(endAngle),
  };
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function hexToRgba(hex, alpha) {
  const value = String(hex || '#007AFF').replace('#', '');
  const normalized = value.length === 3
    ? value.split('').map((char) => char + char).join('')
    : value.padEnd(6, '0').slice(0, 6);
  const number = Number.parseInt(normalized, 16);
  const r = (number >> 16) & 255;
  const g = (number >> 8) & 255;
  const b = number & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function currentMonthRangeLabel(date = new Date(), locale = getLocale()) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const format = { month: 'long', day: 'numeric', year: 'numeric' };
  const language = locale === 'zh-CN' ? 'zh-CN' : 'en-US';
  return `${start.toLocaleDateString(language, format)} - ${end.toLocaleDateString(language, format)}`;
}

function currentYearRangeLabel(date = new Date()) {
  const year = date.getFullYear();
  return `January ${year} - December ${year}`;
}

function ensureChartTooltip() {
  const existing = document.getElementById('chartTooltip');
  if (existing) return existing;
  const tooltip = document.createElement('div');
  tooltip.id = 'chartTooltip';
  tooltip.className = 'chart-tooltip';
  document.body.appendChild(tooltip);
  return tooltip;
}

function attachDashboardChartTooltips(rootEl = document) {
  const tooltip = ensureChartTooltip();
  const moveTooltip = (event) => {
    tooltip.style.left = `${event.clientX}px`;
    tooltip.style.top = `${event.clientY}px`;
  };

  rootEl.querySelectorAll('.dashboard-donut-segment').forEach((segment) => {
    if (segment.dataset.tooltipBound === 'true') return;
    segment.dataset.tooltipBound = 'true';
    segment.addEventListener('pointerenter', (event) => {
      tooltip.textContent = segment.dataset.tooltip || segment.closest('a')?.getAttribute('aria-label') || '';
      moveTooltip(event);
      tooltip.classList.add('visible');
    });
    segment.addEventListener('pointermove', moveTooltip);
    segment.addEventListener('pointerleave', () => tooltip.classList.remove('visible'));
    segment.addEventListener('click', () => tooltip.classList.remove('visible'));
  });
}

function refreshDashboardLocale(locale = getLocale()) {
  document.querySelectorAll('[data-dashboard-month-range]').forEach((element) => {
    element.textContent = currentMonthRangeLabel(new Date(), locale);
  });
  document.querySelectorAll('[data-dashboard-segment-key]').forEach((segment) => {
    const label = t(segment.dataset.dashboardSegmentKey, {}, locale);
    const value = Number(segment.dataset.dashboardSegmentValue) || 0;
    const total = Number(segment.dataset.dashboardSegmentTotal) || 0;
    const tooltip = `${label}: ${value} (${total ? Math.round((value / total) * 100) : 0}%)`;
    segment.dataset.tooltip = tooltip;
    const link = segment.closest('a');
    link?.setAttribute('aria-label', tooltip);
    const title = link?.querySelector('title');
    if (title) title.textContent = tooltip;
  });
}

function donutSegment(segment, start, end, total) {
  const label = t(segment.translationKey);
  const percent = total ? Math.round((segment.value / total) * 100) : 0;
  const tooltip = `${label}: ${segment.value} (${percent}%)`;
  const style = `--segment-glow:${hexToRgba(segment.color, 0.38)}`;
  const common = `class="dashboard-donut-segment" fill="none" stroke="${escapeHtml(segment.color)}" stroke-width="22" pathLength="100" data-tooltip="${escapeHtml(tooltip)}" data-dashboard-segment-key="${escapeHtml(segment.translationKey)}" data-dashboard-segment-value="${segment.value}" data-dashboard-segment-total="${total}" style="${escapeHtml(style)}"`;
  const ring = Math.abs(end - start - Math.PI * 2) < 0.0001
    ? `<circle ${common} cx="80" cy="80" r="48"></circle>`
    : `<path ${common} d="${donutPath(80, 80, 48, start, end)}" stroke-linecap="round"></path>`;

  return `
    <a href="#/dashboard?filter=${escapeHtml(segment.filter)}" aria-label="${escapeHtml(tooltip)}">
      <title>${escapeHtml(tooltip)}</title>
      ${ring}
    </a>
  `;
}

function donutChart(titleKey, segments, totalCount = null) {
  const title = t(titleKey);
  const positiveSegments = segments.filter((item) => item.value > 0);
  const total = totalCount ?? positiveSegments.reduce((sum, item) => sum + item.value, 0);
  const ringTotal = positiveSegments.reduce((sum, item) => sum + item.value, 0);
  let angle = -Math.PI / 2;
  return `
    <section class="dashboard-donut-card">
      <h2 data-i18n="${escapeHtml(titleKey)}">${escapeHtml(title)}</h2>
      <div class="dashboard-donut-layout">
        <svg class="dashboard-donut" viewBox="0 0 160 160" role="img" aria-label="${escapeHtml(title)}" data-i18n-aria-label="${escapeHtml(titleKey)}">
          ${total === 0 ? '<circle cx="80" cy="80" r="48" fill="none" stroke="#E5E5EA" stroke-width="22"></circle>' : ''}
          ${positiveSegments.map((segment) => {
            const start = angle;
            const size = (segment.value / ringTotal) * Math.PI * 2;
            angle += size;
            return donutSegment(segment, start, angle, total);
          }).join('')}
          <text x="80" y="78" text-anchor="middle" class="donut-total">${total}</text>
          <text x="80" y="96" text-anchor="middle" class="donut-caption" data-i18n="common.total">${escapeHtml(t('common.total'))}</text>
        </svg>
        <div class="dashboard-donut-legend">
          ${segments.map((segment) => `
            <a href="#/dashboard?filter=${escapeHtml(segment.filter)}">
              <span style="background:${escapeHtml(segment.color)}"></span>
              <b data-i18n="${escapeHtml(segment.translationKey)}">${escapeHtml(t(segment.translationKey))}</b>
              <em>${segment.value} / ${total ? Math.round((segment.value / total) * 100) : 0}%</em>
            </a>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

function monthlyDashboardCharts() {
  const monthRows = thisMonthContainers();
  const counts = bucketCounts(monthRows);
  const returnedOnTime = counts.empty_returned || 0;
  const urgent = counts.urgent || 0;
  const inactive = monthRows.length - returnedOnTime - urgent;
  const pickedUp = monthRows.filter((item) => stageProgressBucket(item) === 'picked_up').length;
  const warehouse = monthRows.filter((item) => stageProgressBucket(item) === 'warehouse_arrived').length;
  const returned = monthRows.filter((item) => stageProgressBucket(item) === 'empty_returned').length;

  return `
    <section class="dashboard-month">
      <h1 data-i18n="dashboard.monthStatus">${escapeHtml(t('dashboard.monthStatus'))}</h1>
      <p class="month-range" data-dashboard-month-range>${escapeHtml(currentMonthRangeLabel())}</p>
      <div class="dashboard-donuts">
        ${donutChart('dashboard.completionHealth', [
          { translationKey: 'dashboard.emptyReturnedOnTime', value: returnedOnTime, filter: 'completed_on_time', color: '#34C759' },
          { translationKey: 'dashboard.urgent', value: urgent, filter: 'urgent', color: '#FF3B30' },
          { translationKey: 'dashboard.inactive', value: inactive, filter: 'inactive', color: '#8E8E93' },
        ], monthRows.length)}
        ${donutChart('dashboard.stageProgress', [
          { translationKey: 'dashboard.stagePickedUp', value: pickedUp, filter: 'picked_up', color: '#007AFF' },
          { translationKey: 'dashboard.stageWarehouseArrived', value: warehouse, filter: 'warehouse_arrived', color: '#FF9500' },
          { translationKey: 'dashboard.stageReturned', value: returned, filter: 'empty_returned', color: '#34C759' },
        ], monthRows.length)}
      </div>
    </section>
  `;
}

const WORKFLOW_ACTION_LABELS = Object.freeze({
  clerk_port_intake: 'Enter ETA and Ocean Status',
  clerk_lfd: 'Enter LFD',
  finance_initial: 'Enter CTF and TMF status',
  dispatch_planning: 'Plan loaded movement and assign Driver',
  customer_report_empty: 'Report Container empty',
  dispatch_empty_planning: 'Plan Empty Return and assign Driver',
  driver_empty_return_correction: 'Correct Empty Return evidence',
  dispatch_empty_return_review: 'Review Empty Return evidence',
  finance_completion: 'Complete Finance record',
});

function workflowActionLabel(type) {
  return WORKFLOW_ACTION_LABELS[type] || String(type || 'Required action').replaceAll('_', ' ');
}

function workflowBackHash() {
  if (isCustomer()) return '#/customer-portal';
  return '#/dashboard';
}

function isWorkQueueDetailView() {
  return hashParams().get('view') === 'queue';
}

function workQueueHash() {
  return '#/dashboard';
}

function workQueueDetailNavigationMarkup() {
  const queueHash = workQueueHash();
  const dashboardAvailable = currentRolePolicy()?.readableRoutes.includes('dashboard')
    && queueHash !== '#/dashboard';
  return `
    <nav class="queue-detail-navigation" aria-label="Task navigation">
      <a class="button-link secondary" href="${queueHash}">Back to Work Queue</a>
      ${dashboardAvailable ? '<a class="button-link secondary" href="#/dashboard">Back to Dashboard</a>' : ''}
    </nav>`;
}

function clearWorkflowValidation(form) {
  form?.querySelectorAll('.is-invalid').forEach((field) => {
    field.classList.remove('is-invalid');
    field.removeAttribute('aria-invalid');
  });
  form?.querySelectorAll('[data-field-error]').forEach((message) => { message.textContent = ''; });
}

function bindRequiredFieldValidation(form) {
  if (!form) return;
  form.addEventListener('input', (event) => {
    const field = event.target;
    field.classList?.remove('is-invalid');
    field.removeAttribute?.('aria-invalid');
    field.closest?.('label, .workflow-field')?.querySelector('[data-field-error]')?.replaceChildren();
  });
  form.addEventListener('change', (event) => {
    const field = event.target;
    field.classList?.remove('is-invalid');
    field.removeAttribute?.('aria-invalid');
    field.closest?.('label, .workflow-field')?.querySelector('[data-field-error]')?.replaceChildren();
  });
  form.addEventListener('invalid', (event) => {
    const field = event.target;
    field.classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');
    field.closest('label, .workflow-field')?.querySelector('[data-field-error]')
      ?.replaceChildren(document.createTextNode('This field cannot be left empty or invalid.'));
  }, true);
}

function workflowField(form, fieldName) {
  if (!form || !fieldName) return null;
  if (fieldName.startsWith('containers.')) {
    const [, index, field] = fieldName.split('.');
    return form.querySelector(`[data-container-row="${index}"] [name="${field}"]`);
  }
  return form.elements.namedItem(fieldName);
}

function showWorkflowError(form, host, error) {
  clearWorkflowValidation(form);
  const code = error instanceof WorkflowUiError ? error.code : callableErrorCode(error);
  const field = error instanceof WorkflowUiError ? workflowField(form, error.field) : null;
  if (field instanceof HTMLElement) {
    field.classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');
    field.focus();
    field.closest('label, .workflow-field')?.querySelector('[data-field-error]')
      ?.replaceChildren(document.createTextNode('This field cannot be empty or invalid.'));
  }
  const messages = {
    REQUIRED_OR_INVALID: 'Complete the highlighted field with valid information.',
    ROW_COUNT_INVALID: 'Add at least one Container.',
    DUPLICATE_CONTAINER_NO: 'Each Container number must be unique.',
    CONFIRMATION_REQUIRED: 'Confirm this action before submitting.',
    FINANCE_SAVE_REQUIRED: 'Save the Finance summary before completing this action.',
    DRIVER_COMPLETION_EVIDENCE_REQUIRED: 'Upload the required evidence before completing the task.',
    EMPTY_RETURN_REVIEW_REASON_REQUIRED: 'Explain what the Driver must correct.',
    EMPTY_RETURN_REVIEW_EVIDENCE_REQUIRED: 'New Empty Return evidence is required.',
    DISPATCH_PLAN_DRIVER_NOT_ELIGIBLE: 'A selected Driver is not eligible for dispatch. Select another Driver.',
    DISPATCH_PLAN_DRIVER_ACCOUNT_NOT_LINKED: 'A selected Driver has no linked login account and cannot receive tasks. Link a Driver account first, or select another Driver.',
    ASSIGNMENT_DRIVER_ACCOUNT_NOT_LINKED: 'This Driver has no linked login account and cannot receive tasks. Link a Driver account first, or select another Driver.',
    permission_denied: 'You do not have permission to perform this action.',
    'permission-denied': 'You do not have permission to perform this action.',
    already_exists: 'This Container number already exists.',
    'already-exists': 'This Container number already exists.',
  };
  // The server names the numbers that are taken, and which customer holds each
  // (2026-08-19). "This Container number already exists" is true and useless on a
  // form with several rows - it does not say which one, so the only way to find
  // out is to remove numbers one at a time and press again. Alan met it on
  // FFAU4606196 while that Container was also invisible to every view: refused
  // from adding it, unable to reach the one that already existed, and nothing on
  // screen connecting the two.
  const taken = Array.isArray(error?.details?.taken) ? error.details.taken.filter(Boolean) : [];
  if (host) {
    host.textContent = taken.length
      ? `已存在：${taken.join('、')}。搜这个柜号就能打开它。`
      : messages[code] || 'The action could not be completed. Refresh and try again.';
  }
}

function customerContainerRow(index) {
  return `
    <fieldset class="workflow-container-row" data-container-row="${index}">
      <legend>Container ${index + 1}</legend>
      <label>Container #<input name="container_no" maxlength="32" autocomplete="off" required><small data-field-error></small></label>
      <label>Size<select name="size" required><option value="">Select size</option><option>20</option><option>40HQ</option><option>45</option><option>Custom</option></select><small data-field-error></small></label>
      <label>Weight (lbs)<input name="weight" type="number" min="1" max="1000000" step="0.1" inputmode="decimal" required><small data-field-error></small></label>
      <label>Pre-Alert<select name="pre_alert" required><option value="">Select status</option><option>Pre-Alert</option><option>Pre-Alert + DO</option></select><small data-field-error></small></label>
      <button class="secondary workflow-remove-row" type="button" data-remove-container>Remove</button>
    </fieldset>`;
}

async function renderCustomerAddTask() {
  shell(`
    <section class="workflow-page workflow-add-task">
      <header class="workflow-page-header"><div><p class="eyebrow">Customer workflow</p><h1>Add Task</h1><p>Create one shared Container Task. The Clerk is notified after a valid submission.</p></div><button class="secondary" type="button" data-workflow-back>Back</button></header>
      <form class="panel workflow-form" id="customerTaskForm" novalidate>
        <div class="workflow-form-grid">
          <label>MBL # <span class="muted">Optional</span><input name="master_bl_no" maxlength="128"><small data-field-error></small></label>
          <label>DO reference <span class="muted">Optional</span><input name="do_ref" maxlength="500"><small data-field-error></small></label>
          <label class="wide">Delivery warehouse address<input name="delivery_warehouse_address" maxlength="500" autocomplete="street-address" required><small data-field-error></small></label>
        </div>
        <div id="customerContainerRows">${customerContainerRow(0)}</div>
        <div class="workflow-inline-actions"><button class="secondary" type="button" data-add-container>Add another Container</button></div>
        <p class="workflow-form-error" id="customerTaskError" role="alert" aria-live="assertive"></p>
        <div class="actions"><button type="submit" data-submit-customer-task>Create Task</button><button class="secondary" type="button" data-workflow-back>Cancel</button></div>
      </form>
    </section>`);
  const form = document.getElementById('customerTaskForm');
  const rows = document.getElementById('customerContainerRows');
  const submit = form.querySelector('[data-submit-customer-task]');
  const errorHost = document.getElementById('customerTaskError');
  let pending = false;
  let retrySignature = '';
  let retryRequestId = '';

  function renumberRows() {
    [...rows.children].forEach((row, index) => {
      row.dataset.containerRow = String(index);
      row.querySelector('legend').textContent = `Container ${index + 1}`;
      row.querySelector('[data-remove-container]').hidden = rows.children.length === 1;
    });
  }

  root.querySelectorAll('[data-workflow-back]').forEach((button) => button.addEventListener('click', () => routeTo('#/customer-portal')));
  form.addEventListener('input', (event) => {
    event.target.classList?.remove('is-invalid');
    event.target.closest?.('label, .workflow-field')?.querySelector('[data-field-error]')?.replaceChildren();
  });
  form.addEventListener('invalid', (event) => {
    event.target.classList.add('is-invalid');
    event.target.closest('label')?.querySelector('[data-field-error]')
      ?.replaceChildren(document.createTextNode('This field cannot be left empty.'));
  }, true);
  rows.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-remove-container]');
    if (!remove || rows.children.length === 1) return;
    remove.closest('[data-container-row]').remove();
    renumberRows();
  });
  form.querySelector('[data-add-container]').addEventListener('click', () => {
    if (rows.children.length >= 50) return;
    rows.insertAdjacentHTML('beforeend', customerContainerRow(rows.children.length));
    renumberRows();
    applySystemInputGlow(rows.lastElementChild);
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    clearWorkflowValidation(form);
    errorHost.textContent = '';
    if (!form.reportValidity()) return;
    const raw = {
      master_bl_no: form.elements.master_bl_no.value,
      do_ref: form.elements.do_ref.value,
      delivery_warehouse_address: form.elements.delivery_warehouse_address.value,
      containers: [...rows.children].map((row) => ({
        container_no: row.querySelector('[name="container_no"]').value,
        size: row.querySelector('[name="size"]').value,
        weight: row.querySelector('[name="weight"]').value,
        pre_alert: row.querySelector('[name="pre_alert"]').value,
      })),
    };
    const signature = JSON.stringify(raw);
    if (signature !== retrySignature) {
      retrySignature = signature;
      retryRequestId = crypto.randomUUID();
    }
    try {
      const payload = buildCustomerTaskRequest(raw, retryRequestId);
      pending = true;
      submit.disabled = true;
      submit.textContent = 'Creating…';
      const response = await createCustomerContainerTasks(payload);
      if (!Array.isArray(response?.data?.containerIds) || response.data.containerIds.length < 1) {
        throw new Error('Task creation was not confirmed.');
      }
      toast(`${response.data.containerIds.length} Container Task${response.data.containerIds.length === 1 ? '' : 's'} created.`);
      routeTo('#/customer-portal');
    } catch (error) {
      showWorkflowError(form, errorHost, error);
    } finally {
      pending = false;
      submit.disabled = false;
      submit.textContent = 'Create Task';
    }
  });
  renumberRows();
  applySystemInputGlow(form);
}

function workflowActionQueueMarkup(actions) {
  if (actions.length === 0) {
    return '<div class="workflow-empty"><h2>No action required</h2><p>New department actions will appear here in real time.</p></div>';
  }
  return `<div class="workflow-action-list">${actions.map((action) => `
    <a class="workflow-action-card" href="#/action/${encodeURIComponent(action.id)}" data-action-id="${escapeHtml(action.id)}">
      <span class="workflow-action-indicator" aria-hidden="true"></span>
      <span><strong>${escapeHtml(action.container_no || 'Container')}</strong><small>${escapeHtml(workflowActionLabel(action.action_type))}</small></span>
      <span class="workflow-action-meta"><span class="status">${escapeHtml(action.status)}</span><time>${escapeHtml(formatLocalizedDate(dateValue(action.updated_at), getLocale()))}</time></span>
      <span aria-hidden="true">&#8250;</span>
    </a>`).join('')}</div>`;
}

function workQueueItemFromProjection(item) {
  const isoFromMillis = (value) => Number.isSafeInteger(value)
    ? new Date(value).toISOString() : null;
  const container = state.containers.find(({ id }) => id === item.container_id);
  const action = item.action && typeof item.action === 'object' && !Array.isArray(item.action)
    ? item.action
    : Array.isArray(item.required_fields)
      ? { required_fields: item.required_fields }
      : null;
  const requiredFields = Array.isArray(item.required_fields)
    ? item.required_fields
    : Array.isArray(action?.required_fields) ? action.required_fields : [];
  return {
    id: `${item.container_id}:${item.viewer_role}`,
    containerId: item.container_id,
    detailId: item.container_id,
    detailKind: 'container',
    viewerRole: state.role,
    targetType: item.target_type,
    targetId: item.target_id,
    targetField: item.target_field || null,
    containerNo: item.container_no,
    workflowStatus: item.container_status,
    // Read from the projection, not from a separate container list: the pool
    // used to infer cargo from state.containers, which only holds the rows the
    // dashboard has paged in, so a container the operator had not scrolled to
    // was invisible to the pool.
    oceanStatus: item.ocean_status || container?.ocean_status || '',
    dispatchScheduledAt: isoFromMillis(item.dispatch_scheduled_at_millis),
    responsibleRole: item.current_responsible_role || '',
    nextAction: item.next_action || '',
    relevantAt: isoFromMillis(item.relevant_at_millis),
    queueState: item.queue_state,
    priority: item.priority,
    atRisk: item.at_risk === true,
    updatedAt: isoFromMillis(item.generated_at_millis),
    action,
    requiredFields,
    todoProjection: item.todo_projection || null,
    canonicalCompletion: {
      status: item.container_status,
      isEmptyReturned: item.canonical_completed === true,
      emptyReturnedAt: isoFromMillis(item.completed_at_millis),
    },
  };
}

function inertWorkQueueController() {
  return Object.freeze({
    actionRequiredContainerIds: () => [],
    subscribe: () => () => {},
    destroy: () => {},
  });
}

function mountTodayWorkQueue(host) {
  if (!host || !state.user?.uid) return inertWorkQueueController();
  // The queue rail and its Todo card are gone (Alan, 2026-08-07). What needs a
  // hand now shows up in one place - the Work Pool - for every role, so there is
  // no second list to keep in your head and no card to dismiss.
  //
  // This controller stays because the pool is built from its model: it still
  // fetches, still recomputes on every refresh, and still publishes the ids the
  // Work Pool button counts and filters by. What it no longer does is draw
  // anything. Deleting it outright would empty the pool.
  const stopEdgeReveal = () => {};
  const stopProximity = () => {};
  let stopOverflowFade = () => {};
  let destroyed = false;
  let pending = false;
  let rerun = false;
  let generation = 0;
  let currentModel = null;
  let currentPoolModel = null;
  let currentRawItems = [];
  let currentGeneratedAt = new Date();
  let currentTruncated = false;
  let activeQueueId = '';
  const handlePageHide = () => {
    destroyed = true;
    generation += 1;
  };
  window.addEventListener('pagehide', handlePageHide);
  let pinned = window.localStorage.getItem('ay-tms-work-queue-pinned') === 'true';
  const syncPinnedState = () => {
    host.dataset.pinned = String(pinned);
    const control = host.querySelector('[data-work-queue-pin]');
    control?.setAttribute('aria-pressed', String(pinned));
    control?.setAttribute('aria-label', pinned ? 'Unpin Work Queue' : 'Pin Work Queue');
  };
  // The Work Pool count, the pool filter, and the blue cards all read this one model,
  // so a submitted Todo drops out of every surface on the same refresh.
  const workPoolSubscribers = new Set();
  // The Work Pool is NOT the rail. The rail is role-filtered - staff see only what
  // still needs a hand, the boss and the customer see only what finished today -
  // and reading the pool off that model made a customer's pool permanently empty,
  // because a customer's rail carries no action_required rows at all. The pool
  // reads the unfiltered model, so "all my Containers delivered to the warehouse
  // and waiting to be reported empty" still counts.
  // For Dispatch the pool is a different question from everyone else's. Every
  // other role asks "what is waiting on me"; a dispatcher asks "what have I not
  // sent out yet today" (Alan, 2026-08-04) - laden containers that are ready to
  // move and have not been released today. Measured in Los Angeles time, the
  // same clock the office day is measured in everywhere else in this file.
  const dispatchPoolContainerIds = () => {
    const today = officeTodayKey();
    return (currentPoolModel?.items || [])
      .filter((item) => item.oceanStatus === 'available_for_pickup'
        && officeDateKeyOf(item.dispatchScheduledAt) !== today)
      .map((item) => item.containerId);
  };
  const actionRequiredContainerIds = () => (state.role === 'dispatcher'
    ? dispatchPoolContainerIds()
    : (currentPoolModel?.items || [])
      .filter((item) => item.queueState === 'action_required')
      .map((item) => item.containerId));
  const publishWorkPool = () => {
    const ids = actionRequiredContainerIds();
    for (const listener of workPoolSubscribers) listener(ids);
  };
  const renderCurrentQueue = (
    previousScrollTop = host.querySelector('.today-work-queue__list')?.scrollTop || 0,
  ) => {
    if (!currentModel) return;
    publishWorkPool();
    stopOverflowFade();
    stopOverflowFade = () => {};
    // The 200-item cap still applies; the notice about it was removed by
    // request. Nothing in the UI now indicates the list is truncated, which is
    // an accepted trade-off recorded in the density spec.
    // Nothing is drawn. publishWorkPool above is the whole job now.
  };
  const locateWorkQueueContainer = async ({ containerId, containerNo }) => {
    if (state.adaptiveTableController?.locate(containerId)) return true;
    const controller = state.dashboardDataController;
    if (!controller) return false;
    try {
      const ensured = await controller.ensureRow({
        id: containerId,
        search: containerNo,
      });
      if (ensured && state.adaptiveTableController?.locate(containerId)) return true;
    } catch {
      return false;
    }
    return false;
  };
  const quickNavigateQueueItem = async ({ containerId, containerNo, openTodo = false }) => {
    const item = currentModel?.items.find((candidate) => candidate.containerId === containerId);
    if (!item) return;
    const located = await locateWorkQueueContainer({
      containerId,
      containerNo,
    });
    if (!located) {
      sessionStorage.setItem('ay-tms-work-queue-target', containerId);
      routeTo('#/dashboard');
      return;
    }
    if (!openTodo) {
      activeQueueId = '';
      renderCurrentQueue();
      return;
    }
    activeQueueId = item.id;
    renderCurrentQueue();
    window.requestAnimationFrame(() => {
      state.adaptiveTableController?.locate(containerId);
    });
  };
  const queueCallbacks = createTodayWorkQueueCallbackContract({
    onSubmitTodo: ({
      actionId, containerId, expectedRevision, requestId,
    }) => confirmContainerActionTodo({
      action_id: actionId,
      container_id: containerId,
      expected_revision: expectedRevision,
      request_id: requestId,
    }),
    onQuickNavigate: quickNavigateQueueItem,
  });
  const handleQueueClick = async (event) => {
    const retry = event.target.closest('[data-work-queue-retry]');
    if (retry) {
      void refresh();
      return;
    }
    const submitTodo = event.target.closest('[data-work-queue-submit-todo]');
    if (submitTodo) {
      const item = currentModel?.items.find((candidate) => candidate.id === activeQueueId);
      if (!item || submitTodo.disabled || state.role === 'admin') return;
      submitTodo.disabled = true;
      try {
        const response = await queueCallbacks.submitTodo(item);
        const completedProjection = response?.data?.todo_projection;
        const todoCompleted = completedProjection?.status === 'completed';
        if (!completedProjection) throw new Error('Todo submission was not recorded.');
        currentRawItems = currentRawItems.map((rawItem) => (
          rawItem.container_id === item.containerId
            ? { ...rawItem, todo_projection: completedProjection }
            : rawItem
        ));
        currentPoolModel = buildTodayWorkQueueViewModel(
          currentRawItems.map(workQueueItemFromProjection),
          { now: currentGeneratedAt },
        );
        currentModel = buildTodayWorkQueueViewModel(
          currentRawItems.map(workQueueItemFromProjection),
          { now: currentGeneratedAt, role: state.role },
        );
        if (todoCompleted) {
          activeQueueId = '';
          toast('Task submitted.');
          await refresh();
          return;
        }
        renderCurrentQueue();
        toast('Todo is not ready. Complete the listed task fields and submit again.');
      } catch (error) {
        if (submitTodo.isConnected) submitTodo.disabled = false;
        toast(error?.message || 'Todo could not be submitted.');
      }
      return;
    }
    const pin = event.target.closest('[data-work-queue-pin]');
    if (pin) {
      pinned = !pinned;
      window.localStorage.setItem('ay-tms-work-queue-pinned', String(pinned));
      syncPinnedState();
      if (!pinned && !pin.matches(':focus-visible')) pin.blur();
      return;
    }
    const button = event.target.closest('[data-work-queue-container-id]');
    if (!button) return;
    const item = currentModel?.items.find(
      (candidate) => candidate.id === button.dataset.workQueueId,
    );
    if (!item) return;
    await queueCallbacks.quickNavigate(item);
  };
  host.addEventListener('click', handleQueueClick);
  syncPinnedState();

  async function refresh() {
    if (destroyed) return;
    if (pending) {
      rerun = true;
      return;
    }
    pending = true;
    const currentGeneration = ++generation;
    const previousScrollTop = host.querySelector('.today-work-queue__list')?.scrollTop || 0;
    try {
      const response = await listMyWorkQueue({});
      if (destroyed || currentGeneration !== generation) return;
      const rawItems = Array.isArray(response?.data?.items) ? response.data.items : [];
      const generatedAtMillis = response?.data?.generatedAtMillis
        ?? response?.data?.generated_at_millis;
      currentGeneratedAt = Number.isSafeInteger(generatedAtMillis)
        ? new Date(generatedAtMillis) : new Date();
      currentRawItems = rawItems;
      currentTruncated = response?.data?.truncated === true;
      currentPoolModel = buildTodayWorkQueueViewModel(
        currentRawItems.map(workQueueItemFromProjection),
        { now: currentGeneratedAt },
      );
      currentModel = buildTodayWorkQueueViewModel(
        currentRawItems.map(workQueueItemFromProjection),
        { now: currentGeneratedAt, role: state.role },
      );
      if (!currentModel.items.some((item) => (
        item.id === activeQueueId
          && (item.queueState === 'action_required' || item.atRisk)
      ))) {
        activeQueueId = '';
      }
      renderCurrentQueue(previousScrollTop);
    } catch (error) {
      if (!destroyed && currentGeneration === generation) {
        console.error('Today’s Work Queue unavailable.', error);
        stopOverflowFade();
        stopOverflowFade = () => {};
      }
    } finally {
      pending = false;
      if (rerun && !destroyed) {
        rerun = false;
        void refresh();
      }
    }
  }

  const notifyChange = () => void refresh();
  const canonicalRole = canonicalWorkflowRole(state.role);
  const backofficeSignalRoles = new Set(['admin', 'dispatcher', 'clerk', 'finance']);
  const signalId = backofficeSignalRoles.has(canonicalRole)
    ? `role_${canonicalRole}` : `user_${state.user.uid}`;
  const stop = onSnapshot(
    doc(db, 'work_queue_signals', signalId),
    notifyChange,
    (error) => {
      console.warn('Work Queue refresh signal unavailable.', error);
      void refresh();
    },
  );
  const fallbackInterval = window.setInterval(() => void refresh(), 30_000);
  return Object.freeze({
    actionRequiredContainerIds,
    subscribe(listener) {
      if (typeof listener !== 'function') return () => {};
      workPoolSubscribers.add(listener);
      listener(actionRequiredContainerIds());
      return () => workPoolSubscribers.delete(listener);
    },
    destroy() {
      destroyed = true;
      generation += 1;
      stop?.();
      window.clearInterval(fallbackInterval);
      workPoolSubscribers.clear();
      stopEdgeReveal();
      stopProximity();
      stopOverflowFade();
      host.removeEventListener('click', handleQueueClick);
      window.removeEventListener('pagehide', handlePageHide);
    },
  });
}

async function loadEvidenceRecords(taskId) {
  if (!taskId) return [];
  const constraints = [
    where('task_id', '==', taskId),
    where('archived_by_restore', '==', false),
  ];
  const snapshot = await getDocs(query(
    collection(db, 'dispatch_task_evidence'),
    ...constraints,
    limit(100),
  ));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((left, right) => (dateValue(right.created_at)?.getTime() || 0) - (dateValue(left.created_at)?.getTime() || 0));
}

async function loadEvidenceAssets(taskId) {
  const records = await loadEvidenceRecords(taskId);
  const objectUrls = [];
  const items = await Promise.all(records.map(async (record) => {
    try {
      const blob = await getBlob(storageRef(storage, record.storage_path), 10 * 1024 * 1024);
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      return { ...record, objectUrl };
    } catch (error) {
      console.warn('Evidence preview unavailable.', { evidenceId: record.id, error });
      return { ...record, objectUrl: null };
    }
  }));
  return { items, cleanup: () => objectUrls.forEach((url) => URL.revokeObjectURL(url)) };
}

async function loadCustomerEvidenceAssets(containerId) {
  const snapshot = await getDocs(query(
    collection(db, 'dispatch_task_evidence'),
    where('container_id', '==', containerId),
    where('customer_uid', '==', state.user.uid),
    where('archived_by_restore', '==', false),
    limit(100),
  ));
  const records = snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((left, right) => (dateValue(right.created_at)?.getTime() || 0) - (dateValue(left.created_at)?.getTime() || 0));
  const objectUrls = [];
  const items = await Promise.all(records.map(async (record) => {
    try {
      const blob = await getBlob(storageRef(storage, record.storage_path), 10 * 1024 * 1024);
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      return { ...record, objectUrl };
    } catch {
      return { ...record, objectUrl: null };
    }
  }));
  return { items, cleanup: () => objectUrls.forEach((url) => URL.revokeObjectURL(url)) };
}

/**
 * Opens an uploaded delivery order in a new tab (2026-09-27).
 *
 * Read the same way the POD photos are - getBlob under storage.rules, which lets the
 * back office and the uploader read container-task-documents - rather than through a
 * link, because the create callables never made one: the object has no download
 * token, only a path. The tab is opened inside the press, where a browser allows
 * it, and the file is poured into it when it arrives.
 */
async function openStoredDeliveryOrder(storedValue) {
  const objectPath = deliveryOrderObjectPath(storedValue);
  if (!objectPath) {
    toast('这个柜子没有可以打开的 DO。 · This Container has no delivery order to open.');
    return;
  }
  const opened = window.open('', '_blank');
  if (opened) opened.opener = null;
  try {
    const fetched = await getBlob(storageRef(storage, objectPath), DELIVERY_ORDER_MAX_BYTES);
    // Typed from the file's own extension when the read carries no type (the
    // emulator's does not): a typeless blob opens as bytes, not as a PDF.
    const extension = objectPath.split('.').pop();
    const type = fetched.type || Object.entries(DELIVERY_ORDER_CONTENT_TYPES)
      .find(([, ext]) => ext === extension)?.[0] || '';
    const blob = fetched.type || !type ? fetched : new Blob([fetched], { type });
    const url = URL.createObjectURL(blob);
    if (opened && !opened.closed) {
      opened.location.href = url;
    } else {
      // The browser refused the tab: hand the file over as a download instead.
      const link = document.createElement('a');
      link.href = url;
      link.download = objectPath.split('/').pop() || 'delivery-order';
      document.body.append(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  } catch (error) {
    opened?.close?.();
    console.warn('[delivery order] could not be opened', error);
    toast('DO 打不开，请稍后再试。 · The delivery order could not be opened. Try again.');
  }
}

async function loadRoleSafeContainerEvidenceAssets(containerId) {
  if (isCustomer()) return loadCustomerEvidenceAssets(containerId);
  const constraints = [
    where('container_id', '==', containerId),
    where('archived_by_restore', '==', false),
  ];
  const snapshot = await getDocs(query(
    collection(db, 'dispatch_task_evidence'),
    ...constraints,
    limit(100),
  ));
  const records = snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((left, right) => (dateValue(right.created_at)?.getTime() || 0) - (dateValue(left.created_at)?.getTime() || 0));
  const objectUrls = [];
  const items = await Promise.all(records.map(async (record) => {
    try {
      const blob = await getBlob(storageRef(storage, record.storage_path), 10 * 1024 * 1024);
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      return { ...record, objectUrl };
    } catch {
      return { ...record, objectUrl: null };
    }
  }));
  return { items, cleanup: () => objectUrls.forEach((url) => URL.revokeObjectURL(url)) };
}

function evidenceMarkup(items) {
  if (!items.length) return '<div class="workflow-empty"><p>No evidence uploaded yet.</p></div>';
  return `<div class="workflow-evidence-grid">${items.map((item) => `
    <figure class="workflow-evidence-card">
      ${item.objectUrl
        ? `<img src="${escapeHtml(item.objectUrl)}" alt="${escapeHtml(item.kind === 'empty_return' ? 'Empty Return evidence' : 'POD evidence')}">`
        : '<div class="workflow-evidence-unavailable">Preview unavailable</div>'}
      <figcaption><strong>${escapeHtml(item.kind === 'empty_return' ? 'Empty Return' : 'POD')}</strong><span>${escapeHtml(item.note || 'No note')}</span><time>${escapeHtml(formatLocalizedDate(dateValue(item.created_at), getLocale()))}</time></figcaption>
    </figure>`).join('')}</div>`;
}

async function uploadWorkflowEvidence(task, file, note, operationalStatus = '') {
  if (!(file instanceof File) || !file.type.startsWith('image/') || file.size <= 0
    || file.size > 10 * 1024 * 1024) {
    throw new WorkflowUiError('REQUIRED_OR_INVALID', 'evidence_file');
  }
  const kind = evidenceKindForLeg(task.leg_type);
  if (!kind) throw new WorkflowUiError('REQUIRED_OR_INVALID', 'evidence_file');
  const token = crypto.randomUUID();
  const path = evidenceObjectPath(task.id, kind, token);
  await uploadBytes(storageRef(storage, path), file, {
    contentType: file.type,
    customMetadata: {
      uploader_uid: state.user.uid,
      ...(String(note || '').trim() ? { note: String(note).trim().slice(0, 500) } : {}),
      ...(state.role === 'dispatcher' && String(operationalStatus || '').trim()
        ? { operational_status: String(operationalStatus).trim() } : {}),
    },
  });
  const deadline = Date.now() + 20_000;
  while (Date.now() < deadline) {
    const records = await loadEvidenceRecords(task.id);
    if (records.some((record) => record.storage_path === path)) return path;
    await new Promise((resolve) => window.setTimeout(resolve, 300));
  }
  throw new Error('Evidence uploaded, but server verification is still pending. Wait and refresh.');
}

async function loadPodUploadTask(containerId) {
  const snapshot = await getDocs(query(
    collection(db, 'dispatch_tasks'),
    activeRestoreConstraint(),
    where('container_id', '==', containerId),
    limit(100),
  ));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((task) => task.archived_by_restore !== true && evidenceKindForLeg(task.leg_type) === 'pod')
    .sort((left, right) => (dateValue(right.updated_at)?.getTime() || 0)
      - (dateValue(left.updated_at)?.getTime() || 0))[0] || null;
}

async function mountDashboardPodEditor({
  containerId,
  host,
  row = null,
  workspace = false,
  onComplete = null,
} = {}) {
  if (!canEditDashboardPod(state.role)) throw new Error('Your role cannot edit POD photos.');
  if (!host) throw new Error('POD editor requires a visible host.');
  const currentRow = row || state.dashboardDataController?.snapshot?.().rows
    ?.find((item) => item.id === containerId);
  const containerSnap = currentRow ? null : await getDoc(doc(db, 'containers', containerId));
  const containerData = currentRow || (containerSnap?.exists() ? containerSnap.data() : null);
  if (!containerData) throw new Error('Container not found.');
  const containerNo = containerData.container_no || containerId;
  const draft = createPodUploadDraft({ containerId, role: state.role });
  let pending = false;
  let destroyed = false;
  let choiceController = null;

  const render = () => {
    if (destroyed) return;
    choiceController?.destroy?.();
    host.innerHTML = podUploadEditorMarkup({
      containerNo,
      role: state.role,
      ...draft.snapshot(),
      pending,
      workspace,
      embedded: !workspace,
    });
    enhanceResponsiveUi(host);
    const form = host.querySelector('[data-pod-upload-form]');
    const fileInput = form.querySelector('[data-pod-upload-files]');
    fileInput.addEventListener('change', () => {
      draft.addFiles(fileInput.files);
      render();
    });
    form.querySelectorAll('[data-pod-upload-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        draft.remove(Number(button.dataset.podUploadRemove));
        render();
      });
    });
    form.querySelectorAll('[data-pod-upload-note]').forEach((input) => {
      input.addEventListener('input', () => draft.setNote(Number(input.dataset.podUploadNote), input.value));
    });
    form.querySelectorAll('[data-pod-upload-status]').forEach((input) => {
      input.addEventListener('change', () => draft.setStatus(Number(input.dataset.podUploadStatus), input.value));
    });
    form.querySelector('[data-pod-upload-complete-set]')?.addEventListener('change', (event) => {
      draft.setCompleteSetConfirmed(event.currentTarget.checked);
    });
    choiceController = mountWorkspaceChoiceControls(form);
    form.querySelectorAll('[data-workspace-choice^="pod_status_"]').forEach((choice) => {
      const index = Number(choice.dataset.workspaceChoice.replace('pod_status_', ''));
      choice.querySelector('input[type="hidden"]')?.addEventListener('change', (event) => {
        draft.setStatus(index, event.target.value);
      });
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending) return;
      let submission;
      try {
        submission = draft.buildSubmission();
      } catch (error) {
        const validationMessages = {
          NOTE_REQUIRED: 'Add a note for every photo.',
          POD_COMPLETE_SET_CONFIRMATION_REQUIRED: 'Confirm that every required POD photo is included.',
          POD_FILE_REQUIRED: 'Choose at least one POD photo.',
        };
        draft.setError(validationMessages[error?.message] || 'POD photos are not ready to save.');
        render();
        return;
      }
      try {
        pending = true;
        draft.setError('');
        render();
        const task = await loadPodUploadTask(containerId);
        if (!task) throw new Error('No POD transport task is available for this Container.');
        for (const asset of submission.assets) {
          await uploadWorkflowEvidence(task, asset.file, asset.note, asset.status);
        }
        toast('POD photos verified.');
        await state.dashboardDataController?.refresh?.();
        if (typeof onComplete === 'function') {
          await onComplete();
        } else if (workspace) {
          await state.taskWorkspaceController?.confirmRoute(`#/pod-edit/${containerId}`);
        }
      } catch (error) {
        pending = false;
        draft.setError(error?.message || 'POD photos could not be saved.');
        render();
      }
    });
    applySystemInputGlow(form);
  };

  render();
  return Object.freeze({
    destroy() {
      destroyed = true;
      choiceController?.destroy?.();
      choiceController = null;
    },
  });
}

async function renderDashboardPodEditor(containerId) {
  const workspaceHost = state.workspaceCaptureHost;
  if (!workspaceHost) throw new Error('POD editor requires a task window.');
  const controller = await mountDashboardPodEditor({
    containerId,
    host: workspaceHost,
    workspace: true,
  });
  registerActivePageCleanup(() => controller.destroy());
}

function workflowActionHeader(action, subtitle = '') {
  if (state.workspaceCaptureHost) {
    return `<span data-workspace-container-number hidden>${escapeHtml(action.container_no || 'Container')}</span>`;
  }
  return `
    <header class="workflow-page-header">
      <div><p class="eyebrow">${escapeHtml(action.container_no || 'Container Task')}</p><h1>${escapeHtml(workflowActionLabel(action.action_type))}</h1>${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ''}</div>
      <button class="secondary" type="button" data-workflow-back>Back</button>
    </header>`;
}

function bindWorkflowBack(target = root) {
  target.querySelectorAll('[data-workflow-back]').forEach((button) => {
    button.addEventListener('click', () => routeTo(workflowBackHash()));
  });
}

function actionDateTimeValue(value) {
  if (!Number.isSafeInteger(value) || value < 0) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function actionDateValue(value) {
  return actionDateTimeValue(value).slice(0, 10);
}

function standardActionFields(action, existing = {}) {
  if (state.workspaceCaptureHost) {
    return workspaceStandardActionFields(action.action_type, existing);
  }
  if (action.action_type === 'clerk_port_intake') {
    return `
      <label>ETA<input name="eta_at" type="date" value="${escapeHtml(actionDateValue(existing.eta_at))}" required><small data-field-error></small></label>
      <label>Ocean Status<select name="ocean_status" required><option value="">Select status</option>${OCEAN_STATUS_VALUES.map((value) => `<option value="${value}" ${existing.ocean_status === value ? 'selected' : ''}>${containerStatusLabel(value)}</option>`).join('')}</select><small data-field-error></small></label>`;
  }
  if (action.action_type === 'clerk_lfd') {
    return `<label>LFD<input name="lfd_at" type="date" value="${escapeHtml(actionDateValue(existing.lfd_at))}" required><small data-field-error></small></label>
      <label>Pickup Appointment<input name="pickup_appointment_at" type="datetime-local" value="${escapeHtml(actionDateTimeValue(existing.pickup_appointment_at))}" required><small data-field-error></small></label>
      <label>Return Empty LFD<input name="return_empty_lfd_at" type="date" value="${escapeHtml(actionDateValue(existing.return_empty_lfd_at))}" required><small data-field-error></small></label>`;
  }
  if (action.action_type === 'finance_initial') {
    const options = (selected) => `<option value="">Select status</option>${[['paid', 'Paid'], ['unpaid', 'Unpaid']].map(([value, label]) => `<option value="${value}" ${selected === value ? 'selected' : ''}>${label}</option>`).join('')}`;
    return `<label>CTF Status<select name="ctf_status" required>${options(existing.ctf_status)}</select><small data-field-error></small></label><label>TMF Status<select name="tmf_status" required>${options(existing.tmf_status)}</select><small data-field-error></small></label><label class="workflow-confirmation wide"><input name="customer_paid" type="checkbox" value="true" required><span>Customer Paid</span><small data-field-error></small></label>`;
  }
  if (action.action_type === 'customer_report_empty') {
    return '<label class="workflow-confirmation wide"><input name="report_empty_confirmed" type="checkbox" required><span>I confirm this Container is empty and ready for Empty Return planning.</span><small data-field-error></small></label>';
  }
  return '';
}

function bindStandardActionForm(action, renderScope = state.workspaceCaptureHost || document) {
  const form = renderScope.querySelector('#workflowActionForm');
  const errorHost = renderScope.querySelector('#workflowActionError');
  const submit = form.querySelector('[type="submit"]');
  let pending = false;
  let signature = '';
  let requestId = '';
  form.addEventListener('input', (event) => event.target.classList?.remove('is-invalid'));
  form.addEventListener('invalid', (event) => {
    event.target.classList.add('is-invalid');
    event.target.closest('label')?.querySelector('[data-field-error]')
      ?.replaceChildren(document.createTextNode('This field cannot be left empty.'));
  }, true);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    clearWorkflowValidation(form);
    errorHost.textContent = '';
    if (!form.reportValidity()) return;
    const values = Object.fromEntries(new FormData(form).entries());
    if (action.action_type === 'customer_report_empty') {
      values.report_empty_confirmed = form.elements.report_empty_confirmed.checked === true
        || form.elements.report_empty_confirmed.value === 'true';
    }
    const nextSignature = JSON.stringify(values);
    if (nextSignature !== signature) {
      signature = nextSignature;
      requestId = crypto.randomUUID();
    }
    try {
      const payload = {
        action_id: action.id,
        request_id: requestId,
        expected_revision: action.revision,
        values: buildWorkflowActionValues(action.action_type, values),
      };
      pending = true;
      submit.disabled = true;
      submit.textContent = 'Submitting…';
      await completeContainerAction(payload);
      toast('Action completed.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo(workflowBackHash());
    } catch (error) {
      showWorkflowError(form, errorHost, error);
    } finally {
      pending = false;
      submit.disabled = false;
      submit.textContent = 'Complete Action';
    }
  });
  applySystemInputGlow(form);
}

async function renderEvidenceCorrectionAction(action) {
  const workspaceRoute = Boolean(state.workspaceCaptureHost);
  const renderScope = state.workspaceCaptureHost || document;
  const taskId = action.source_task_id || docIdFromRef(action.source_task_ref);
  const taskSnap = taskId ? await getDoc(doc(db, 'dispatch_tasks', taskId)) : null;
  if (!taskSnap?.exists()) throw new Error('The assigned Driver task is unavailable.');
  const task = { id: taskSnap.id, ref: taskSnap.ref, ...taskSnap.data() };
  const evidence = await loadEvidenceAssets(taskId);
  const workspaceMarkup = `
    <section class="workflow-page workflow-page--workspace-action">${workflowActionHeader(action)}
      <section class="workflow-evidence-section workflow-evidence-section--workspace" aria-label="Current evidence">${evidenceMarkup(evidence.items)}</section>
      <form class="workflow-form workflow-form--workspace evidence-correction-workspace" id="correctionEvidenceForm" data-workspace-done-target novalidate>
        <label>Corrected image<input name="evidence_file" type="file" accept="image/*" required><small data-field-error></small></label>
        <label>Note<textarea name="note" maxlength="500" placeholder="Explain what was corrected"></textarea><small data-field-error></small></label>
        <p class="workflow-form-error" id="correctionUploadError" role="alert"></p>
      </form>
    </section>`;
  const fullPageMarkup = `
    <section class="workflow-page">${workflowActionHeader(action, action.blocked_reason || 'Upload corrected evidence, then resubmit it to Dispatch.')}
      <section class="panel workflow-evidence-section"><h2>Current evidence</h2>${evidenceMarkup(evidence.items)}</section>
      <form class="panel workflow-form" id="correctionEvidenceForm" novalidate>
        <h2>Upload corrected evidence</h2>
        <label>Evidence image<input name="evidence_file" type="file" accept="image/*" required><small data-field-error></small></label>
        <label>Note<textarea name="note" maxlength="500" placeholder="Explain what was corrected"></textarea><small data-field-error></small></label>
        <p class="workflow-form-error" id="correctionUploadError" role="alert"></p>
        <div class="actions"><button type="submit">Upload evidence</button></div>
      </form>
      <section class="panel"><h2>Submit correction</h2><p>Submit only after the corrected image appears above.</p><p class="workflow-form-error" id="correctionSubmitError" role="alert"></p><div class="actions"><button type="button" data-submit-correction>Submit to Dispatch</button></div></section>
    </section>`;
  shell(workspaceRoute ? workspaceMarkup : fullPageMarkup);
  bindWorkflowBack();
  registerActivePageCleanup(evidence.cleanup);
  const form = renderScope.querySelector('#correctionEvidenceForm');
  const uploadButton = form.querySelector('[type="submit"]');
  const uploadError = renderScope.querySelector('#correctionUploadError');
  const submitCorrection = renderScope.querySelector('[data-submit-correction]');
  const refreshCorrectionAction = async () => {
    if (workspaceRoute && state.taskWorkspaceController) {
      await state.taskWorkspaceController.refreshRoute(`#/action/${action.id}`);
      return;
    }
    await renderContainerActionDetail(action.id);
  };
  let pending = false;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !form.reportValidity()) return;
    try {
      pending = true;
      if (uploadButton) {
        uploadButton.disabled = true;
        uploadButton.textContent = 'Uploading…';
      }
      await uploadWorkflowEvidence(task, form.elements.evidence_file.files[0], form.elements.note.value);
      if (workspaceRoute) {
        await submitEmptyReturnCorrection({
          action_id: action.id,
          request_id: crypto.randomUUID(),
          expected_revision: action.revision,
        });
        toast('Correction sent to Dispatch.');
        await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
        routeTo('#/dashboard');
        return;
      }
      toast('Evidence verified.');
      await refreshCorrectionAction();
    } catch (error) {
      showWorkflowError(form, uploadError, error);
    } finally {
      pending = false;
      if (uploadButton) {
        uploadButton.disabled = false;
        uploadButton.textContent = 'Upload evidence';
      }
    }
  });
  submitCorrection?.addEventListener('click', async () => {
    if (pending) return;
    const errorHost = renderScope.querySelector('#correctionSubmitError');
    try {
      pending = true;
      submitCorrection.disabled = true;
      submitCorrection.textContent = 'Submitting…';
      await submitEmptyReturnCorrection({
        action_id: action.id,
        request_id: crypto.randomUUID(),
        expected_revision: action.revision,
      });
      toast('Correction sent to Dispatch.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo('#/dashboard');
    } catch (error) {
      showWorkflowError(null, errorHost, error);
    } finally {
      pending = false;
      submitCorrection.disabled = false;
      submitCorrection.textContent = 'Submit to Dispatch';
    }
  });
  applySystemInputGlow(form);
}

async function renderEmptyReturnReviewAction(action) {
  const workspaceRoute = Boolean(state.workspaceCaptureHost);
  const renderScope = state.workspaceCaptureHost || document;
  const taskId = action.source_task_id || docIdFromRef(action.source_task_ref);
  const evidence = await loadEvidenceAssets(taskId);
  const workspaceMarkup = `
    <section class="workflow-page workflow-page--workspace-action">${workflowActionHeader(action)}
      <section class="workflow-evidence-section workflow-evidence-section--workspace" aria-label="Empty Return evidence">${evidenceMarkup(evidence.items)}</section>
      <form class="workflow-form workflow-form--workspace empty-return-review-workspace" id="emptyReturnReviewForm" data-workspace-done-target novalidate>
        ${workspaceChoiceMarkup({
    name: 'review_decision',
    label: 'Decision',
    value: '',
    options: [
      { value: 'confirm', label: 'Confirm Empty Returned', tone: 'empty-returned' },
      { value: 'return', label: 'Return for correction', tone: 'pending' },
    ],
  })}
        <label class="wide"><span class="workspace-field-label">Return reason <em>Only when returning</em></span><textarea name="reason" maxlength="2000" placeholder="Explain exactly what must be corrected"></textarea><small data-field-error></small></label>
        <p class="workflow-form-error" id="emptyReturnReviewError" role="alert"></p>
      </form>
    </section>`;
  const fullPageMarkup = `
    <section class="workflow-page">${workflowActionHeader(action, 'Review the Driver evidence. Only Dispatch confirmation completes the Container.')}
      <section class="panel workflow-evidence-section"><h2>Driver evidence</h2>${evidenceMarkup(evidence.items)}</section>
      <form class="panel workflow-form" id="emptyReturnReviewForm" novalidate>
        <label class="wide">Return reason <span class="muted">Required only when returning</span><textarea name="reason" maxlength="2000" placeholder="Tell the Driver exactly what to correct"></textarea><small data-field-error></small></label>
        <p class="workflow-form-error" id="emptyReturnReviewError" role="alert"></p>
        <div class="actions"><button type="button" data-review-decision="confirm" ${evidence.items.length ? '' : 'disabled'}>Confirm Empty Returned</button><button class="secondary" type="button" data-review-decision="return">Return for correction</button></div>
      </form>
    </section>`;
  shell(workspaceRoute ? workspaceMarkup : fullPageMarkup);
  bindWorkflowBack();
  const form = renderScope.querySelector('#emptyReturnReviewForm');
  const errorHost = renderScope.querySelector('#emptyReturnReviewError');
  const choiceController = workspaceRoute ? mountWorkspaceChoiceControls(form) : null;
  registerActivePageCleanup(() => {
    choiceController?.destroy();
    evidence.cleanup();
  });
  let pending = false;
  const submitDecision = async (decision, button = null) => {
    if (pending) return;
    const reason = form.elements.reason.value.trim();
    if (!['confirm', 'return'].includes(decision)) {
      errorHost.textContent = 'Choose Confirm Empty Returned or Return for correction.';
      return;
    }
    if (decision === 'confirm' && evidence.items.length === 0) {
      errorHost.textContent = 'Verified Empty Return evidence is required before confirmation.';
      return;
    }
    if (decision === 'return' && !reason) {
      showWorkflowError(
        form,
        errorHost,
        new WorkflowUiError('EMPTY_RETURN_REVIEW_REASON_REQUIRED', 'reason'),
      );
      return;
    }
    try {
      pending = true;
      renderScope.querySelectorAll('[data-review-decision], .workspace-choice__option, textarea').forEach((control) => { control.disabled = true; });
      if (button) button.textContent = decision === 'confirm' ? 'Confirming…' : 'Returning…';
      await reviewEmptyReturnEvidence({
        action_id: action.id,
        request_id: crypto.randomUUID(),
        expected_revision: action.revision,
        decision,
        reason: decision === 'return' ? reason : null,
      });
      toast(decision === 'confirm' ? 'Container confirmed Empty Returned.' : 'Evidence returned to Driver.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo(workflowBackHash());
    } catch (error) {
      showWorkflowError(form, errorHost, error);
      renderScope.querySelectorAll('[data-review-decision], .workspace-choice__option, textarea').forEach((control) => { control.disabled = false; });
      if (button) button.textContent = decision === 'confirm' ? 'Confirm Empty Returned' : 'Return for correction';
      pending = false;
    }
  };
  if (workspaceRoute) {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void submitDecision(form.elements.review_decision.value);
    });
  } else {
    renderScope.querySelectorAll('[data-review-decision]').forEach((button) => button.addEventListener('click', () => {
      void submitDecision(button.dataset.reviewDecision, button);
    }));
  }
  applySystemInputGlow(form);
}

function centsInput(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

async function loadFinanceRecord(containerId) {
  const [summarySnap, linesSnap] = await Promise.all([
    getDoc(doc(db, 'finance_summaries', containerId)),
    getDocs(query(
      collection(db, 'finance_line_items'),
      where('container_id', '==', containerId),
      where('archived_by_restore', '==', false),
      limit(200),
    )),
  ]);
  return {
    summary: summarySnap.exists() ? { id: summarySnap.id, ...summarySnap.data() } : {
      id: containerId, revision: 0, customer_quote_cents: 0, customer_receivable_cents: 0,
      ctf_status: 'unpaid', tmf_status: 'unpaid', remittance_status: 'not_received',
      total_operational_cost_cents: 0, total_revenue_cents: 0, profit_cents: 0,
    },
    lines: linesSnap.docs.map((item) => ({ id: item.id, ...item.data() }))
      .filter((item) => item.archived !== true && item.archived_by_restore !== true),
  };
}

function financeLineByType(lines, lineType) {
  return lines.find((line) => line.line_type === lineType
    && line.archived !== true && line.archived_by_restore !== true) || null;
}

function financeLineDate(row, lineType, existingLine) {
  if (existingLine?.date) return existingLine.date;
  const source = {
    loaded_pickup_cost: row?.pickup_appointment_at,
    empty_return_cost: row?.empty_returned_at || row?.return_empty_lfd_at,
    transfer_cost: row?.transfer_date,
  }[lineType];
  const parsed = dateValue(source) || new Date();
  return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function financeLinePayee(row, lineType, existingLine) {
  if (existingLine?.name_or_payee) return existingLine.name_or_payee;
  const source = {
    loaded_pickup_cost: row?.pickup_driver_name,
    empty_return_cost: row?.return_driver_name,
    transfer_cost: row?.transfer_driver_name,
  }[lineType];
  return String(source || row?.pickup_company || 'KM Transportation').trim();
}

async function renderFinanceCompletionWorkspace(action, record, renderScope) {
  const summary = { ...record.summary };
  const lines = record.lines.map((line) => ({ ...line }));
  const row = state.dashboardDataController?.snapshot?.().rows
    ?.find((item) => item.id === action.container_id) || {};
  shell(`<section class="workflow-page workflow-page--workspace-action">
    ${financeCompletionWorkspaceMarkup({
    containerNo: action.container_no,
    state: financeCompletionWorkspaceState({ summary, lines }),
  })}
  </section>`);
  const form = renderScope.querySelector('.finance-completion-workspace');
  const errorHost = form.querySelector('[data-finance-workspace-error]');
  const choiceController = mountWorkspaceChoiceControls(form);
  registerActivePageCleanup(() => choiceController.destroy());
  let pending = false;
  let signature = '';
  let requestIds = new Map();
  const requestId = (key) => {
    if (!requestIds.has(key)) requestIds.set(key, crypto.randomUUID());
    return requestIds.get(key);
  };
  const setPending = (value) => {
    pending = value;
    form.querySelectorAll('button, input, textarea').forEach((control) => {
      control.disabled = value;
    });
  };
  const persistLine = async ({ lineType, amount, category, date, payee, details = '' }) => {
    const existing = financeLineByType(lines, lineType);
    if (!existing && Number(amount) === 0) return;
    const response = await mutateContainerFinance(buildFinanceLineRequest({
      line_type: lineType,
      category,
      date,
      name_or_payee: payee,
      amount,
      details,
    }, {
      containerId: action.container_id,
      requestId: requestId(`line:${lineType}`),
      summaryRevision: summary.revision,
      lineItemId: existing?.id || null,
      lineRevision: existing?.revision || null,
    }));
    summary.revision = response.data.summaryRevision;
    if (existing) {
      existing.revision = response.data.lineItemRevision;
      existing.amount_cents = Math.round(Number(amount) * 100);
      existing.date = date;
      existing.name_or_payee = payee;
      existing.details = details;
    } else if (response.data.lineItemId) {
      lines.push({
        id: response.data.lineItemId,
        line_type: lineType,
        amount_cents: Math.round(Number(amount) * 100),
        date,
        name_or_payee: payee,
        details,
        revision: response.data.lineItemRevision,
        archived: false,
      });
    }
  };
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    errorHost.textContent = '';
    if (!form.reportValidity()) return;
    const values = Object.fromEntries(new FormData(form).entries());
    const nextSignature = JSON.stringify(values);
    if (signature !== nextSignature) {
      signature = nextSignature;
      requestIds = new Map();
    }
    const specialAmount = String(values.special_amount || '').trim();
    if (specialAmount && (!String(values.special_date || '').trim() || !String(values.special_name || '').trim())) {
      errorHost.textContent = 'Special fee date and Driver / payee are required when an amount is entered.';
      return;
    }
    try {
      setPending(true);
      for (const definition of [
        ['loaded_pickup_cost', values.loaded_pickup_cost, 'Loaded Pickup Cost'],
        ['empty_return_cost', values.empty_return_cost, 'Empty Return Cost'],
        ['transfer_cost', values.transfer_cost, 'Transfer Cost'],
      ]) {
        const [lineType, amount, category] = definition;
        const existing = financeLineByType(lines, lineType);
        await persistLine({
          lineType,
          amount,
          category,
          date: financeLineDate(row, lineType, existing),
          payee: financeLinePayee(row, lineType, existing),
          details: existing?.details || '',
        });
      }
      if (specialAmount) {
        await persistLine({
          lineType: 'driver_special_cost',
          amount: specialAmount,
          category: 'Driver Special Cost',
          date: values.special_date,
          payee: values.special_name,
          details: values.special_details || '',
        });
      }
      const summaryResponse = await mutateContainerFinance(buildFinanceSummaryRequest({
        customer_quote: values.customer_quote,
        customer_receivable: values.customer_receivable,
        ctf_status: summary.ctf_status,
        tmf_status: summary.tmf_status,
        remittance_status: values.remittance_status,
      }, {
        containerId: action.container_id,
        requestId: requestId('summary'),
        revision: summary.revision,
      }));
      summary.revision = summaryResponse.data.summaryRevision;
      await completeContainerAction({
        action_id: action.id,
        request_id: requestId('complete'),
        expected_revision: action.revision,
        values: buildWorkflowActionValues('finance_completion', {
          finance_summary_revision: summary.revision,
        }),
      });
      toast('Finance action completed.');
      await state.dashboardDataController?.refresh?.();
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo('#/dashboard');
    } catch (error) {
      showWorkflowError(form, errorHost, error);
      setPending(false);
    }
  });
  applySystemInputGlow(form);
}

async function renderFinanceCompletionAction(action) {
  const workspaceRoute = Boolean(state.workspaceCaptureHost);
  const renderScope = state.workspaceCaptureHost || document;
  const record = await loadFinanceRecord(action.container_id);
  const { summary, lines } = record;
  if (workspaceRoute) {
    await renderFinanceCompletionWorkspace(action, record, renderScope);
    return;
  }
  const statusOptions = (selected) => `<option value="paid" ${selected === 'paid' ? 'selected' : ''}>Paid</option><option value="unpaid" ${selected === 'unpaid' ? 'selected' : ''}>Unpaid</option>`;
  const remittanceOptions = ['received', 'not_received'].map((value) => `<option value="${value}" ${summary.remittance_status === value ? 'selected' : ''}>${value.replaceAll('_', ' ')}</option>`).join('');
  shell(`
    <section class="workflow-page finance-workflow">${workflowActionHeader(action, 'Record costs and revenue, review the calculated profit, then complete Finance.')}
      <div class="finance-summary-cards"><div><span>Operational Cost</span><strong>$${centsInput(summary.total_operational_cost_cents)}</strong></div><div><span>Total Revenue</span><strong>$${centsInput(summary.total_revenue_cents)}</strong></div><div><span>Profit</span><strong>$${centsInput(summary.profit_cents)}</strong></div></div>
      <form class="panel workflow-form" id="financeSummaryForm" novalidate>
        <h2>Revenue and payment status</h2><div class="workflow-form-grid">
          <label>Customer Quote ($)<input name="customer_quote" type="number" min="0" step="0.01" value="${centsInput(summary.customer_quote_cents)}" required><small data-field-error></small></label>
          <label>Customer Receivable ($)<input name="customer_receivable" type="number" min="0" step="0.01" value="${centsInput(summary.customer_receivable_cents)}" required><small data-field-error></small></label>
          <label>CTF Status<select name="ctf_status">${statusOptions(summary.ctf_status)}</select><small data-field-error></small></label>
          <label>TMF Status<select name="tmf_status">${statusOptions(summary.tmf_status)}</select><small data-field-error></small></label>
          <label>Remittance Status<select name="remittance_status">${remittanceOptions}</select><small data-field-error></small></label>
        </div><p class="workflow-form-error" id="financeSummaryError" role="alert"></p><div class="actions"><button type="submit">Save Finance summary</button></div>
      </form>
      <section class="panel finance-lines"><h2>Costs and special charges</h2>
        ${lines.length ? `<div class="table-wrap"><table><thead><tr><th>Type</th><th>Category</th><th>Date</th><th>Payee</th><th>Amount</th><th>Actions</th></tr></thead><tbody>${lines.map((line) => `<tr><td>${escapeHtml(String(line.line_type).replaceAll('_', ' '))}</td><td>${escapeHtml(line.category)}</td><td>${escapeHtml(line.date)}</td><td>${escapeHtml(line.name_or_payee)}</td><td>$${centsInput(line.amount_cents)}</td><td><button class="secondary" type="button" data-edit-finance-line="${escapeHtml(line.id)}">Edit</button><button class="secondary danger" type="button" data-archive-finance-line="${escapeHtml(line.id)}">Archive</button></td></tr>`).join('')}</tbody></table></div>` : '<div class="workflow-empty"><p>No cost or charge lines yet.</p></div>'}
      </section>
      <form class="panel workflow-form" id="financeLineForm" novalidate><h2 id="financeLineTitle">Add cost or charge</h2><div class="workflow-form-grid">
        <label>Type<select name="line_type" required><option value="">Select type</option><option value="loaded_pickup_cost">Loaded Pickup Cost</option><option value="empty_return_cost">Empty Return Cost</option><option value="transfer_cost">Transfer Cost</option><option value="driver_special_cost">Driver Special Cost</option><option value="container_exception_cost">Container Exception Cost</option><option value="customer_special_charge">Customer Special Charge</option></select><small data-field-error></small></label>
        <label>Category<input name="category" maxlength="100" required><small data-field-error></small></label><label>Date<input name="date" type="date" required><small data-field-error></small></label><label>Name / Payee<input name="name_or_payee" maxlength="200" required><small data-field-error></small></label><label>Amount ($)<input name="amount" type="number" min="0" step="0.01" required><small data-field-error></small></label><label class="wide">Details<textarea name="details" maxlength="2000"></textarea><small data-field-error></small></label>
      </div><p class="workflow-form-error" id="financeLineError" role="alert"></p><div class="actions"><button type="submit">Save line</button><button class="secondary" type="button" data-cancel-finance-edit hidden>Cancel edit</button></div></form>
      <section class="panel finance-complete"><h2>Complete Finance</h2><p>Completing closes this department action. The Container remains canonically completed by Empty Returned.</p><p class="workflow-form-error" id="financeCompleteError" role="alert"></p><div class="actions"><button type="button" data-complete-finance ${summary.revision > 0 ? '' : 'disabled'}>Complete Finance</button></div></section>
    </section>`);
  bindWorkflowBack();
  const summaryForm = renderScope.querySelector('#financeSummaryForm');
  const lineForm = renderScope.querySelector('#financeLineForm');
  const refreshFinanceAction = async () => {
    if (workspaceRoute && state.taskWorkspaceController) {
      await state.taskWorkspaceController.refreshRoute(`#/action/${action.id}`);
      return;
    }
    await renderContainerActionDetail(action.id);
  };
  let pending = false;
  let editing = null;
  const disabledBeforePending = new WeakMap();
  const setPending = (value) => {
    pending = value;
    renderScope.querySelectorAll('button, input, select, textarea').forEach((control) => {
      if (control.matches('[data-workflow-back]')) return;
      if (value) {
        disabledBeforePending.set(control, control.disabled);
        control.disabled = true;
        return;
      }
      control.disabled = disabledBeforePending.get(control) ?? false;
      disabledBeforePending.delete(control);
    });
  };
  summaryForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !summaryForm.reportValidity()) return;
    try {
      const values = Object.fromEntries(new FormData(summaryForm).entries());
      const request = buildFinanceSummaryRequest(values, {
        containerId: action.container_id, requestId: crypto.randomUUID(), revision: summary.revision,
      });
      setPending(true);
      await mutateContainerFinance(request);
      toast('Finance summary saved.');
      await refreshFinanceAction();
    } catch (error) {
      showWorkflowError(summaryForm, renderScope.querySelector('#financeSummaryError'), error);
      setPending(false);
    }
  });
  renderScope.querySelectorAll('[data-edit-finance-line]').forEach((button) => button.addEventListener('click', () => {
    editing = lines.find((line) => line.id === button.dataset.editFinanceLine) || null;
    if (!editing) return;
    for (const field of ['line_type', 'category', 'date', 'name_or_payee', 'details']) {
      lineForm.elements[field].value = editing[field] || '';
    }
    lineForm.elements.amount.value = centsInput(editing.amount_cents);
    renderScope.querySelector('#financeLineTitle').textContent = 'Edit cost or charge';
    lineForm.querySelector('[data-cancel-finance-edit]').hidden = false;
    lineForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }));
  lineForm.querySelector('[data-cancel-finance-edit]').addEventListener('click', () => {
    editing = null; lineForm.reset();
    renderScope.querySelector('#financeLineTitle').textContent = 'Add cost or charge';
    lineForm.querySelector('[data-cancel-finance-edit]').hidden = true;
  });
  lineForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !lineForm.reportValidity()) return;
    try {
      const values = Object.fromEntries(new FormData(lineForm).entries());
      const request = buildFinanceLineRequest(values, {
        containerId: action.container_id, requestId: crypto.randomUUID(), summaryRevision: summary.revision,
        lineItemId: editing?.id || null, lineRevision: editing?.revision || null,
      });
      setPending(true);
      await mutateContainerFinance(request);
      toast(editing ? 'Finance line updated.' : 'Finance line added.');
      await refreshFinanceAction();
    } catch (error) {
      showWorkflowError(lineForm, renderScope.querySelector('#financeLineError'), error);
      setPending(false);
    }
  });
  renderScope.querySelectorAll('[data-archive-finance-line]').forEach((button) => button.addEventListener('click', async () => {
    if (pending) return;
    const line = lines.find((item) => item.id === button.dataset.archiveFinanceLine);
    if (!line) return;
    try {
      setPending(true);
      await mutateContainerFinance({
        operation: 'archive_line_item', container_id: action.container_id,
        request_id: crypto.randomUUID(), expected_summary_revision: summary.revision,
        line_item_id: line.id, expected_line_revision: line.revision,
      });
      toast('Finance line archived.');
      await refreshFinanceAction();
    } catch (error) {
      showWorkflowError(null, renderScope.querySelector('#financeLineError'), error);
      setPending(false);
    }
  }));
  renderScope.querySelector('[data-complete-finance]').addEventListener('click', async () => {
    if (pending) return;
    try {
      setPending(true);
      await completeContainerAction({
        action_id: action.id, request_id: crypto.randomUUID(), expected_revision: action.revision,
        values: buildWorkflowActionValues('finance_completion', { finance_summary_revision: summary.revision }),
      });
      toast('Finance action completed.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo(workflowBackHash());
    } catch (error) {
      showWorkflowError(null, renderScope.querySelector('#financeCompleteError'), error);
      setPending(false);
    }
  });
  applySystemInputGlow(renderScope);
}

async function loadEligibleDispatchDriverOptions() {
  const drivers = [];
  const seen = new Set();
  let cursorName = '';
  let cursorId = '';
  for (let page = 0; page < 10 && drivers.length < 200; page += 1) {
    const response = await listEligibleDispatchDrivers({
      search: '', cursorName, cursorId,
    });
    const rows = Array.isArray(response?.data?.drivers) ? response.data.drivers : [];
    rows.forEach((driver) => {
      if (!driver?.id || !driver?.fullName || seen.has(driver.id)) return;
      seen.add(driver.id);
      drivers.push({ id: driver.id, fullName: driver.fullName });
    });
    const next = response?.data?.nextCursor;
    if (!next?.id || !next?.name || response?.data?.truncated !== true) break;
    cursorName = next.name;
    cursorId = next.id;
  }
  return drivers;
}

async function renderDispatchPlanWorkspace(action) {
  const renderScope = state.workspaceCaptureHost || document;
  let drivers;
  let existing = {};
  try {
    const [driverRows, detailResponse] = await Promise.all([
      loadEligibleDispatchDriverOptions(),
      getMyContainerTaskDetail({ container_id: action.container_id }).catch(() => null),
    ]);
    drivers = driverRows;
    existing = detailResponse?.data?.visible_fields || {};
  } catch (error) {
    shell(`<section class="workflow-page workflow-page--workspace-action"><div class="workflow-context-number" data-workspace-container-number>${escapeHtml(action.container_no)}</div><p class="workflow-form-error" role="alert">Eligible Drivers could not be loaded. Try again.</p></section>`);
    return;
  }
  if (!drivers.length) {
    shell(`<section class="workflow-page workflow-page--workspace-action"><div class="workflow-context-number" data-workspace-container-number>${escapeHtml(action.container_no)}</div><p class="workflow-form-error" role="alert">Add an active Driver business record before planning this transport.</p></section>`);
    return;
  }
  shell(dispatchPlanWorkspaceMarkup({
    action,
    drivers,
    values: {
      chassis_no: existing.chassis_no || '',
      empty_container_no: existing.empty_container_no || '',
      pickup_appointment_at: actionDateTimeValue(existing.pickup_appointment_at),
      transfer_at: actionDateTimeValue(existing.transfer_at),
      transfer_from_address: existing.transfer_from_address || '',
      transfer_to_address: existing.transfer_to_address || '',
    },
  }));
  if (existing.customer_chassis === 'own_chassis') {
    const emptyContainerInput = renderScope.querySelector('[name="empty_container_no"]');
    if (emptyContainerInput) {
      emptyContainerInput.value = '';
      emptyContainerInput.disabled = true;
      emptyContainerInput.removeAttribute('required');
      emptyContainerInput.setAttribute('aria-describedby', 'empty-container-owned-chassis-note');
      emptyContainerInput.insertAdjacentHTML('afterend', '<small id="empty-container-owned-chassis-note">Not needed: customer uses own chassis.</small>');
    }
  }
  const form = renderScope.querySelector('[data-dispatch-plan-form]');
  const errorHost = renderScope.querySelector('[data-dispatch-plan-error]');
  const submit = form.querySelector('[data-workspace-done-target]');
  let pending = false;
  let signature = '';
  let requestId = '';
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    errorHost.textContent = '';
    if (!form.reportValidity()) return;
    const values = Object.fromEntries(new FormData(form).entries());
    const nextSignature = JSON.stringify(values);
    if (nextSignature !== signature) {
      signature = nextSignature;
      requestId = crypto.randomUUID();
    }
    try {
      const payload = buildDispatchPlanWorkspaceRequest({
        actionId: action.id,
        containerId: action.container_id,
        requestId,
        expectedRevision: action.revision,
        chassisNo: values.chassis_no,
        emptyContainerNo: values.empty_container_no,
        pickupAppointmentAt: values.pickup_appointment_at,
        transferDriverId: values.transfer_driver_id,
        transferAt: values.transfer_at,
        transferFromName: values.transfer_from_name,
        transferFromAddress: values.transfer_from_address,
        transferToName: values.transfer_to_name,
        transferToAddress: values.transfer_to_address,
        transferNotes: values.transfer_notes,
        loadedPickupDriverId: values.loaded_pickup_driver_id,
        loadedPickupAt: values.loaded_pickup_at,
        loadedPickupFromName: values.loaded_pickup_from_name,
        loadedPickupFromAddress: values.loaded_pickup_from_address,
        loadedPickupToName: values.loaded_pickup_to_name,
        loadedPickupToAddress: values.loaded_pickup_to_address,
        loadedPickupNotes: values.loaded_pickup_notes,
        emptyPickupDriverId: values.empty_pickup_driver_id,
        emptyPickupAt: values.empty_pickup_at,
        emptyPickupFromName: values.empty_pickup_from_name,
        emptyPickupFromAddress: values.empty_pickup_from_address,
        emptyPickupToName: values.empty_pickup_to_name,
        emptyPickupToAddress: values.empty_pickup_to_address,
        emptyPickupNotes: values.empty_pickup_notes,
        emptyReturnDriverId: values.empty_return_driver_id,
        emptyReturnAt: values.empty_return_at,
        emptyReturnFromName: values.empty_return_from_name,
        emptyReturnFromAddress: values.empty_return_from_address,
        emptyReturnToName: values.empty_return_to_name,
        emptyReturnToAddress: values.empty_return_to_address,
        emptyReturnNotes: values.empty_return_notes,
      });
      pending = true;
      submit.disabled = true;
      const response = await createDispatchPlan(payload);
      if (!Array.isArray(response?.data?.taskIds) || response.data.taskIds.length !== 4
        || response.data.status !== 'assigned') {
        throw new Error('The saved Dispatch plan could not be confirmed.');
      }
      toast('Complete Dispatch plan saved.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo(workflowBackHash());
    } catch (error) {
      showWorkflowError(form, errorHost, error);
    } finally {
      pending = false;
      submit.disabled = false;
    }
  });
  applySystemInputGlow(form);
}

async function renderEmptyReturnPlanWorkspace(action) {
  const renderScope = state.workspaceCaptureHost || document;
  let drivers;
  let existing = {};
  try {
    const [driverRows, detailResponse] = await Promise.all([
      loadEligibleDispatchDriverOptions(),
      getMyContainerTaskDetail({ container_id: action.container_id }).catch(() => null),
    ]);
    drivers = driverRows;
    existing = detailResponse?.data?.visible_fields || {};
  } catch (_) {
    shell(`<section class="workflow-page workflow-page--workspace-action"><div class="workflow-context-number" data-workspace-container-number>${escapeHtml(action.container_no)}</div><p class="workflow-form-error" role="alert">Eligible Drivers could not be loaded. Try again.</p></section>`);
    return;
  }
  shell(emptyReturnPlanWorkspaceMarkup({
    action,
    drivers,
    values: {
      empty_return_at: actionDateTimeValue(existing.return_empty_lfd_at),
      actual_empty_return_date: actionDateValue(existing.actual_empty_returned_at),
      actual_empty_return_time: existing.actual_empty_returned_precision === 'date_time'
        ? actionDateTimeValue(existing.actual_empty_returned_at).slice(11) : '',
      empty_return_from_address: existing.transfer_to_address || existing.delivery_warehouse_address || '',
      empty_return_to_name: existing.terminal_name || '',
    },
  }));
  const form = renderScope.querySelector('[data-empty-return-plan-form]');
  const errorHost = renderScope.querySelector('[data-empty-return-plan-error]');
  const submit = form.querySelector('[data-workspace-done-target]');
  const saveActual = async () => {
    const date = form.elements.actual_empty_return_date.value;
    const time = form.elements.actual_empty_return_time.value || '';
    if (!date) throw new Error('Return Appt Date is required.');
    await state.dashboardAutosaveController?.save({
      containerId: action.container_id,
      field: 'actual_empty_returned_at',
      value: { date, time },
    });
  };
  form.querySelector('[data-save-actual-empty-return]')?.addEventListener('click', async () => {
    errorHost.textContent = '';
    try {
      await saveActual();
      toast('Return Appt saved. Assign the driver when ready.');
    } catch (error) {
      errorHost.textContent = error?.message || 'The Return Appt could not be saved.';
    }
  });
  let pending = false;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !form.reportValidity()) return;
    const values = Object.fromEntries(new FormData(form).entries());
    try {
      await saveActual();
      const payload = buildDispatchAssignmentRequest({
        containerId: action.container_id,
        driverId: values.empty_return_driver_id,
        legType: 'return_empty',
        scheduledStartAt: values.empty_return_at,
        originName: values.empty_return_from_name,
        originAddress: values.empty_return_from_address,
        originType: 'yard',
        destinationName: values.empty_return_to_name,
        destinationAddress: values.empty_return_to_address,
        destinationType: 'terminal',
        dispatcherNotes: values.empty_return_notes,
      });
      pending = true;
      submit.disabled = true;
      const response = await createDispatchAssignment(payload);
      if (!response?.data?.taskId || response.data.status !== 'assigned') {
        throw new Error('The saved Empty Return plan could not be confirmed.');
      }
      toast('Empty Return plan saved.');
      await state.taskWorkspaceController?.confirmRoute(`#/action/${action.id}`);
      routeTo(workflowBackHash());
    } catch (error) {
      showWorkflowError(form, errorHost, error);
    } finally {
      pending = false;
      submit.disabled = false;
    }
  });
  applySystemInputGlow(form);
}

async function renderContainerActionDetail(id) {
  const workspaceRoute = Boolean(state.workspaceCaptureHost);
  const renderScope = state.workspaceCaptureHost || document;
  const actionSnap = await getDoc(doc(db, 'container_actions', id));
  if (!actionSnap.exists()) {
    shell(`<section class="workflow-page"><div class="workflow-empty is-error"><h1>Action unavailable</h1><p>This action does not exist or you cannot access it.</p><button class="secondary" data-workflow-back>Back</button></div></section>`);
    bindWorkflowBack();
    return;
  }
  const action = { id: actionSnap.id, ref: actionSnap.ref, ...actionSnap.data() };
  if (!canOpenWorkflowAction(action, { uid: state.user.uid, role: state.role })) {
    shell('<section class="workflow-page"><div class="workflow-empty is-error"><h1>Access denied</h1><p>This action belongs to another role or user.</p><button class="secondary" data-workflow-back>Back</button></div></section>');
    bindWorkflowBack();
    return;
  }
  if (action.status === 'completed') {
    shell(`<section class="workflow-page">${workflowActionHeader(action)}<div class="workflow-empty is-complete"><h2>Action completed</h2><p>This item is no longer actionable.</p></div></section>`);
    bindWorkflowBack();
    return;
  }
  if (action.action_type === 'dispatch_planning') return renderDispatchPlanWorkspace(action);
  if (action.action_type === 'dispatch_empty_planning') return renderEmptyReturnPlanWorkspace(action);
  if (action.action_type === 'driver_empty_return_correction') return renderEvidenceCorrectionAction(action);
  if (action.action_type === 'dispatch_empty_return_review') return renderEmptyReturnReviewAction(action);
  if (action.action_type === 'finance_completion') return renderFinanceCompletionAction(action);
  let existingValues = {};
  try {
    const detailResponse = await getMyContainerTaskDetail({ container_id: action.container_id });
    existingValues = detailResponse?.data?.visible_fields || {};
  } catch (error) {
    console.warn('Existing Action values could not be loaded.', error);
  }
  const fields = standardActionFields(action, existingValues);
  if (!fields) throw new Error('This action requires a dedicated workflow that is unavailable.');
  const formClass = workspaceRoute
    ? 'workflow-form workflow-form--workspace'
    : 'panel workflow-form';
  const formActions = workspaceRoute
    ? '<button type="submit" data-workspace-done-target hidden>Complete Action</button>'
    : '<div class="actions"><button type="submit">Complete Action</button><button class="secondary" type="button" data-workflow-back>Cancel</button></div>';
  shell(`
    <section class="workflow-page${workspaceRoute ? ' workflow-page--workspace-action' : ''}">${workflowActionHeader(action, 'Complete the required fields below. The next department is notified automatically.')}
      <form class="${formClass}" id="workflowActionForm" novalidate><div class="workflow-form-grid">${fields}</div><p class="workflow-form-error" id="workflowActionError" role="alert" aria-live="assertive"></p>${formActions}</form>
    </section>`);
  bindWorkflowBack();
  const choiceController = workspaceRoute ? mountWorkspaceChoiceControls(renderScope) : null;
  registerActivePageCleanup(() => choiceController?.destroy());
  bindStandardActionForm(action, renderScope);
}

function dashboardMetricIcon(kind) {
  const path = ({
    total: '<path d="M4 17 9 12l3 3 8-9"/><path d="M16 6h4v4"/>',
    active: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
    action: '<path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/>',
    risk: '<path d="M12 8v5"/><path d="M12 17h.01"/><path d="M10.3 3.7 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z"/>',
    completed: '<path d="m5 12 4 4L19 6"/>',
    revenue: '<circle cx="12" cy="12" r="8"/><path d="M15 8.5c-.7-.5-1.6-.8-2.6-.8-1.6 0-2.8.8-2.8 2s1 1.8 2.9 2.3c1.8.5 2.7 1.1 2.7 2.3 0 1.3-1.2 2.1-2.9 2.1-1.1 0-2.2-.4-3-1"/><path d="M12.4 6v12"/>',
    due: '<rect x="4" y="6" width="16" height="14" rx="2"/><path d="M8 3v6M16 3v6M4 11h16"/>',
  })[kind];
  return `<span class="dashboard-metric-card__icon" aria-hidden="true"><svg viewBox="0 0 24 24">${path || ''}</svg></span>`;
}

function dashboardMetricCard({ kind, label, value, history, labels, displayHistory }) {
  const trend = monthlyPercentChange(history.at(-1), history.at(-2), { locale: getLocale() });
  const arrow = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : trend.direction === 'flat' ? '→' : '';
  return `
    <article class="dashboard-metric-card dashboard-metric-card--${escapeHtml(kind)}" data-dashboard-metric="${escapeHtml(kind)}">
      <header class="dashboard-metric-card__heading">${dashboardMetricIcon(kind)}<span>${escapeHtml(label)}</span><b class="dashboard-metric-card__trend dashboard-metric-card__trend--${escapeHtml(trend.direction)}"><i aria-hidden="true">${escapeHtml(arrow)}</i><span class="dashboard-metric-card__trend-value">${escapeHtml(trend.label)}</span></b></header>
      <strong class="dashboard-metric-card__value" data-dashboard-metric-value>${escapeHtml(value)}</strong>
      ${kpiSparklineMarkup({ values: history, labels, displayValues: displayHistory, ariaLabel: t('dashboard.kpi.trend', { label }) })}
    </article>`;
}

function dashboardMetricCardsMarkup(summary, rows) {
  const history = containerKpiMonthlySeries({ rows, locale: getLocale() });
  const numberHistory = (kind) => history[kind].map((value) => Number(value).toLocaleString(getLocale()));
  const items = [
    { kind: 'total', label: t('dashboard.kpi.total'), value: summary.total.toLocaleString(getLocale()) },
    { kind: 'active', label: t('dashboard.kpi.active'), value: summary.active.toLocaleString(getLocale()) },
    { kind: 'risk', label: t('dashboard.kpi.risk'), value: summary.atRisk.toLocaleString(getLocale()) },
    { kind: 'completed', label: t('dashboard.kpi.completed7d'), value: summary.completed7d.toLocaleString(getLocale()) },
    { kind: 'due', label: t('dashboard.kpi.dueToday'), value: summary.dueToday.toLocaleString(getLocale()) },
  ];
  return items.map((item) => dashboardMetricCard({
    ...item,
    history: history[item.kind],
    labels: history.labels,
    displayHistory: numberHistory(item.kind),
  })).join('');
}

function dashboardDateMillis(value, endOfDay = false) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000';
  const millis = new Date(`${value}${suffix}`).getTime();
  return Number.isFinite(millis) ? millis : null;
}

/**
 * Pulls the rest of the pages before anyone looks at the table.
 *
 * The approved Ocean Status order is a property of the whole fleet and the
 * client is what applies it, so sorting one page of 25 sorts 25 rows out of 452
 * and puts whichever documents came first by id at the top, arranged in status
 * order among themselves. Alan reported the order simply not being there, three
 * times, and that is why: page one was never the first page of the order, it was
 * the first page of the database.
 *
 * Paging the rest in on scroll would not fix it either - the rows would reshuffle
 * under the cursor as each page landed.
 *
 * Five requests at 100 a page for 452 containers, and afterwards scrolling
 * touches the network not at all. The guard is a runaway stop, not a policy: if
 * the fleet ever outgrows it the controller still reports hasMore and the
 * load-status strip keeps its old job.
 */
async function loadRemainingDashboardPages(controller) {
  let guard = 0;
  while (controller.snapshot().hasMore && guard < 40) {
    guard += 1;
    await controller.loadNext();
  }
}

function dashboardQueryValue({
  search = '', dateField = '', from = '', to = '', statuses = [], cargo = null, missing = [],
  dispatched = false, appointment = null,
  ctfStatus = '', tmfStatus = '', customer_name = '', terminal_name = '', driver_name = '',
  ssl = '',
}) {
  const fromMillis = dashboardDateMillis(from);
  const toMillis = dashboardDateMillis(to, true);
  // 可提 is a status and a date range the query already understands (Alan,
  // 2026-09-26). The chip clears every other filter when pressed, so nothing
  // else is competing for these three keys while it is on.
  const appointmentRange = appointmentQuery(appointment);
  if (appointmentRange) {
    return {
      ...dashboardQueryValue({
        search, statuses, cargo, missing, dispatched,
        ctfStatus, tmfStatus, customer_name, terminal_name, driver_name, ssl,
      }),
      ...appointmentRange,
    };
  }
  return {
    search: String(search).trim(),
    dateField: dateField || null,
    fromMillis: fromMillis !== null && toMillis !== null ? fromMillis : null,
    toMillis: fromMillis !== null && toMillis !== null ? toMillis : null,
    statuses: Array.isArray(statuses) ? statuses : [],
    cargo: cargo === 'laden' || cargo === 'empty' ? cargo : null,
    // Any of eta / ssl / terminal: "show the Tasks without ANY of these". Multi
    // -select and unioned (Alan, 2026-08-13: "这三个是例外可以多选"). Filtered
    // against the same list the chips are built from, so a stale hash or a hand
    // -made request cannot ask the callable for a field it will refuse.
    missing: (Array.isArray(missing) ? missing : [])
      .filter((kind) => MISSING_FILTER_CHIPS.some(([known]) => known === kind)),
    // Only what has been sent to a driver (Alan, 2026-08-19: "只会显示被安排派送的
    // 任务和信息"). Answered by the server on dispatch_released, not over the loaded
    // page: 725 Containers against a 600-row page means a client-side version
    // would answer with whatever happened to be loaded and look complete.
    dispatched: dispatched === true,
    ctfStatus,
    tmfStatus,
    customer: customer_name,
    terminal: terminal_name,
    driver: driver_name,
    // Shipping line, added with the Filters rebuild (Alan, 2026-08-08).
    ssl,
  };
}

function dashboardInsightStatuses(insight) {
  return ({
    'at-risk': ['pending'],
    completed: ['empty_returned'],
    'return-bottleneck': ['delivered_to_warehouse'],
  })[String(insight || '')] || [];
}

function updateDashboardMetricCards(summary) {
  if (!summary) return;
  const values = {
    total: summary.total,
    active: summary.active,
    action: summary.actionRequired,
    risk: summary.atRisk,
    completed: summary.completed7d,
    due: summary.dueToday,
    revenue: new Intl.NumberFormat(getLocale(), {
      style: 'currency', currency: 'USD', currencyDisplay: 'code', maximumFractionDigits: 0,
    }).format((summary.revenue7dCents || 0) / 100),
  };
  for (const [kind, value] of Object.entries(values)) {
    const target = root.querySelector(`[data-dashboard-metric="${kind}"] [data-dashboard-metric-value]`);
    if (target && value !== null && value !== undefined) {
      target.textContent = typeof value === 'number' ? value.toLocaleString(getLocale()) : value;
    }
  }
}

// How many Containers are laden and how many are empty, on the two chips that
// filter by exactly that. The counts come from the server summary, which is
// computed over every matching Container rather than the loaded page -- the
// table pages at 25 and grows as you scroll, so counting rows in the DOM would
// understate both numbers and keep changing as the user scrolls.
function updateDashboardCargoCounts(summary) {
  // Dispatched carries a number too (Alan, 2026-08-19: "已派单要有数字"). Same
  // source and same rule as the other two - the server counts it over the whole
  // fleet in the pass that already reads dispatch_released, so it does not move
  // as the table pages.
  const dispatched = root.querySelector('[data-dispatched-count]');
  if (dispatched) {
    const count = leftPaneChipCount(summary?.dispatched, dashboardSplitCountExclusion?.dispatched);
    const known = Number.isSafeInteger(count);
    dispatched.textContent = known ? count.toLocaleString(getLocale()) : '';
    dispatched.hidden = !known;
  }
  // 可提: same source, same rule - counted over the whole fleet, so the number is
  // the rows pressing it shows. Kept for the day strip, which is drawn from it.
  const appointmentCount = root.querySelector('[data-appointment-count]');
  if (appointmentCount) {
    const count = summary?.appointments?.total;
    const known = Number.isSafeInteger(count);
    appointmentCount.textContent = known ? count.toLocaleString(getLocale()) : '';
    appointmentCount.hidden = !known;
  }
  if (summary?.appointments) dashboardAppointmentSummary = summary.appointments;
  renderDashboardAppointmentDays();
  for (const kind of ['laden', 'empty']) {
    const target = root.querySelector(`[data-cargo-count="${kind}"]`);
    if (!target) continue;
    const count = summary?.[kind];
    const known = Number.isSafeInteger(count);
    target.textContent = known ? count.toLocaleString(getLocale()) : '';
    target.hidden = !known;
    const chip = target.closest('[data-cargo-filter]');
    if (chip) {
      const label = kind === 'laden' ? 'laden' : 'empty';
      chip.setAttribute('aria-label', known
        ? `Show ${label} Containers only, ${count} Container${count === 1 ? '' : 's'}`
        : `Show ${label} Containers only`);
    }
  }
  updateDashboardMissingCounts(summary?.missing);
}

/**
 * The three chase-me chips: how many Containers are still without ETA, shipping
 * line and terminal (Alan, 2026-08-13).
 *
 * A chip at zero is hidden rather than shown reading 0. The number is the size of
 * a job, so nothing left to chase should leave nothing on screen - and a row of
 * three permanent zeroes is exactly the chrome that buries the one real number
 * beside it.
 */
function updateDashboardMissingCounts(missing) {
  const zh = String(getLocale()).toLowerCase().startsWith('zh');
  // The Dispatched toggle carries no count - it is a view, not a worklist with a
  // size - but its label switches language with the rest of the bar.
  const dispatchedLabel = root.querySelector('[data-dispatched-label]');
  if (dispatchedLabel) {
    dispatchedLabel.textContent = zh ? dispatchedLabel.dataset.labelZh : dispatchedLabel.dataset.labelEn;
  }
  const appointmentLabel = root.querySelector('[data-appointment-label]');
  if (appointmentLabel) {
    appointmentLabel.textContent = zh ? appointmentLabel.dataset.labelZh : appointmentLabel.dataset.labelEn;
  }
  for (const [kind, , , description] of MISSING_FILTER_CHIPS) {
    const target = root.querySelector(`[data-missing-count="${kind}"]`);
    const chip = target?.closest('[data-missing-filter]');
    if (!target || !chip) continue;
    const count = leftPaneChipCount(missing?.[kind], dashboardSplitCountExclusion?.missing?.[kind]);
    const known = Number.isSafeInteger(count) && count > 0;
    target.textContent = known ? count.toLocaleString(getLocale()) : '';
    // Hidden while pressed would take the control away mid-use, and the last one
    // filled in is exactly when the count reaches zero.
    chip.hidden = !known && chip.getAttribute('aria-pressed') !== 'true';
    const label = chip.querySelector('[data-missing-label]');
    if (label) {
      label.textContent = zh ? label.dataset.labelZh : label.dataset.labelEn;
    }
    chip.setAttribute('aria-label', known
      ? `${description}, ${count} Container${count === 1 ? '' : 's'}`
      : description);
  }
}

function renderDashboardLoadStatus(controller, loadNext = () => controller.loadNext()) {
  const host = document.getElementById('dashboardLoadStatus');
  if (!host) return;
  const snapshot = controller.snapshot();
  // Nothing is said while loading. The rows already on screen stay on screen and
  // the next page appends beneath them, so a "Loading Containers…" line only
  // replaces the sentence that tells the operator more will arrive - it flickers
  // between the two on every page. A failure still speaks, because that is the
  // one case where waiting longer will not help.
  if (snapshot.loading) return;
  if (snapshot.error) {
    host.innerHTML = `<span>${escapeHtml(t('loading.failed'))}</span><button type="button" class="secondary" data-dashboard-retry>${escapeHtml(t('common.retry'))}</button>`;
    host.querySelector('[data-dashboard-retry]')?.addEventListener('click', () => {
      void loadNext();
    }, { once: true });
    return;
  }
  host.innerHTML = snapshot.hasMore ? '<span>More Containers load as you approach the end.</span>' : '';
}

// The chips never store their own flag: the active chip is read back from the query.
//
// The cargo leg is its own query field, not a list of Ocean Statuses. Laden means
// every Container that is not empty, which a status list cannot say -- a retired
// alias or a row with no status matched neither chip and fell out of both counts.
// As a leg the two chips partition the table, so their counts always add up to
// the total (Alan, 2026-07-31).
function activeCargoFilterKind(cargo) {
  if (cargo === 'laden' || cargo === 'empty') return cargo;
  return 'all';
}

function cargoFilterLeg(kind) {
  return kind === 'laden' || kind === 'empty' ? kind : null;
}

function bindDashboardQueryControls({
  controller, emptiesSource, preferenceStore, preferences, columns, workQueue,
  // Handed in, not read off `state` (2026-08-16). It was on `state` for one day
  // and the pairing split silently did nothing on production the whole time:
  // `state.mountContainerPane` was undefined here, the guard returned, and the
  // button changed nothing. A dependency passed through a shared mutable object
  // has no call site to look at when it turns out to be missing - this one is
  // handed over on the line after it is created.
  mountContainerPane,
}) {
  const search = document.getElementById('dashboardSearch');
  const from = document.getElementById('dashboardRangeFrom');
  const to = document.getElementById('dashboardRangeTo');
  const copyButton = document.getElementById('dashboardCopyContainersButton');
  const filterButton = document.getElementById('dashboardFilterButton');
  const editLockButton = document.getElementById('dashboardEditLockButton');
  const cargoChips = [...root.querySelectorAll('[data-cargo-filter]')];
  const dispatchedChip = root.querySelector('[data-dispatched-filter]');
  const appointmentChip = root.querySelector('[data-appointment-filter]');
  const appointmentDays = root.querySelector('[data-appointment-days]');
  const missingChips = [...root.querySelectorAll('[data-missing-filter]')];
  const overlayManager = createOverlayManager({ documentRef: document });
  const filterState = {
    dateField: preferences.dateField,
    statuses: dashboardInsightStatuses(hashParams().get('insight')),
    cargo: null,
    missing: [],
    dispatched: false,
    // null, 'all', or one Los Angeles day 'YYYY-MM-DD' (the 可提 chip).
    appointment: null,
    ctfStatus: '',
    tmfStatus: '',
    customer_name: '',
    terminal_name: '',
    driver_name: '',
    ssl: '',
  };
  let searchTimer = null;
  let copyResetTimer = null;

  if (from) from.value = preferences.from;
  if (to) to.value = preferences.to;

  const currentQuery = () => dashboardQueryValue({
    search: search?.value || '',
    dateField: filterState.dateField,
    from: from?.value || '',
    to: to?.value || '',
    ...filterState,
  });

  const renderBulkCopyState = ({ status, count = 0 }) => {
    if (!copyButton?.isConnected) return;
    if (copyResetTimer !== null) {
      window.clearTimeout(copyResetTimer);
      copyResetTimer = null;
    }
    const label = copyButton.querySelector('[data-copy-label]');
    const text = ({
      idle: 'Copy Container #',
      copying: count > 0 ? `Copying ${count}…` : 'Copying…',
      success: `Copied ${count}`,
      empty: 'No containers',
      limit: 'Refine filters',
      error: 'Retry copy',
    })[status] || 'Copy Container #';
    const icon = copyButton.querySelector('[data-copy-icon]');
    if (icon) icon.innerHTML = topbarIcon(status === 'success' ? 'check' : 'copy');
    if (label) label.textContent = text;
    copyButton.disabled = status === 'copying';
    copyButton.dataset.copyState = status;
    if (['success', 'empty', 'limit', 'error'].includes(status)) {
      copyResetTimer = window.setTimeout(() => bulkCopyController?.cancel(), 1800);
    }
  };

  // The numbers that last went to the clipboard, in the order they went. Bulk
  // paste lays its answers against exactly this list: it is what the terminal's
  // site or the spreadsheet was asked about, whatever the table has done since.
  const clipboard = createDashboardClipboard();
  // The table this toolbar drives: the single table, or - while the split is up and
  // the toolbar sits over its left pane - that pane's (2026-09-27). Copy, its marks
  // and 批量上传 all work in the order the operator is looking at, so they follow it.
  const toolbarTable = () => (state.browseSplit?.isOpen()
    ? state.browseSplit.paneTable('others') : null) ?? state.adaptiveTableController;
  const bulkCopyController = copyButton && clipboard
    ? createDashboardBulkCopyController({
      clipboard,
      onState: renderBulkCopyState,
      // The numbers the table is showing, in the order it is showing them, so
      // copying "the first ten" means the ten under the operator's eyes rather than
      // ten the server picked in document-id order (Alan, 2026-08-08). Read from the
      // table itself since 2026-09-27: a chip or a heading can sort it by a date,
      // and the approved Ocean Status order it used to be rebuilt from is then not
      // what is on screen.
      numbers: () => (toolbarTable()?.rowsInDisplayOrder?.() ?? sortDashboardContainers(controller.snapshot().rows))
        .map((row) => row.container_no),
      maxItems: 5000,
    })
    : null;

  const copyMenuController = copyButton && bulkCopyController
    ? mountDashboardCopyMenu({
      trigger: copyButton,
      overlayManager,
      maxItems: 5000,
      locale: () => getLocale(),
      // What the filters currently match, so the panel can say how many groups
      // the number divides them into.
      total: () => bulkCopyController.available(),
      // The number is a group size, not a total: the terminal sites only accept
      // so many at a time, so the fleet is cut into groups and copied one at a
      // time (Alan, 2026-08-08).
      // Copying is the first half of a lookup, so the rows that went out are
      // marked in the table and stay marked across groups until they are cleared
      // (Alan, 2026-08-09).
      onCopy: (size, index) => bulkCopyController
        .copyGroup(currentQuery(), { size, index })
        .then((result) => {
          if (result?.copied) {
            toolbarTable()?.markCopiedContainers(result.numbers);
          }
          return result;
        })
        .catch(() => {}),
      onCopyAll: () => bulkCopyController.copy(currentQuery())
        .then((result) => {
          if (result?.copied) {
            toolbarTable()?.markCopiedContainers(result.numbers);
          }
          return result;
        })
        .catch(() => {}),
      // Highlight the group the stepper is sitting on, using the same slice the
      // copy would take (Alan, 2026-08-11: so you can track which group you are
      // looking for while you step through them).
      onPreviewGroup: (size, index) => toolbarTable()
        ?.previewCopyGroup?.(bulkCopyController.groupNumbers({ size, index })),
      markedCount: () => toolbarTable()?.copiedContainerCount?.() || 0,
      onClearMarks: () => toolbarTable()?.clearCopiedContainers?.(),
    })
    : null;

  // The highlighter: a colour, then a click on any cell. It reaches every table on screen
  // (the single one, or both split panes); the marks live in each table and are never saved.
  const highlightGroup = document.querySelector('[data-highlighter]');
  if (highlightGroup) {
    const highlightTables = () => {
      const split = state.browseSplit?.isOpen?.() ? state.browseSplit : null;
      return [state.adaptiveTableController, split?.paneTable('others'), split?.paneTable('empty')]
        .filter((table) => typeof table?.setHighlightColor === 'function');
    };
    const toggle = highlightGroup.querySelector('#dashboardHighlightButton');
    const tools = highlightGroup.querySelector('[data-highlighter-tools]');
    let highlightColor = null;
    const paintHighlighter = () => {
      toggle.setAttribute('aria-pressed', String(highlightColor !== null));
      tools.hidden = highlightColor === null;
      highlightGroup.querySelectorAll('[data-highlight-color]').forEach((swatch) => {
        swatch.setAttribute('aria-pressed', String(swatch.dataset.highlightColor === highlightColor));
      });
      highlightTables().forEach((table) => table.setHighlightColor(highlightColor));
    };
    toggle.addEventListener('click', () => {
      highlightColor = highlightColor === null ? 'yellow' : null;
      paintHighlighter();
    });
    highlightGroup.addEventListener('click', (event) => {
      const swatch = event.target.closest?.('[data-highlight-color]');
      if (swatch) {
        highlightColor = swatch.dataset.highlightColor;
        paintHighlighter();
      } else if (event.target.closest?.('[data-highlight-clear]')) {
        highlightTables().forEach((table) => table.clearCellMarks());
      }
    });
    // Panes made by opening or closing 分屏 start with the pen down, or up, to match.
    document.getElementById('dashboardBrowseSplitButton')?.addEventListener('click', () => {
      setTimeout(paintHighlighter, 700);
    });
  }

  // 多选 (Alan, 2026-10-11): tick Containers, pick a column, give it a value. Each write goes through the
  // same autosave a typed cell uses, so permission, revision and audit are the cell's own.
  const bulkEditRoot = document.querySelector('[data-bulk-edit-bar]');
  const selectModeButton = document.getElementById('dashboardSelectModeButton');
  if (bulkEditRoot && selectModeButton) {
    mountDashboardBulkEditBar({
      root: bulkEditRoot,
      button: selectModeButton,
      role: state.role,
      columns,
      getTables: () => {
        const split = state.browseSplit?.isOpen?.() ? state.browseSplit : null;
        return [state.adaptiveTableController, split?.paneTable('others'), split?.paneTable('empty')]
          .filter((table) => typeof table?.setSelectMode === 'function');
      },
      save: (payload) => (state.dashboardAutosaveController
        ? state.dashboardAutosaveController.save(payload)
        : Promise.reject(new Error('SAVE_UNAVAILABLE'))),
      loadDriverOptions: loadEligibleDispatchDriverOptions,
    });
  }

  // Edit is a lock against the accidental tap, not a mode you wait for (Alan,
  // 2026-08-09). The cells are always built as their editable selves, so this
  // flips a class and nothing repaints - the two-second toggle it replaces was
  // rewriting every cell in the table.
  if (editLockButton) {
    // The button says which state it is in, in words - a tint alone left Alan
    // unsure whether the table was open or not (2026-08-09: "编辑按钮需要有提示到底
    // 打开没打开需要展示清楚").
    const paintLock = (unlocked) => {
      editLockButton.setAttribute('aria-pressed', String(unlocked));
      editLockButton.setAttribute(
        'aria-label',
        unlocked ? '锁定表格 Lock table editing' : '解锁表格编辑 Unlock table editing',
      );
      const icon = editLockButton.querySelector('[data-edit-lock-icon]');
      const label = editLockButton.querySelector('[data-edit-lock-label]');
      if (icon) icon.textContent = unlocked ? '✏️' : '🔒';
      if (label) label.textContent = unlocked ? '编辑中' : '已锁定';
    };
    paintLock(state.adaptiveTableController?.isLocked() === false);
    editLockButton.addEventListener('click', () => {
      const locked = state.adaptiveTableController?.isLocked() !== false;
      paintLock(state.adaptiveTableController?.setLocked(!locked) === false);
      // The split's panes are their own table instances with their own lock, and
      // this button only ever spoke to the Dashboard's. So they mounted locked and
      // stayed locked: the toolbar read 编辑中 while every cell in both panes
      // refused to open (Alan, 2026-08-16: "这个状态下应该可以编辑的为什么我编辑不了").
      // One switch, every table on screen.
      browseSplit.syncLock();
    });
  }

  // Whatever the toolbar's lock is right now. Called when a pane is created too -
  // a table mounts locked by default, so a pane appearing while the operator has
  // already unlocked would arrive locked and disagree with the button above it.
  const dashboardIsUnlocked = () => state.adaptiveTableController?.isLocked() === false;

  // A new query is a new fleet: it has to arrive whole before the order means
  // anything, for the same reason the first load does.
  // The curtain goes up while the fleet loads, and it takes the clicks with it
  // (Alan, 2026-08-19: "系统在加载的时候包括在加载container的时候播放圈动画并且不让
  // 在这个同时点击别的按钮不然一直这样子就让系统发不过来").
  //
  // A filter press starts a full re-read of the fleet - reset plus every
  // remaining page. It is the slowest thing on this screen, and with nothing on
  // top of it a second press lands mid-flight and starts another, which is how
  // one impatient hand turns into four overlapping reads of 725 Containers.
  //
  // showRouteLoading is the same overlay the routes use: it covers the viewport,
  // so it blocks the presses by existing rather than by disabling each control -
  // and it releases itself after ROUTE_LOADING_RELEASE_MS, so a query that never
  // settles leaves the app usable instead of locked.
  // What is narrowing the table right now, in the words on the buttons.
  //
  // "No Containers match this view" cannot tell a mistyped digit from a filter
  // left switched on from the row having gone missing - and Alan asked all three
  // questions in one day, the last time about a Container number that exists
  // (2026-08-19). The empty row names the filters instead of describing itself.
  const describeActiveFilters = () => {
    const zh = String(getLocale()).toLowerCase().startsWith('zh');
    const parts = [];
    // Not `const search` - that would shadow the input element in its own
    // initializer and throw before the sentence is ever built.
    const typedFull = String(search?.value ?? '').trim();
    // A pasted list of numbers would make this sentence a wall of text.
    const typed = typedFull.length > 40 ? `${typedFull.slice(0, 37)}…` : typedFull;
    if (typed) parts.push(zh ? `搜索「${typed}」` : `search "${typed}"`);
    if (filterState.cargo === 'laden') parts.push(zh ? '重柜' : 'Loaded');
    if (filterState.cargo === 'empty') parts.push(zh ? '空柜' : 'Empty');
    if (filterState.dispatched) parts.push(zh ? '已派单' : 'Dispatched');
    if (filterState.appointment) {
      parts.push(zh ? '可提有预约' : 'Available with an appointment');
      if (filterState.appointment !== 'all') parts.push(appointmentSelectionLabel(filterState.appointment));
    }
    for (const kind of filterState.missing) {
      const chip = MISSING_FILTER_CHIPS.find(([known]) => known === kind);
      if (chip) parts.push(zh ? chip[2] : chip[1]);
    }
    if (filterState.statuses.length) parts.push(zh ? '状态筛选' : 'a status filter');
    for (const [key, label, zhLabel] of [
      ['customer_name', 'customer', '客户'], ['terminal_name', 'terminal', '码头'],
      ['driver_name', 'driver', '司机'], ['ssl', 'shipping line', '船司'],
      ['ctfStatus', 'CTF', 'CTF'], ['tmfStatus', 'TMF', 'TMF'],
    ]) {
      if (String(filterState[key] ?? '').trim()) parts.push(zh ? zhLabel : label);
    }
    if (from?.value && to?.value) parts.push(zh ? '日期范围' : 'a date range');
    if (!parts.length) return '';
    return zh
      ? `没有柜子符合:${parts.join(' + ')}。清掉其中一个再试。`
      : `Nothing matches ${parts.join(' + ')}. Clear one of them and try again.`;
  };

  // Many numbers pasted at once (Alan, 2026-09-30: "可以批量的搜索柜子一次性的搜索很多"):
  // once the whole answer is in, say which of them are not in the system. Skipped if the
  // text changed while it loaded - that report would describe a question nobody is
  // asking any more.
  const reportBatchSearch = (searchedText) => {
    if (String(search?.value ?? '') !== searchedText) return;
    const report = batchSearchReport({ search: searchedText, rows: controller.snapshot().rows });
    if (report) toast(batchSearchText(report, getLocale()));
  };

  const applyQuery = () => {
    bulkCopyController?.cancel();
    // Before the reset: the right pane starts its own question from the empties
    // it is showing, and in a moment the toolbar's rows will be empty.
    ensureEmptiesSource();
    state.adaptiveTableController?.setEmptyReason(describeActiveFilters());
    state.adaptiveTableController?.setSearchOrder?.(dashboardSearchListTerms(search?.value ?? ''));
    showRouteLoading(document, '载入柜子…');
    // The curtain lifts on the FIRST page, not on the whole fleet.
    //
    // It covered both when it went in this morning, and that made every filter
    // press wait for all 765 Containers before the screen came back - two round
    // trips and about 2.3 MB - where the table used to be usable as soon as the
    // first 600 landed. Alan felt it immediately (2026-08-19: "我要的是比excel表格
    // 更方便更好用现在我反而感觉更卡"), and he is right: a curtain that outstays
    // the wait it explains is just a slower app.
    //
    // The rest still loads, behind the table, because sorting and filtering run
    // over the whole set - it just no longer holds the screen hostage while it
    // does.
    const searchedText = String(search?.value ?? '');
    return controller.reset(currentQuery())
      .finally(() => hideRouteLoading(document))
      .then(() => loadRemainingDashboardPages(controller))
      .then(() => reportBatchSearch(searchedText))
      .catch(() => {});
  };

  const onSearch = () => {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(applyQuery, 220);
  };
  search?.addEventListener('input', onSearch);

  // A date range typed while 可提 is on is a different question from 可提's own
  // days, and the chip's range would silently win over it - so the chip steps aside.
  const releaseAppointmentForRange = () => {
    if (!filterState.appointment || !(from?.value || to?.value || filterState.from || filterState.to)) return;
    filterState.appointment = null;
    syncAppointmentChip();
  };
  const onRange = () => {
    preferenceStore.save({ from: from?.value || '', to: to?.value || '' });
    releaseAppointmentForRange();
    void applyQuery();
  };
  from?.addEventListener('change', onRange);
  to?.addEventListener('change', onRange);

  const filterController = filterButton ? mountDashboardFilterPopover({
    trigger: filterButton,
    overlayManager,
    columns,
    initial: filterState,
    // Shipping line and terminal are imported values with no catalog behind
    // them, so the only honest source of "which terminals exist" is the ones in
    // front of you (Alan, 2026-08-08).
    rows: () => controller.snapshot().rows,
    // Not while the split is up: the left pane never shows a reported empty.
    excludeStatuses: () => (state.browseSplit?.isOpen() ? BROWSE_SPLIT_EMPTY_STATUSES : []),
    onApply(next) {
      Object.assign(filterState, next);
      preferenceStore.save({ dateField: filterState.dateField });
      // The chosen timeline takes over the table's order, and the range it bounds
      // travels as the query's own from/to.
      dashboardTimelineField = next.from || next.to ? String(next.dateField || '') : '';
      if (from) from.value = next.from || '';
      if (to) to.value = next.to || '';
      releaseAppointmentForRange();
      syncCargoChips();
      void applyQuery();
    },
  }) : null;

  function syncCargoChips() {
    const active = activeCargoFilterKind(filterState.cargo);
    for (const chip of cargoChips) {
      chip.setAttribute('aria-pressed', String(chip.dataset.cargoFilter === active));
    }
    for (const chip of missingChips) {
      chip.setAttribute(
        'aria-pressed',
        String(filterState.missing.includes(chip.dataset.missingFilter)),
      );
    }
  }

  // The Work Pool button is gone (Alan, 2026-08-08). The queue it filtered by is
  // still computed and still drives the live-refresh signal; what went is the
  // toggle, which asked the operator to switch the table into a second mode to
  // see a subset the filters already reach.
  const stopWorkPoolSubscription = workQueue?.subscribe(() => {}) || (() => {});

  /**
   * A chip is a fresh start, not another narrowing (Alan, 2026-08-13: "这里的任何
   * button不受任何filter 和search影响点击就等于说直接只显示这些并清楚其他的filter
   * 的所有filter").
   *
   * Every one of these rows answers a question about the WHOLE fleet - how many
   * are loaded, how many still have no ETA - and the number on the chip is
   * counted over the whole fleet too. Pressing one while a search and three
   * filters were still on showed a subset of a subset under a chip promising 53,
   * which is the chip lying about itself.
   *
   * So it clears the lot: the search box, the date range, the status filter and
   * every field filter, including the Filters panel's own copy of them. What it
   * does NOT do is stop you filtering afterwards - "但是当选择这些后在去选择filter
   * 会改变" - so the chip is the starting point and anything chosen next narrows
   * within it.
   */
  const resetFiltersForChip = () => {
    filterState.statuses = [];
    filterState.dispatched = false;
    root.querySelector('[data-dispatched-filter]')?.setAttribute('aria-pressed', 'false');
    filterState.appointment = null;
    syncAppointmentChip();
    filterState.ctfStatus = '';
    filterState.tmfStatus = '';
    filterState.customer_name = '';
    filterState.terminal_name = '';
    filterState.driver_name = '';
    filterState.ssl = '';
    // Set by the panel's own Apply, which does Object.assign(filterState, next).
    // Leaving them behind would keep the date range in the query even with both
    // boxes emptied on screen.
    filterState.from = '';
    filterState.to = '';
    if (search) search.value = '';
    if (from) from.value = '';
    if (to) to.value = '';
    // The Filters panel is handed this exact object as `initial` and rebuilds its
    // markup from it every time it opens, so clearing these keys is what clears
    // its checkboxes too. Anything left set here would come back ticked over a
    // table it no longer describes, and the next Apply would restore all of it.
    dashboardTimelineField = '';
  };

  // A chip is a worklist, and a worklist has an order (Alan, 2026-08-16: 点击重柜
  // filter出所有的可提的柜子按pickup appointment排序).
  //
  // Loaded only. The empties were measured first and carry no date at all: of the
  // 55 reported empties in production, 0 have a reported_empty_at and 0 have a
  // return_empty_lfd_at - the status was set by hand rather than through Report
  // Empty, which is the only thing that stamps it. Sorting them would order 55
  // rows by nothing while looking deliberate, so they keep the resting order until
  // there is a real date to sort on (Alan chose to leave it).
  //
  // Loaded is thin too - 2 of 44 carry an appointment - but the rule is right and
  // it fills as he uses it, where the empty one would stay empty.
  const CARGO_CHIP_SORT = Object.freeze({ laden: 'pickup_appointment_at' });

  const onCargoFilter = (event) => {
    const kind = event.currentTarget.dataset.cargoFilter;
    // Pressed while the split is up, it is the latest word on the cargo: turning
    // the split off must not put back the Empty chip the split once set aside.
    cargoBeforeBrowse = undefined;
    resetFiltersForChip();
    filterState.cargo = cargoFilterLeg(kind);
    // Null for every other chip, which puts the resting order back rather than
    // leaving the last chip's sort on a list it does not describe.
    // Soonest first (Alan, 2026-09-26: "早的在上") - it was newest first until then.
    state.adaptiveTableController?.setDateSort(CARGO_CHIP_SORT[kind] ?? null, { ascending: true });
    // The two rows are one choice. Loaded and No ETA answer different questions
    // about the same fleet, and a table narrowed by both at once is a third thing
    // nobody asked for - so pressing either releases the other. The missing chips
    // multi-select among THEMSELVES; this releases all of them at once.
    filterState.missing = [];
    syncCargoChips();
    void applyQuery();
  };
  for (const chip of cargoChips) chip.addEventListener('click', onCargoFilter);

  // Released to a driver, and nothing else on top of it (Alan, 2026-08-19: "上面
  // 的按钮这些全是独立的不会和派单混合。派单就会直接过滤掉其他只会显示空柜和重柜").
  //
  // I built it as a toggle that combined with the cargo chip - "the empties I have
  // already sent" seemed a question worth asking. It is not the question he wants
  // this button to answer. Dispatched is a VIEW: press it and the fleet narrows to
  // what has gone out, loaded and empty together, with every other filter released.
  // So it behaves like the cargo chips do to each other rather than stacking on
  // them, and pressing it again gives the whole fleet back.
  const onDispatchedFilter = () => {
    const next = !filterState.dispatched;
    resetFiltersForChip();
    filterState.dispatched = next;
    filterState.cargo = null;
    filterState.missing = [];
    state.adaptiveTableController?.setDateSort(null);
    syncCargoChips();
    syncDispatchedChip();
    void applyQuery();
  };
  const syncDispatchedChip = () => {
    dispatchedChip?.setAttribute('aria-pressed', String(filterState.dispatched === true));
  };
  dispatchedChip?.addEventListener('click', onDispatchedFilter);

  // 可提 (Alan, 2026-09-26: "可提柜子那里所有有预约的过滤出来，能细到一天一天的过滤
  // 更好"). A view like 已派单: pressing it releases every other filter and narrows
  // to Available for Pickup with an appointment, ordered by the appointment; the
  // strip it opens narrows to one day; pressing the chip again gives the fleet back.
  function syncAppointmentChip() {
    appointmentChip?.setAttribute('aria-pressed', String(Boolean(filterState.appointment)));
    dashboardAppointmentSelection = filterState.appointment;
    renderDashboardAppointmentDays();
  }
  const onAppointmentFilter = () => {
    const next = filterState.appointment ? null : 'all';
    resetFiltersForChip();
    filterState.appointment = next;
    filterState.cargo = null;
    filterState.missing = [];
    state.adaptiveTableController?.setDateSort(next ? 'pickup_appointment_at' : null, { ascending: true });
    syncCargoChips();
    syncDispatchedChip();
    syncAppointmentChip();
    void applyQuery();
  };
  // Two clicks make a span, not a drag (Alan, 2026-09-28: "这些按钮改成按钮就是可以
  // 概括任何选择的日期不只是这样子一个个的日期"): the first click on a day is a fresh
  // single-day pick; a second click on a DIFFERENT day, while the selection is
  // still that one day, extends it to the closed range between them. A click once
  // a range is already showing starts over at that one day - see the header
  // comment in dashboard-appointment-filter.mjs for the full cycle. This is the
  // only place that ADVANCES a selection; appointmentSelectionBounds/-Of/-Label
  // only ever read one.
  const onAppointmentDay = (event) => {
    const day = event.target.closest?.('[data-appointment-day]')?.dataset.appointmentDay;
    if (!day || !filterState.appointment) return;
    const bounds = appointmentSelectionBounds(filterState.appointment);
    const next = day === 'all' ? 'all'
      : bounds && bounds.from === bounds.to && day !== bounds.from ? appointmentSelectionOf(bounds.from, day)
        : day;
    if (next === filterState.appointment) return;
    filterState.appointment = next;
    syncAppointmentChip();
    void applyQuery();
  };
  appointmentChip?.addEventListener('click', onAppointmentFilter);
  appointmentDays?.addEventListener('click', onAppointmentDay);

  // Pressing the pressed chip clears it. These are worklists rather than modes:
  // you open No ETA, fill a few in, and want the whole fleet back - and the
  // chip you pressed is the nearest thing to hand.
  // These three are the exception to one-chip-at-a-time (Alan, 2026-08-13: "这三个
   // 是例外可以多选"). They are three faces of one question - what is still
  // missing - so they accumulate among themselves and are UNIONED: a Container
  // short of any selected field shows. All three pressed is therefore the whole
  // worklist, not the handful missing all three at once.
  //
  // They still clear everything OUTSIDE their own group, exactly as the cargo
  // chips do, because each count is measured over the whole fleet.
  const onMissingFilter = (event) => {
    const kind = event.currentTarget.dataset.missingFilter;
    const next = filterState.missing.includes(kind)
      ? filterState.missing.filter((entry) => entry !== kind)
      : [...filterState.missing, kind];
    resetFiltersForChip();
    filterState.missing = next;
    filterState.cargo = null;
    syncCargoChips();
    void applyQuery();
  };
  for (const chip of missingChips) chip.addEventListener('click', onMissingFilter);

  // The main table split into 空柜 and 其他 (Alan, 2026-09-21: "在主表格页面可以分屏
  // ...一半空柜一半其他的"). The pairing split's two-table machinery as a way of
  // reading the fleet, with no flow behind it. Everything that can be decided
  // without a page lives in dashboard-browse-split.mjs; this is the wiring.
  //
  // Three rules, all here:
  //   - it is a preference, remembered per person, so it is how they open the
  //     Dashboard tomorrow too;
  //   - the flows win. 配空 and the three single-leg dispatches narrow and read the
  //     main table, so the split steps aside while one runs and comes back after -
  //     which is why every flow's entry and exit calls syncBrowseSplit;
  //   - the cargo chips are off while it is up. The server's cargo filter narrows
  //     to ONE kind, and a split needs both kinds in hand, so the split is the
  //     cargo view and the chips would only ever empty one of its panes. The chip
  //     that was pressed comes back when the split is turned off.
  const browseSplitButton = document.getElementById('dashboardBrowseSplitButton');
  // No button, no split: a customer who turned it on before 2026-10-12 would otherwise be stuck in it.
  let browseSplitPreferred = browseSplitButton ? preferences.browseSplit === true : false;
  // What the cargo chips were set to before the split took them over. undefined
  // means "not taken", as distinct from a remembered null (All).
  let cargoBeforeBrowse;

  // The right pane's own question (Alan, 2026-09-26: "这个空柜的页面是和另外的页面是
  // 分开独立的就是不要让别的页面的筛选影响到空柜部分"), asked from a toolbar of its own
  // since 2026-09-27 ("分屏后是完全的两套一样的操作系统"): 已派单, the missing-field
  // chips, a date range, search and Filters. The toolbar above never reads it, nor
  // does a chip there clear it.
  const emptiesFilter = blankPaneFilter();
  const emptiesPaneQuery = (filter = emptiesFilter) => ({
    ...dashboardQueryValue({
      search: filter.search,
      dateField: filter.dateField,
      from: filter.from,
      to: filter.to,
      ssl: filter.ssl,
      terminal_name: filter.terminal_name,
      dispatched: filter.dispatched === true,
      missing: filter.missing,
    }),
    statuses: [...BROWSE_SPLIT_EMPTY_STATUSES],
  });
  // Narrowing means asking the server something other than the bare question: a
  // date field chosen with no range, or half a range, narrows nothing.
  const sameQuery = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  const emptiesPaneNarrows = () => !sameQuery(
    emptiesPaneQuery(), emptiesPaneQuery(blankPaneFilter(emptiesFilter.dateField)),
  );
  const toolbarNarrows = () => !sameQuery(
    currentQuery(), dashboardQueryValue({ dateField: filterState.dateField }),
  );
  const describeEmptiesFilter = () => {
    if (!emptiesPaneNarrows()) return '';
    const zh = String(getLocale()).toLowerCase().startsWith('zh');
    const parts = [];
    const typed = String(emptiesFilter.search ?? '').trim();
    if (typed) parts.push(zh ? `搜索「${typed}」` : `search "${typed}"`);
    if (emptiesFilter.dispatched) parts.push(zh ? '已派单' : 'Dispatched');
    for (const kind of emptiesFilter.missing ?? []) {
      const chip = MISSING_FILTER_CHIPS.find(([known]) => known === kind);
      if (chip) parts.push(zh ? chip[2] : chip[1]);
    }
    if (emptiesFilter.ssl) parts.push(zh ? '船司' : 'shipping line');
    if (emptiesFilter.terminal_name) parts.push(zh ? '码头' : 'terminal');
    if (emptiesFilter.from && emptiesFilter.to) parts.push(zh ? '日期范围' : 'a date range');
    if (!parts.length) return '';
    return zh
      ? `没有空柜符合:${parts.join(' + ')}。清掉其中一个再试。`
      : `No empty matches ${parts.join(' + ')}. Clear one of them and try again.`;
  };
  // The question the right pane's source last asked, while it has one.
  let emptiesSourceKey = null;
  /**
   * Gives the right pane a question of its own the first time either side narrows.
   *
   * Until then it reads the Dashboard's rows, which hold every empty, so the split
   * asks the server for nothing extra in the common case. From the first narrowing
   * it keeps its own answer for as long as the split is up: handing the pane back
   * to the Dashboard's rows when the toolbar clears would put it on rows that are
   * being re-read at that moment, and blank it. It starts from what the pane is
   * showing, so asking does not blank it either.
   */
  const ensureEmptiesSource = () => {
    if (!browseSplit.isOpen()) return;
    if (!emptiesSource.isActive() && !toolbarNarrows() && !emptiesPaneNarrows()) return;
    // Not `query`: that is Firestore's, imported at the top of this file.
    const question = emptiesPaneQuery();
    const key = JSON.stringify(question);
    if (emptiesSource.isActive() && key === emptiesSourceKey) return;
    const seed = emptiesSource.isActive() ? null : browseSplit.paneRows('empty');
    emptiesSourceKey = key;
    void emptiesSource.load(question, { seed }).catch((error) => {
      console.warn('[browse split] the empties could not be loaded', error);
    });
  };
  // What every empty counts to on the chips, as last seen whole: the right pane's
  // chips show it, and the left pane's subtract it (see dashboardSplitCountExclusion).
  // Null until the empties have been seen whole in this split.
  let emptiesCounts = null;
  const emptiesSeenWhole = () => !emptiesPaneNarrows() && (emptiesSource.isActive()
    ? emptiesSource.snapshot().loading !== true
    : !toolbarNarrows() && controller.snapshot().loading !== true
      && controller.snapshot().hasMore !== true);
  let emptiesToolbar = null;
  const onBrowseSplitRefreshed = ({ empty }) => {
    if (emptiesSeenWhole()) {
      emptiesCounts = paneChipCounts(empty);
      dashboardSplitCountExclusion = emptiesCounts;
      updateDashboardCargoCounts(state.dashboardSummary);
    }
    emptiesToolbar?.paint();
  };
  const releaseEmptiesSource = () => {
    emptiesSource.clear();
    emptiesSourceKey = null;
    emptiesCounts = null;
    dashboardSplitCountExclusion = null;
    updateDashboardCargoCounts(state.dashboardSummary);
  };
  /**
   * The Dashboard's own toolbar, carried into the left pane while the split is up
   * (Alan, 2026-09-27: "分屏后是完全的两套一样的操作系统 ... 左边是一个可以自由筛选的
   * 池"). The controls themselves move - the same elements, still bound - so the left
   * pane runs on exactly the code the single table does, status filter and 可提 days
   * included. What stays above is the page's own: 配空 and the leg buttons, 分屏 and
   * the lock. A marker holds each one's place for the way back.
   */
  const adoptToolbarIntoPane = (toolbar) => {
    const moved = [
      root.querySelector('[data-work-pool-bar]'),
      from?.closest('label'),
      to?.closest('label'),
      search?.closest('label'),
      copyButton,
      filterButton,
    ].filter((element) => element?.parentNode);
    const returns = moved.map((element) => {
      const marker = document.createComment('split-toolbar');
      element.parentNode.insertBefore(marker, element);
      toolbar.appendChild(element);
      return () => {
        if (marker.parentNode) marker.parentNode.replaceChild(element, marker);
        else element.remove();
      };
    });
    return () => { for (const back of returns) back(); };
  };
  // Its twin over the right pane: the same controls less the four that would empty
  // a pane of reported empties (全部 / 重柜 / 空柜 / 可提). See dashboard-pane-toolbar.mjs.
  const mountEmptiesToolbar = (toolbar, pane) => {
    emptiesToolbar = mountPaneToolbar({
      toolbar,
      filter: emptiesFilter,
      onFilterChange: () => ensureEmptiesSource(),
      counts: () => emptiesCounts,
      table: () => pane,
      // The Filters choices come from every empty in hand, not only the ones this
      // pane's filter already let through - or a shipping line, once chosen, would be
      // the only one left to choose.
      optionRows: () => [
        ...browseSplit.paneRows('empty'),
        ...partitionRowsForBrowseSplit(controller.snapshot().rows).empty,
      ],
      overlayManager,
      columns,
      role: state.role,
      locale: () => getLocale(),
      clipboard,
      save: (payload) => (state.dashboardAutosaveController
        ? state.dashboardAutosaveController.save(payload)
        : Promise.reject(new Error('SAVE_UNAVAILABLE'))),
      iconFor: topbarIcon,
    });
    return () => {
      emptiesToolbar?.destroy();
      emptiesToolbar = null;
    };
  };
  const bindPaneToolbar = ({ key, toolbar, pane }) => {
    if (!toolbar) return undefined;
    return key === 'others' ? adoptToolbarIntoPane(toolbar) : mountEmptiesToolbar(toolbar, pane);
  };
  const browseSplit = createBrowseSplit({
    documentRef: document,
    workspaceHost: () => document.getElementById('dashboardContainerWorkspace'),
    // Its own userKey per pane: column pins are stored per key, and sharing the
    // pairing split's would move a column there by pinning one here.
    mountPane: ({ host, rows, paneKey }) => mountContainerPane({ host, rows, paneKey: `browse-${paneKey}` }),
    getRows: () => controller.snapshot().rows,
    getEmptyRows: () => (emptiesSource.isActive() ? emptiesSource.snapshot().rows : null),
    adoptEmpties: () => !emptiesPaneNarrows(),
    isLoading: (key) => (key === 'empty' && emptiesSource.isActive()
      ? emptiesSource.snapshot().loading === true
      : controller.snapshot().loading === true || controller.snapshot().hasMore === true),
    isUnlocked: dashboardIsUnlocked,
    // Each pane names its own filters: the toolbar's for the left, the pane's own
    // for the right - and, with none on, the honest reason it can be empty.
    emptyReasonFor: (key) => {
      if (key !== 'empty') return describeActiveFilters();
      return describeEmptiesFilter()
        || (String(getLocale()).toLowerCase().startsWith('zh') ? '没有已报空的柜子。' : 'No Container is reported empty.');
    },
    onPaneTools: bindPaneToolbar,
    onRefreshed: onBrowseSplitRefreshed,
    // A pane mounts after the summary has usually arrived, so the dispatch mark
    // is handed to it here rather than waiting for the next summary.
    onPaneMounted: (_, pane) => pane.setActiveDispatchIds?.(state.dashboardSummary?.activeDispatchContainerIds ?? null),
    locale: () => getLocale(),
  });
  state.browseSplit = browseSplit;
  const paintBrowseSplitButton = () => {
    browseSplitButton?.setAttribute('aria-pressed', String(browseSplitPreferred));
    // Disabled, not hidden: a chip that vanishes reads as a feature that went
    // missing, and one that is greyed reads as "not now". Only Empty since the
    // panes were separated (2026-09-26): its rows are the right pane's own, so
    // under it the left pane would be empty. All and Loaded narrow the left pane
    // like any other toolbar filter.
    for (const chip of cargoChips) chip.disabled = browseSplit.isOpen() && chip.dataset.cargoFilter === 'empty';
  };
  const syncBrowseSplit = async () => {
    const wanted = browseSplitShouldBeOpen({ preferred: browseSplitPreferred });
    if (wanted && !browseSplit.isOpen()) {
      if (filterState.cargo === 'empty') {
        if (cargoBeforeBrowse === undefined) cargoBeforeBrowse = filterState.cargo;
        filterState.cargo = null;
        syncCargoChips();
        // Awaited, as the pairing does: the cargo change re-queries the server,
        // and partitioning the rows loaded BEFORE it answered would split the
        // wrong fleet.
        await applyQuery();
      }
      browseSplit.open();
      ensureEmptiesSource();
      // The panes cannot page, so whatever is still to come is pulled now.
      if (controller.snapshot().hasMore) void loadRemainingDashboardPages(controller).catch(() => {});
    } else if (!wanted && browseSplit.isOpen()) {
      browseSplit.close();
      releaseEmptiesSource();
    } else if (browseSplit.isOpen()) {
      browseSplit.refresh();
    }
    // The chip comes back only when the person turned the split OFF.
    if (!browseSplitPreferred && cargoBeforeBrowse !== undefined) {
      const restored = cargoBeforeBrowse;
      cargoBeforeBrowse = undefined;
      if (restored !== filterState.cargo) {
        filterState.cargo = restored;
        syncCargoChips();
        await applyQuery();
      }
    }
    paintBrowseSplitButton();
  };
  const onBrowseSplitToggle = () => {
    browseSplitPreferred = !browseSplitPreferred;
    preferenceStore.save({ browseSplit: browseSplitPreferred });
    // Pressing 分屏 always opens on every empty (Alan, 2026-09-27: "不管什么时候
    // 点击分屏永远有部分屏幕直接显示所有的空柜子"): whatever the right pane was
    // narrowed to last time is let go. A flow that closes the split and brings it
    // back is not a press, so its filters survive that.
    if (browseSplitPreferred) Object.assign(emptiesFilter, blankPaneFilter());
    // Caught, not voided: a split that cannot be drawn has to say so, or the
    // button just looks dead (the pairing split shipped exactly that once).
    syncBrowseSplit().catch((error) => {
      console.error('[browse split] could not be drawn', error);
      browseSplitPreferred = false;
      preferenceStore.save({ browseSplit: false });
      paintBrowseSplitButton();
      toast(`分屏打不开：${error?.message || error}`);
    });
  };
  browseSplitButton?.addEventListener('click', onBrowseSplitToggle);

  syncCargoChips();

  // The way they left it. The first page is in hand by now (the binder runs after
  // the mount); later pages reach the panes through onChange.
  if (browseSplitPreferred) void syncBrowseSplit().catch((error) => console.error('[browse split]', error));
  return () => {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    if (copyResetTimer !== null) window.clearTimeout(copyResetTimer);
    bulkCopyController?.cancel();
    search?.removeEventListener('input', onSearch);
    from?.removeEventListener('change', onRange);
    to?.removeEventListener('change', onRange);
    copyMenuController?.destroy();
    for (const chip of cargoChips) chip.removeEventListener('click', onCargoFilter);
    dispatchedChip?.removeEventListener('click', onDispatchedFilter);
    appointmentChip?.removeEventListener('click', onAppointmentFilter);
    appointmentDays?.removeEventListener('click', onAppointmentDay);
    dashboardAppointmentSelection = null;
    dashboardSplitCountExclusion = null;
    stopWorkPoolSubscription();
    browseSplitButton?.removeEventListener('click', onBrowseSplitToggle);
    browseSplit.destroy();
    if (state.browseSplit === browseSplit) state.browseSplit = null;
    filterController?.destroy();
    overlayManager.destroy();
  };
}

function hasTrustworthyDashboardInsights(containers) {
  const dated = containers.filter((container) => container.created_at instanceof Date
    && Number.isFinite(container.created_at.getTime()));
  return dated.length >= 2
    && new Set(dated.map((container) => `${container.created_at.getFullYear()}-${container.created_at.getMonth()}`)).size >= 2;
}

async function renderDashboard() {
  const columns = registryColumnsForRole(state.role);
  const authorizedDateFields = registryDateFieldsForRole(state.role);
  const preferenceStore = createDashboardPreferenceStore({
    storage: localStorage,
    userKey: `${state.user.uid}:${state.role}`,
  });
  const preferences = preferenceStore.load({ authorizedDateFields });
  // The KPI band is gone, and so is the call that fed it: a callable fired on
  // every Dashboard open, aggregated server-side, for a strip Alan asked to
  // remove on 2026-08-09.
  let mounted = false;
  const controller = createDashboardDataController({
    callList: listMyContainerDashboard,
    normalizeRow: normalizeRoleDashboardRow,
    onChange(snapshot) {
      state.containers = snapshot.rows;
      rememberContainerRows(snapshot.rows);
      if (snapshot.summary) {
        try {
          rememberDashboardSummary(normalizeRoleDashboardSummary(snapshot.summary));
        } catch (error) {
          console.warn('Dashboard summary rejected.', error);
        }
      }
      if (mounted) {
        state.adaptiveTableController?.setRows(sortDashboardContainers(state.containers));
        // The browse split partitions the same rows; a page landing or a cell
        // saved has to reach both panes, not only the table hidden behind them.
        state.browseSplit?.refresh();
        // The split's panes have no pager: only the single table pages as it is
        // scrolled, and it is hidden behind them. So while the split is up the
        // fleet is pulled whole, as the first load does - otherwise a refresh (a
        // release, a cancellation, a refused save) left the panes holding its
        // first page.
        if (state.browseSplit?.isOpen() && snapshot.hasMore && !snapshot.loading && !snapshot.error) {
          void loadNextDashboardPage();
        }
        updateDashboardMetricCards(state.dashboardSummary);
        updateDashboardCargoCounts(state.dashboardSummary);
        renderDashboardLoadStatus(controller, loadNextDashboardPage);
      }
    },
  });
  // The split's right pane, once it asks a question of its own (Alan, 2026-09-26:
  // "不要让别的页面的筛选影响到空柜部分"). Idle until then; see ensureEmptiesSource.
  const emptiesSource = createWholeQuerySource({
    callList: listMyContainerDashboard,
    normalizeRow: normalizeRoleDashboardRow,
    onChange(snapshot) {
      rememberContainerRows(snapshot.rows);
      if (mounted) state.browseSplit?.refresh();
    },
  });
  // Both sources, whenever the data may have moved under them: a release, a
  // cancellation, a refused save.
  const refreshDashboardSources = () => {
    if (emptiesSource.isActive()) void emptiesSource.refresh().catch(() => {});
    return controller.refresh();
  };
  // No loading overlay on paging (Alan, 2026-08-04). Infinite scroll reaches the
  // end of the table repeatedly, and an overlay that opens and closes on each
  // approach is the stutter itself - the rows are already on screen and the next
  // page simply appends beneath them. A failure leaves the rows that did load
  // and reports through the load-status strip rather than covering the table.
  const loadNextDashboardPage = async () => {
    try {
      await controller.loadNext();
    } catch (_) {
      // The controller's own snapshot carries the error; the strip reads it.
    }
  };
  /**
   * The whole fleet before the table draws.
   *
   * The approved Ocean Status order is a property of the fleet, and the client
   * is what applies it - so sorting one page of 25 sorts 25 rows out of 452 and
   * puts whichever documents happen to come first by id at the top, arranged in
   * status order among themselves. Alan reported the order simply not being
   * there, three times, and that is why: page one was never the first page of
   * the order, it was the first page of the database.
   *
   * Paging the rest in on scroll cannot fix it either - it would reshuffle the
   * rows under the cursor as each page landed.
   *
   * So the pages are pulled before the first paint. Five requests at 100 a page
   * for 452 containers, and afterwards scrolling touches the network not at all.
   * The boot ring covers the wait. If the fleet ever outgrows maxRows the
   * controller reports `hasMore` and the load-status strip keeps its old job.
   */
  try {
    await controller.reset(dashboardQueryValue({
      ...preferences,
      statuses: dashboardInsightStatuses(hashParams().get('insight')),
    })).then(() => loadRemainingDashboardPages(controller));
  } catch (error) {
    // Rethrown to renderRoute, which paints the real error page. Showing a
    // full-screen loader that turns into a retry button just puts a second
    // screen in front of the same message.
    throw error;
  }
  state.dashboardDataController = controller;
  const summary = rememberDashboardSummary(
    normalizeRoleDashboardSummary(controller.snapshot().summary),
  );
  const canViewRevenue = roleDashboardColumnPolicy(state.role).showFinance;
  const dashboardRows = sortDashboardContainers(state.containers);
  // The opening page, for the lookups below. Every later page and every requery
  // adds to the same index through onChange.
  rememberContainerRows(dashboardRows);
  const addressCatalogPromise = loadAddressCatalog(dashboardRows, columns);
  // The Dashboard is the table and nothing else (Alan, 2026-08-09: "把 dashboard
  // 上面的 kpi 移除然后表格往上面移"). The KPI band above it and the charts below it
  // both went: the page no longer scrolls, so anything under the grid could not be
  // reached anyway, and the band was the last thing standing between the operator
  // and the first row.
  shell(`
    <div class="operations-dashboard-layout" data-dashboard-table-first="true">
      <div class="operations-dashboard-main">
        ${dashboardContainerTableHtml()}
      </div>
    </div>
  `);
  // They sat on the chrome bar beside the menu from 2026-08-09 ("把这个按键移动到
  // 最上方和 menu 同一条线上面") until 2026-08-13, when the row had grown to fifteen
  // controls and stopped fitting on a narrower screen ("有的电脑屏幕会显示不全。能
  // 不能把这些西东下来在和表格放在一起").
  //
  // Now they sit directly above the table, where they have the full page width
  // instead of whatever the logo, the nav, the task shelf and the profile menu
  // leave over. The topbar slot stays for Company Overview and the Chassis map,
  // which still put their own smaller controls there - which is why this targets
  // its own host rather than the shared one.
  const pageControls = document.querySelector('[data-table-controls]');
  if (pageControls) {
    pageControls.innerHTML = dashboardQueryControlsMarkup({
      iconFor: topbarIcon, role: state.role, locale: getLocale(),
    });
  }
  const dashboardWorkspace = document.getElementById('dashboardContainerWorkspace');
  // The newest copy wherever it is held. The empties pane's own source can hold a
  // Container the Dashboard's rows do not, or a newer copy of one they do, and a
  // save has to name the revision the server has now.
  const dashboardRowFor = (containerId) => freshestRow(
    containerId, controller.snapshot().rows, emptiesSource.snapshot().rows,
  );
  const autosaveController = createDashboardFieldAutosaveController({
    callUpdate: updateMyContainerDashboardField,
    getRow: dashboardRowFor,
    applyPatch: (containerId, patch) => {
      // Any accepted write makes the cached fleet stale, so the next page that
      // asks for it goes back to the server rather than redrawing yesterday.
      invalidateContainerCache();
      // Every copy moves together, onto the newest, so the two sources never tell
      // two stories about one Container.
      const current = dashboardRowFor(containerId);
      const next = current ? { ...current, ...patch } : patch;
      const patchedMain = controller.patchRow(containerId, next);
      const patchedEmpties = emptiesSource.patchRow(containerId, next);
      // Reported empty by hand while the right pane narrows on something of its
      // own: it lands there anyway rather than leaving the left pane for nowhere.
      const carried = !patchedEmpties && browseSplitIsEmpty(next) && emptiesSource.adoptRow(next);
      return patchedMain || patchedEmpties || carried;
    },
    onError(error) {
      if (dashboardAutosaveErrorCode(error) === 'REVISION_CONFLICT') {
        void refreshDashboardSources().catch(() => {});
      }
    },
  });
  state.dashboardAutosaveController = autosaveController;
  // Every field write from a table cell or the Container's page. Empty # is typed
  // again (2026-09-27), and a number typed from memory and mistyped matches no
  // Container - the reason it was a pointer from 2026-08-03 - so a number nobody
  // carries is saved, and said, rather than silently pairing with nothing.
  const saveDashboardField = async (request) => {
    const result = await autosaveController.save(request);
    const typed = request?.field === 'empty_container_no' ? String(request.value ?? '').trim() : '';
    if (typed && !result?.unchanged && !containerRowByNumber(typed)) {
      toast(`没有找到柜号 ${typed} 的柜子，已保存 — 请检查是否打错。 · No Container ${typed} was found; saved — check it for a typo.`);
    }
    return result;
  };
  const loadTaskModalEvidence = async (containerId, context) => {
    const assets = await loadRoleSafeContainerEvidenceAssets(containerId);
    const hasPod = assets.items.some((item) => item.kind === 'pod');
    if (!shouldUseDemoPodEvidence({
      containerId,
      podCount: context?.podCount,
      hasPod,
    })) return assets;
    return {
      ...assets,
      items: [
        ...assets.items,
        ...dashboardDemoPodEvidence({
          containerNo: context.containerNo,
          count: Number(context.podCount),
          assetBaseUrl: new URL('./assets', document.baseURI).href,
        }),
      ],
    };
  };
  // NOT awaited (Alan, 2026-08-11). loadAddressCatalog fans out one callable per
  // customer - about twenty round trips at 600-840ms each in his network tab -
  // and awaiting it here held the whole dashboard behind address-dropdown options
  // that most people never open. It is the single most expensive thing on a page
  // load.
  //
  // Both consumers now accept a getter as well as a value, and both read it late:
  // the table when an address cell renders or its editor opens, the modal when it
  // opens. So the fleet paints immediately and the catalog fills in behind it.
  // state.addressCatalog is set by loadAddressCatalog itself, including on
  // failure, so the getter is never left undefined for long and an address cell
  // that renders early simply has no custom-address chip until the next paint.
  const addressCatalog = () => state.addressCatalog;
  void addressCatalogPromise.catch(() => {});
  // On state so the pairing flow can open the laden container's page at its
  // confirm step - the flow lives in bindDashboardQueryControls, a different
  // scope, and this is the same bridge dashboardAutosaveController already uses.
  const taskDetailModal = state.containerTaskModal = createContainerTaskModal({
    documentRef: document,
    role: state.role,
    columns,
    addressCatalog,
    formatValue: (_key, row, column) => formatAdaptiveContainerCell(column, row),
    loadEvidence: loadTaskModalEvidence,
    onSaveField: (request) => saveDashboardField(request),
    // Which empties a load has already named. Computed over the whole fleet by
    // the callable, because the laden holding the claim is a different Container
    // and usually on another page - it cannot be worked out from the row the
    // modal was opened on.
    claimedEmptyContainerNos: state.dashboardSummary?.claimedEmptyContainerNos ?? null,
    // The paired empty, by number, from the rows already loaded - the whole fleet
    // is in hand at page limit 600 - so the load's Empty Return leg draws the
    // empty's own route without that route being copied onto the load.
    findEmptyByNumber: containerRowByNumber,
    openDeliveryOrder: openStoredDeliveryOrder,
    loadDriverOptions: loadEligibleDispatchDriverOptions,
    async loadDetail(containerId) {
      const [detailResult, relatedResult] = await Promise.allSettled([
        getMyContainerTaskDetail({ container_id: containerId }),
        getContainerInspectionDetail({ container_id: containerId }),
      ]);
      if (detailResult.status === 'rejected' && relatedResult.status === 'rejected') {
        throw detailResult.reason;
      }
      return {
        detail: detailResult.status === 'fulfilled' ? detailResult.value?.data || null : null,
        related: relatedResult.status === 'fulfilled' ? relatedResult.value?.data || null : null,
      };
    },
    async loadDriverProfile(driverId) {
      const [drivers, rows] = await Promise.all([
        loadDrivers(),
        loadContainers(),
      ]);
      const driver = drivers.find(({ id }) => id === driverId);
      if (!driver) throw new Error('Driver Profile is unavailable.');
      return driverProfileSnapshot({ driver, rows });
    },
    renderDriverProfile: (profile) => teamDriverProfileMarkup(
      profile,
      getLocale(),
      { canEdit: state.role === 'admin' },
    ),
    async onDriverUpdate({ driverId, values }) {
      const payload = buildDriverProfileUpdatePayload(values, driverId);
      const result = await updateBusinessDriverRecord(payload);
      if (result?.data?.updated !== true) throw new Error(t('team.mutationError'));
      toast('Driver profile updated.');
    },
    async onCancelDispatch({ containerId }) {
      // The task, not the Container. cancelDispatchAssignment is optimistic on the
      // TASK's status and updated_at (dispatch-cancellation.ts refuses without
      // both), and the dashboard row carries a task summary that has neither - so
      // it is read here, at the press, exactly as the old cancel form read it.
      const taskSnap = await getDocs(query(
        collection(db, 'dispatch_tasks'),
        where('container_ref', '==', doc(db, 'containers', containerId)),
        where('archived_by_restore', '==', false),
        limit(101),
      ));
      const active = taskSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .find(({ status }) => status !== 'cancelled' && status !== 'completed');
      if (!active) {
        toast('There is no active Dispatch Task on this Container to cancel.');
        return { cancelled: false };
      }
      const updatedAt = active.updated_at?.toDate?.();
      if (!(updatedAt instanceof Date) || !Number.isFinite(updatedAt.getTime())) {
        // The same anomaly the old form refuses on. Without a timestamp there is
        // nothing to check the cancellation against, so it is not attempted.
        throw new Error(t('dispatch.cancel.error.CANCELLATION_DATA_ANOMALY'));
      }
      const reason = await askCancellationReason(active);
      if (!reason) return { cancelled: false };
      const response = await cancelDispatchAssignment({
        taskId: active.id,
        cancelReasonCode: reason.code,
        cancelReasonExplanation: reason.explanation,
        expectedStatus: active.status,
        expectedUpdatedAt: updatedAt.toISOString(),
        requestId: createCancellationRequestId(crypto),
      });
      if (response?.data?.status !== 'cancelled') throw new Error('Unconfirmed cancellation result');
      toast('Dispatch cancelled. The Container is back with Dispatch.');
      // Same as the release, in reverse: the row carries it so the table and
      // Active Containers move without a reload.
      const row = containerRowById(containerId);
      if (row) {
        row.dispatch_released = false;
        row.dispatch_released_at = null;
        row.dispatch_released_legs = [];
      }
      // Re-fetch through the data controller, which IS in this scope. This line
      // was `applyQuery()`, a function that lives inside bindDashboardQueryControls
      // and does not exist here - so the cancellation succeeded on the server and
      // then threw a ReferenceError on the way out, which the modal's catch turned
      // into an error toast over a task that had in fact been cancelled (Alan,
      // 2026-08-16: 取消release doesn't work). The chips read the fresh fleet
      // totals from the same fetch.
      void refreshDashboardSources();
      return { cancelled: true };
    },
    async onReleaseDispatch({ containerId }) {
      const response = await releaseContainerDispatch({
        containerId,
        requestId: crypto.randomUUID(),
      });
      // A press sends the ready legs and holds the rest (2026-08-12), so the
      // toast says which happened - "released" over a half-sent trip reads as
      // everything went.
      const held = Array.isArray(response?.data?.held) ? response.data.held : [];
      toast(held.length
        ? `Released. Held: ${held.map(({ label }) => label).join(', ')} — fill in the missing info, then release again.`
        : 'Released to drivers.');
      // The row carries the release too, so Active Containers and the table both
      // move without a reload.
      const row = containerRowById(containerId);
      if (row) {
        row.dispatch_released = true;
        row.dispatch_released_at = Number.isSafeInteger(response?.data?.releasedAtMillis)
          ? new Date(response.data.releasedAtMillis) : new Date();
        if (Array.isArray(response?.data?.allLegs)) {
          row.dispatch_released_legs = response.data.allLegs;
        }
      }
      // The Loaded and Empty chips count over the WHOLE fleet on the server - a
      // released load leaves the Laden pool - so the numbers only move when the
      // summary is fetched again (Alan, 2026-08-16: 点击过release driver 那么对应
      // 的这个loaded，empty 的上面的数字会减法). The pairing release already
      // re-queries through endPairing; a release from the Container's own tab
      // patched the row and left the chips reading the old totals.
      void refreshDashboardSources();
      return response?.data ?? {};
    },
    // The no-evidence-yet path onto canonical completion (Alan, confirmed after
    // being told the exact consequences): Dispatch/Admin may close a Container out
    // here without a driver having submitted Empty Return evidence first. The
    // button that calls this lives beside the Empty Return fields and is only
    // drawn when the modal was told there is no pending review action - see
    // pendingEmptyReturnReview below.
    async onManuallyCompleteEmptyReturn({ containerId }) {
      const response = await manuallyCompleteEmptyReturn({
        container_id: containerId,
        request_id: crypto.randomUUID(),
        expected_revision: containerRowById(containerId)?.information_revision ?? 0,
      });
      toast('Empty Return marked complete.');
      const row = containerRowById(containerId);
      if (row) {
        row.status = 'empty_returned';
        row.is_empty_returned = true;
        row.locked = true;
        row.current_action_type = 'finance_completion';
      }
      // The Loaded/Empty/Completed chips and the work queue all count over the
      // whole fleet on the server, the same reason every other in-modal action
      // here re-fetches rather than trusting the patched row alone.
      void refreshDashboardSources();
      return response?.data ?? {};
    },
    mountPodEditor: (options) => mountDashboardPodEditor({
      ...options,
      workspace: false,
    }),
  });
  // A driver's progress lands in dispatch_tasks, the signal fires, and the table
  // reloads itself. Without this the dispatcher watched a stale row until they
  // reloaded the page.
  const stopDashboardLiveRefresh = state.liveRefresh?.subscribe(() => {
    if (!mounted) return;
    void refreshDashboardSources();
    // The open Task follows the same driver. It re-reads its own detail rather
    // than waiting for the table's row to arrive, because the two load through
    // different callables and the modal shows fields the table does not carry.
    void taskDetailModal?.refreshOpenTask?.();
  }) ?? (() => {});

  // Everything a Container table needs that is the same wherever it is drawn.
  // The pairing split mounts two more of these beside the Dashboard's own
  // (Alan, 2026-08-16), and a second copy of this list would drift from this one
  // - a cell that saves in the main table and silently does not in a pane is
  // exactly the kind of difference nobody notices until it costs a dispatch.
  const containerTableOptions = {
    copiedMarksStorage: (() => { try { return window.localStorage; } catch (_) { return null; } })(),
    copiedMarksKey: `ay-tms-copied-marks:${state.user.uid}`,
    role: state.role,
    addressCatalog,
    formatCell: (column, row) => formatAdaptiveContainerCell(column, row, { compact: true }),
    renderActionCell: renderAdaptiveContainerActions,
    navigate: routeTo,
    onSaveField: (request) => saveDashboardField(request),
    // "选" on a Container's Empty # while the split is up: the empties are on the right,
    // so the right pane is pointed at and whichever one is clicked there is written into
    // the cell that asked (Alan, 2026-09-30). Not for a cell that is itself on the right.
    onBeginEmptyPick: (sourceRow) => {
      const emptyPane = state.browseSplit?.isOpen?.() ? state.browseSplit.paneTable('empty') : null;
      if (!emptyPane?.beginExternalEmptyPick) return false;
      if (emptyPane.rowsInDisplayOrder?.().some((row) => row?.id === sourceRow?.id)) return false;
      return emptyPane.beginExternalEmptyPick({
        onPick: (row) => {
          void saveDashboardField({
            containerId: sourceRow.id,
            field: 'empty_container_no',
            value: String(row?.container_no ?? '').trim().toUpperCase(),
          });
        },
      });
    },
    loadDriverOptions: loadEligibleDispatchDriverOptions,
    // Uploading a POD is gated on the role, not on the table being in Edit Mode.
    // Hiding it behind Edit Mode left the system with no reachable upload entry
    // at all (Alan, 2026-08-01), so every route into the modal now offers it to
    // whoever Storage would actually accept an upload from.
    onOpenPod: (row, anchor) => taskDetailModal.open({
      row,
      initialSection: 'pod',
      podEditable: canEditDashboardPod(state.role),
      // Set the moment a driver's Empty Return evidence is submitted and cleared
      // the moment reviewEmptyReturnEvidence decides it either way - the row
      // already carries it (dashboard-query.ts's next_action) so no extra fetch is
      // needed to know whether the manual-completion button should be offered.
      pendingEmptyReturnReview: row?.current_action_type === 'dispatch_empty_return_review',
      anchor,
    }),
    onOpenPodEditor: (row, anchor) => taskDetailModal.open({
      row,
      initialSection: 'pod',
      podEditable: canEditDashboardPod(state.role),
      pendingEmptyReturnReview: row?.current_action_type === 'dispatch_empty_return_review',
      anchor,
    }),
    onInspectRow: (row, anchor) => taskDetailModal.open({
      row,
      initialSection: 'overview',
      podEditable: canEditDashboardPod(state.role),
      pendingEmptyReturnReview: row?.current_action_type === 'dispatch_empty_return_review',
      anchor,
    }),
    onCopyFeedback: () => toast('Copied.'),
    onEditorError(error) {
      const code = dashboardAutosaveErrorCode(error);
      // Say WHICH rule refused (Alan, 2026-08-17: "如果有的时候会出现这种情况那就请
      // 配上合理的信息为什么不可以改"). Four server refusals shared PERMISSION_DENIED,
      // so a locked Container, a billed one, a closed customer window and a stale
      // session all told him his role could not edit a field his role could edit.
      // Each has its own code now; this table is the other half.
      const messages = {
        REVISION_CONFLICT: 'This Container changed. The latest value has been reloaded.',
        PERMISSION_DENIED: 'Your role cannot edit this field.',
        CONTAINER_COMPLETED: '柜子已完成 — the empty is back and this Container is closed to edits.',
        CONTAINER_BILLED: '已开票 — CTF/TMF is settled, so this Container is closed to edits.',
        CUSTOMER_WINDOW_CLOSED: 'The ETA is entered, so the customer can no longer change this.',
        SESSION_NOT_VERIFIED: '登录状态需要刷新 — your session could not be verified. Sign out and back in, then try again.',
        SIGN_IN_REQUIRED: '请重新登录 — you are signed out.',
        FIELD_INVALID: 'This field cannot be edited.',
        VALUE_INVALID: 'Enter a valid value.',
        NO_CHANGE: 'No change was needed.',
        CUSTOMER_NOT_FOUND: '没有这个客户 — no customer by that name. Type an existing customer’s name, short name or alias.',
        CUSTOMER_AMBIGUOUS: '这个名字对应了不止一个客户 — that name matches more than one customer. Use the full name.',
        CUSTOMER_UNAVAILABLE: '这个客户不能接收柜子 — that customer is inactive or has no login account.',
        CUSTOMER_SHIPMENT_SHARED: '这个提单下还有别的柜子 — its shipment holds other Containers, so moving this one alone would split it. Not changed.',
      };
      toast(messages[code] || error?.message || 'The field could not be saved.');
    },
  };

  state.adaptiveTableController = mountAdaptiveContainerTable({
    ...containerTableOptions,
    host: dashboardWorkspace,
    rows: dashboardRows,
    userKey: `${state.user.uid}:${state.role}:dashboard`,
    // Only the Dashboard's own table pages. The split's panes are handed a
    // partition of rows that are already loaded, so a near-end signal from one of
    // them would ask the server for a page nobody is showing.
    onNearEnd: () => { void loadNextDashboardPage(); },
  });

  // How the pairing split gets its two tables. It lives in a different scope
  // (bindDashboardQueryControls), so the factory is handed over rather than the
  // options - that way the panes cannot be mounted with a different set by
  // accident, and there is still exactly one place that says what a Container
  // table is.
  //
  // Each pane needs its OWN userKey: column pins are persisted per key, so two
  // tables sharing one would fight over the same stored list and pinning a column
  // in the empty pane would move a column in the laden one.
  const mountContainerPane = ({
    host, rows, paneKey, columnKeys, onAfterSave = () => {},
  }) => mountAdaptiveContainerTable({
    ...containerTableOptions,
    host,
    rows,
    columnKeys,
    // The pairing is recomputed from the two live rows after every cell save, so
    // correcting the empty's Return Driver in one pane moves the load's Pickup
    // Driver in the other immediately. The save itself is unchanged - same
    // callable, same optimistic patch - this only tells the split to look again.
    async onSaveField(request) {
      const result = await containerTableOptions.onSaveField(request);
      onAfterSave(request);
      return result;
    },
    // Half a screen measures as `mobile`, which draws a card list carrying none
    // of the pairing fields. The pane is narrow on purpose and a table on
    // purpose, and only this says so.
    forceTier: 'standard',
    userKey: `${state.user.uid}:${state.role}:pair-${paneKey}`,
  });
  mounted = true;
  const mountedRows = sortDashboardContainers(controller.snapshot().rows);
  if (mountedRows.length !== dashboardRows.length) {
    state.adaptiveTableController.setRows(mountedRows);
  }
  dashboardWorkspace.dataset.reviewId = 'dashboard.container-table';
  const pendingWorkQueueTarget = sessionStorage.getItem('ay-tms-work-queue-target');
  if (pendingWorkQueueTarget && state.adaptiveTableController?.locate(pendingWorkQueueTarget)) {
    sessionStorage.removeItem('ay-tms-work-queue-target');
  }
  attachDashboardChartTooltips(root);
  // The Work Queue is gone and so is its polling (Alan, 2026-08-11: "the queue
  // task is the previous work, I deleted it… I don't need it now").
  //
  // The rail was removed on 2026-08-07 but only its UI: this still mounted the
  // live controller against a throwaway div, so every dashboard kept calling
  // listMyWorkQueue every ten seconds for a panel nobody could see. That callable
  // has since started answering 500, so the console was carrying a failing round
  // trip on a loop, forever, for a deleted feature - visible in Alan's network
  // tab as a wall of red.
  //
  // The inert controller keeps the contract bindDashboardQueryControls expects
  // (actionRequiredContainerIds / subscribe / destroy) and makes no requests.
  // mountTodayWorkQueue is left in place rather than deleted: the Work Pool still
  // wants this data eventually, and a dead poller is cheaper to re-enable than to
  // rewrite.
  const workQueue = inertWorkQueueController();
  const stopQueryControls = bindDashboardQueryControls({
    controller,
    emptiesSource,
    preferenceStore,
    preferences,
    columns,
    workQueue,
    mountContainerPane,
  });
  // The chips are painted by the shell above, after the first summary already
  // arrived, so the first fill happens here rather than in the change handler.
  updateDashboardCargoCounts(summary);
  renderDashboardLoadStatus(controller);
  state.pageCleanup = () => {
    mounted = false;
    stopDashboardLiveRefresh();
    stopQueryControls();
    controller.destroy();
    emptiesSource.clear();
    autosaveController.destroy();
    if (state.dashboardAutosaveController === autosaveController) {
      state.dashboardAutosaveController = null;
    }
    if (state.dashboardDataController === controller) state.dashboardDataController = null;
    workQueue.destroy();
    taskDetailModal.destroy();
    if (state.containerTaskModal === taskDetailModal) state.containerTaskModal = null;
  };
}

function createCustomerPortalSource() {
  return createCustomerDashboardSource({
    uid: state.user.uid,
    db,
    callList: listCustomerContainerViews,
    firestore: { doc, getDoc, onSnapshot },
    loadEvidence: loadCustomerEvidenceAssets,
  });
}

async function renderCustomerPortal() {
  shell('<div id="customer-dashboard-root"></div>');
  const controller = mountCustomerDashboard({
    container: document.getElementById('customer-dashboard-root'),
    source: createCustomerPortalSource(),
    user: { uid: state.user.uid, role: state.role },
    locale: getLocale(),
    navigate: routeTo,
  });
  state.customerDashboardController = controller;
  state.pageCleanup = () => {
    controller.destroy();
    if (state.customerDashboardController === controller) state.customerDashboardController = null;
  };
}

async function renderCustomerContainerDetail(id) {
  const queueView = isWorkQueueDetailView();
  shell(`${queueView ? workQueueDetailNavigationMarkup() : ''}<div id="customer-dashboard-root"></div>`);
  const detailOptions = {
    container: document.getElementById('customer-dashboard-root'),
    source: createCustomerPortalSource(),
    id,
    locale: getLocale(),
  };
  if (queueView) detailOptions.back = () => routeTo(workQueueHash());
  const controller = mountCustomerContainerDetail(detailOptions);
  state.customerDashboardController = controller;
  state.pageCleanup = () => {
    controller.destroy();
    if (state.customerDashboardController === controller) state.customerDashboardController = null;
  };
}

function containerTaskDetailErrorMessage(error) {
  const code = error instanceof ContainerTaskDetailUiError
    ? error.code
    : callableErrorCode(error);
  const messages = {
    FIELD_REQUIRED: 'Complete the highlighted field.',
    FIELD_INVALID: 'Enter a valid value.',
    FIELD_FORBIDDEN: 'This field is not editable by your role.',
    NOTE_REQUIRED: 'Enter a custom note of at least 3 characters.',
    NO_CHANGES: 'Change at least one value before saving.',
    DECISION_INVALID: 'Choose Approve or Reject.',
    PREFILL_CURRENT_ACTION_BYPASS: 'Open the current Required Action to complete these fields.',
    PREFILL_REVISION_MISMATCH: 'This task changed. Refresh and try again.',
    CHANGE_REQUEST_REVISION_MISMATCH: 'This request changed. Refresh and try again.',
  };
  return messages[code] || error?.message || 'The operation could not be completed.';
}

function showContainerTaskDetailError(host, error, form = null) {
  if (!host) return;
  host.textContent = containerTaskDetailErrorMessage(error);
  const fieldName = error instanceof ContainerTaskDetailUiError ? error.field : null;
  if (fieldName && form) {
    const field = form.elements?.[fieldName] || form.querySelector(`[name="${fieldName}"]`);
    if (field) {
      field.classList.add('is-invalid');
      field.setAttribute('aria-invalid', 'true');
      field.focus();
    }
  }
}

function enabledTaskDetailValues(form, baseline = {}) {
  return Object.fromEntries([...form.querySelectorAll('[data-task-field]:not(:disabled)')]
    .filter((field) => field.value.trim() !== ''
      || (baseline[field.name] !== null && baseline[field.name] !== undefined && baseline[field.name] !== ''))
    .map((field) => [field.name, field.value]));
}

function bindContainerTaskDetail(detail) {
  const rootEl = document.querySelector('[data-container-task-detail]');
  if (!rootEl) return;
  const containerId = detail.container_id;
  const revision = Number(detail.information_revision || 0);
  const baseline = detail.visible_fields || {};
  rootEl.querySelectorAll('[data-task-prefill-form]').forEach((prefillForm) => {
    const idleSubmitText = prefillForm.querySelector('[type="submit"]')?.textContent || 'Save Information';
    prefillForm.querySelector('[data-unlock-future]')?.addEventListener('click', (event) => {
      prefillForm.querySelectorAll('[data-task-field]:disabled').forEach((field) => {
        field.disabled = false;
      });
      event.currentTarget.remove();
      prefillForm.querySelector('[data-task-field]')?.focus();
    }, { once: true });
    let pending = false;
    prefillForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || !prefillForm.reportValidity()) return;
      const errorHost = prefillForm.querySelector('[data-prefill-error]');
      const submit = prefillForm.querySelector('[type="submit"]');
      errorHost.textContent = '';
      prefillForm.querySelectorAll('.is-invalid').forEach((field) => field.classList.remove('is-invalid'));
      try {
        const request = buildContainerPrefillRequest({
          target_role: prefillForm.dataset.targetRole,
          values: enabledTaskDetailValues(prefillForm, baseline),
          update_note: prefillForm.elements.update_note.value,
        }, {
          containerId,
          revision,
          requestId: crypto.randomUUID(),
          baseline,
        });
        pending = true;
        submit.disabled = true;
        submit.textContent = 'Saving...';
        await prefillContainerInformation(request);
        toast('Information saved and Audit Log recorded.');
        await renderSafeContainerTaskDetail(containerId);
      } catch (error) {
        showContainerTaskDetailError(errorHost, error, prefillForm);
        pending = false;
        submit.disabled = false;
        submit.textContent = idleSubmitText;
      }
    });
  });

  const changeForm = rootEl.querySelector('[data-customer-change-form]');
  if (changeForm) {
    const fieldSelect = changeForm.elements.field;
    const valueHost = changeForm.querySelector('[data-change-value-host]');
    const renderValue = () => {
      valueHost.innerHTML = customerChangeValueControl(fieldSelect.value, baseline[fieldSelect.value]);
    };
    fieldSelect.addEventListener('change', renderValue);
    renderValue();
    let pending = false;
    changeForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || !changeForm.reportValidity()) return;
      const errorHost = changeForm.querySelector('[data-change-error]');
      const submit = changeForm.querySelector('[type="submit"]');
      errorHost.textContent = '';
      try {
        const request = buildCustomerChangeRequest({
          field: fieldSelect.value,
          proposed_value: changeForm.elements.proposed_value?.value,
          reason: changeForm.elements.reason.value,
        }, { containerId, revision, requestId: crypto.randomUUID() });
        pending = true;
        submit.disabled = true;
        submit.textContent = 'Submitting...';
        await submitCustomerContainerChangeRequest(request);
        toast('Edit Request submitted for review.');
        await renderSafeContainerTaskDetail(containerId);
      } catch (error) {
        showContainerTaskDetailError(errorHost, error, changeForm);
        pending = false;
        submit.disabled = false;
        submit.textContent = 'Submit Edit Request';
      }
    });
  }

  rootEl.querySelectorAll('[data-change-request-id]').forEach((card) => {
    let pending = false;
    card.querySelectorAll('[data-change-decision]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (pending) return;
        const errorHost = card.querySelector('[data-decision-error]');
        errorHost.textContent = '';
        try {
          const request = buildChangeDecisionRequest({
            decision: button.dataset.changeDecision,
            decision_note: card.querySelector('[data-decision-note]')?.value,
          }, {
            changeRequestId: card.dataset.changeRequestId,
            requestRevision: Number(card.dataset.requestRevision),
            containerRevision: revision,
            requestId: crypto.randomUUID(),
          });
          pending = true;
          card.querySelectorAll('button').forEach((item) => { item.disabled = true; });
          await reviewCustomerContainerChangeRequest(request);
          toast(`Edit Request ${request.decision === 'approve' ? 'approved' : 'rejected'}.`);
          await renderSafeContainerTaskDetail(containerId);
        } catch (error) {
          showContainerTaskDetailError(errorHost, error, card);
          pending = false;
          card.querySelectorAll('button').forEach((item) => { item.disabled = false; });
        }
      });
    });
  });
}

async function renderSafeContainerTaskDetail(id) {
  const queueView = isWorkQueueDetailView();
  const navigation = queueView
    ? workQueueDetailNavigationMarkup()
    : '<nav class="queue-detail-navigation" aria-label="Task navigation"><button type="button" class="secondary" onclick="history.back()">Back</button></nav>';
  // This section is not decoration - it is the mount point that outerHTML
  // replaces once the detail arrives, so deleting it would blank the page. It is
  // emptied instead: no spinner, no sentence, nothing to flash between.
  shell(`${navigation}<section class="panel" id="safeContainerTaskDetail" aria-live="polite"></section>`);
  try {
    const [response, evidence] = await Promise.all([
      getMyContainerTaskDetail({ container_id: id }),
      loadRoleSafeContainerEvidenceAssets(id),
    ]);
    const detail = response?.data || {};
    const host = document.getElementById('safeContainerTaskDetail');
    host.outerHTML = renderContainerTaskDetailMarkup(detail, { role: state.role });
    document.querySelector('[data-container-task-detail]')?.insertAdjacentHTML('beforeend', `
      <section class="panel workflow-evidence-section">
        <h2>POD and Empty Return evidence</h2>
        ${evidenceMarkup(evidence.items)}
      </section>`);
    bindContainerTaskDetail(detail);
    state.pageCleanup = evidence.cleanup;
    applySystemInputGlow(root);
  } catch (error) {
    console.error('Role-safe Container Task detail unavailable.', error);
    const host = document.getElementById('safeContainerTaskDetail');
    // The failure is written into the mount point, where the operator is already
    // looking, rather than behind a second full-screen layer carrying the same
    // sentence and the same retry.
    if (host) {
      host.innerHTML = `<h1>Container Task unavailable</h1><p class="error">${escapeHtml(containerTaskDetailErrorMessage(error))}</p><button type="button" class="secondary" data-container-detail-retry>${escapeHtml(t('common.retry'))}</button>`;
      host.querySelector('[data-container-detail-retry]')?.addEventListener('click', () => {
        void renderSafeContainerTaskDetail(id);
      }, { once: true });
    }
  }
}

async function renderContainerDetail(id) {
  const queueView = isWorkQueueDetailView();
  if (queueView) return renderSafeContainerTaskDetail(id);
  if (isCustomer()) return renderCustomerContainerDetail(id);
  if (!['admin', 'dispatcher'].includes(state.role)) return renderSafeContainerTaskDetail(id);
  const canViewFinanceDetails = ['admin', 'finance', 'accounting'].includes(state.role);
  const snap = await getDoc(doc(db, 'containers', id));
  if (!snap.exists()) {
    shell('<h1>Container not found</h1>');
    return;
  }
  const container = { id: snap.id, ref: snap.ref, ...snap.data() };
  let shipment = null;
  let tasks = [];
  let events = [];

  try {
    if (container.shipment_ref) {
      const shipmentSnap = await getDoc(container.shipment_ref);
      shipment = shipmentSnap.exists() ? shipmentSnap.data() : null;
    }
  } catch (_) {
    shipment = null;
  }

  try {
    const taskSnap = await getDocs(query(
      collection(db, 'dispatch_tasks'),
      activeRestoreConstraint(),
      where('container_ref', '==', container.ref),
      orderBy('sequence', 'asc'),
    ));
    tasks = taskSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  } catch (_) {
    tasks = [];
  }

  try {
    const eventSnap = await getDocs(query(
      collection(db, 'containers', id, 'events'),
      orderBy('event_at', 'desc'),
    ));
    events = eventSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  } catch (_) {
    events = [];
  }

  shell(`
    ${queueView ? workQueueDetailNavigationMarkup() : ''}
    <h1>Container ${escapeHtml(container.container_no)}</h1>
    <div class="actions">
      ${canManageContainers() && !queueView ? `<button onclick="location.hash='#/edit-status/${id}'">Edit Status</button>` : ''}
      ${canManage() && !queueView && isDispatchAssignmentAllowed(container) ? `<button class="secondary" onclick="location.hash='#/assign-driver/${id}'">Assign Driver</button>` : ''}
      ${canManage() && !queueView && container.current_task_ref?.id ? `<button class="secondary" data-testid="edit-dispatch-task" data-task-id="${escapeHtml(container.current_task_ref.id)}" onclick="location.hash='#/edit-dispatch-task/${escapeHtml(container.current_task_ref.id)}'">${escapeHtml(t('dispatch.edit.open'))}</button>` : ''}
      ${queueView ? '' : '<button class="secondary" onclick="history.back()">Back</button>'}
    </div>
    <section class="panel">
      <h2>Container</h2>
      ${canManageContainers() && !queueView ? `<div class="quick-status-detail">${quickStatusButtons(container)}</div>` : ''}
      <p><b>Status:</b> <span class="status">${escapeHtml(statusLabel(container.status))}</span></p>
      <p><b>Stage:</b> ${escapeHtml(statusLabel(container.current_stage))}</p>
      <p><b>Last note:</b> ${escapeHtml(container.last_status_note)}</p>
      <p><b>Customer:</b> ${escapeHtml(container.customer_name)}</p>
      <p><b>Customer account:</b> ${escapeHtml(container.customer_email)}</p>
      <p><b>Customer link:</b> ${customerLinkBadge(container)}</p>
      <p><b>Terminal:</b> ${escapeHtml(container.terminal_name)}</p>
      <p><b>Job:</b> ${escapeHtml(container.job_no || container.shipment_name)}</p>
      <p><b>SSL:</b> ${escapeHtml(container.ssl)}</p>
      <p><b>LFD:</b> ${formatDate(container.lfd_at)}</p>
      <p><b>Due:</b> ${formatDate(container.due_at)}</p>
      <p><b>Completion rule:</b> Complete only after Dispatch review sets both canonical Empty Returned status and confirmation evidence.</p>
    </section>
    <section class="panel">
      <h2>Shipment</h2>
      ${shipment ? `
        <p><b>Master BL:</b> ${escapeHtml(shipment.master_bl_no)}</p>
        <p><b>House BL:</b> ${escapeHtml(shipment.house_bl_no)}</p>
        <p><b>Vessel:</b> ${escapeHtml(shipment.vessel_name)}</p>
        <p><b>Voyage:</b> ${escapeHtml(shipment.voyage_no)}</p>
      ` : '<p class="muted">Shipment details unavailable.</p>'}
    </section>
    ${canViewFinanceDetails ? `<section class="panel">
      <h2>Charges</h2>
      <p>Pier Pass: ${cents(container.pier_pass_cents)}</p>
      <p>CTF: ${cents(container.ctf_cents)}</p>
      <p>Demurrage Fee: ${cents(container.demurrage_cents)}</p>
      <p>Exam Fee: ${cents(container.exam_fee_cents)}</p>
      <p>Service Fee: ${cents(container.service_fee_cents)}</p>
      <p>Per Diem: ${cents(container.per_diem_cents)}</p>
      <p>Other: ${cents(container.other_charge_cents)}</p>
      <p><b>Total:</b> ${cents(chargeTotal(container))}</p>
      <p><b>Charge remarks:</b> ${escapeHtml(container.charge_remarks)}</p>
    </section>` : ''}
    <section class="panel">
      <h2>Dispatch Tasks</h2>
      ${tasks.length ? tasks.map((task) => `
        <p>${task.sequence}. ${escapeHtml(task.leg_type)} - ${escapeHtml(task.status)} - ${escapeHtml(task.driver_name)}</p>
      `).join('') : '<p class="muted">No visible tasks.</p>'}
    </section>
    <section class="panel">
      <h2>Status History</h2>
      ${events.length ? events.map((event) => `
        <p><b>${formatDate(event.event_at)}</b> ${escapeHtml(statusLabel(event.status))} ${escapeHtml(statusLabel(event.current_stage))}<br>${escapeHtml(event.note)}</p>
      `).join('') : '<p class="muted">No status events yet.</p>'}
    </section>
  `);
  attachQuickStatusHandlers();
}

function options(items, labelField = 'name') {
  return items.map((item) => `<option value="${item.id}">${escapeHtml(item[labelField] ?? item.id)}</option>`).join('');
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  const [year, month] = key.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString([], { month: 'short', year: 'numeric' });
}

function completedStatsContainers() {
  return state.containers.filter(isStatsComplete);
}

function statsMonthRows() {
  const groups = new Map();
  completedStatsContainers().forEach((container) => {
    const date = returnedDate(container);
    if (!date) return;
    const key = monthKey(date);
    if (!groups.has(key)) groups.set(key, { key, containers: [], money: 0 });
    const group = groups.get(key);
    group.containers.push(container);
    group.money += chargeTotal(container);
  });
  return [...groups.values()]
    .map((group) => ({ ...group, count: group.containers.length }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function statsGraph(groups) {
  if (!groups.length) return '<div class="notice">No completed containers yet.</div>';
  const width = 720;
  const height = 220;
  const pad = 34;
  const maxValue = Math.max(...groups.map((group) => Math.max(group.count, group.money / 100)), 1);
  const points = groups.map((group, index) => {
    const x = groups.length === 1 ? width / 2 : pad + (index * (width - pad * 2)) / (groups.length - 1);
    const y = height - pad - (Math.max(group.count, group.money / 100) / maxValue) * (height - pad * 2);
    return { ...group, x, y };
  });
  return `
    <div class="stats-chart">
      <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Monthly completed containers and money gained">
        <polyline points="${points.map((point) => `${point.x},${point.y}`).join(' ')}" fill="none" stroke="#2367c8" stroke-width="3" />
        ${points.map((point) => `
          <g class="month-dot">
            <circle cx="${point.x}" cy="${point.y}" r="7"></circle>
            <text x="${point.x}" y="${height - 8}" text-anchor="middle">${escapeHtml(monthLabel(point.key))}</text>
            <text x="${point.x}" y="${point.y - 14}" text-anchor="middle">${point.count} / ${escapeHtml(cents(point.money))}</text>
          </g>
        `).join('')}
      </svg>
    </div>
  `;
}

/**
 * Company Overview, on the route Status used to hold (Alan, 2026-08-08). One card
 * per customer: how many containers they have with us, how many are finished -
 * meaning the empty is back - and what is late. Admin and Finance also get the
 * money, because they are the only roles the projection sends it to.
 *
 * It reads state.containers, the same fleet the Dashboard already loads, so there
 * is no second query and no second definition of what "complete" means.
 */
async function renderCompanyOverviewPage() {
  // The customer list as well as the containers: a customer with nothing moving
  // still gets a card (Alan, 2026-08-09).
  const [, customers] = await Promise.all([loadContainers(), loadCustomers()]);
  const model = companyOverviewModel({
    rows: state.containers, customers, role: state.role,
  });
  shell(companyOverviewMarkup({ model, locale: getLocale() }));
  // The five figures ride the chrome bar rather than a strip above the cards.
  const overviewControls = document.querySelector('[data-page-controls]');
  if (overviewControls) {
    overviewControls.innerHTML = companyOverviewBarMarkup({ model, locale: getLocale() });
  }
}

async function renderStatusAnalyticsPage() {
  await loadContainers();
  const model = statusPageModel({ rows: state.containers, role: state.role });
  // Weekly money lives here rather than on the Dashboard (Alan, 2026-08-05).
  // The Dashboard is where the day's work is run; this is where the business is
  // read. It needs no controller - the weeks are native disclosures.
  shell(`${statusOperationsMarkup({ model, locale: getLocale() })}${trendSectionMarkup({
    series: containerKpiMonthlySeries({ rows: state.containers, locale: getLocale() }),
    locale: getLocale(),
  })}${weeklyFinancialsMarkup({
    weeks: weeklyMoney({ rows: state.containers, locale: getLocale() }),
    locale: getLocale(),
  })}`);
  const statusTimelineController = mountStatusTimeline(root, { model, locale: getLocale() });
  registerActivePageCleanup(() => statusTimelineController.destroy());
}

// Chassis Locations. The snapshot callable answers with the last saved snapshot
// when Anytrek itself is unreachable (stale: true), so a failure here means the
// call did not return at all — which the page renders as an error with a retry,
// never as a fleet of zero.
async function loadChassisSnapshot() {
  const result = await getAnytrekChassisSnapshot({});
  const snapshot = result?.data;
  if (snapshot?.ok !== true || !Array.isArray(snapshot.assets)) {
    throw new Error('ANYTREK_INVALID_RESPONSE');
  }
  return snapshot;
}

// While something is actually moving, the map is a live view and re-reads
// itself; while the whole fleet is parked or offline it re-reads nothing at all
// (Alan, 2026-08-04). Leaving the page, or backgrounding the tab, stops it dead.
//
// Thirty seconds rather than five, because five buys nothing. Measured against
// the live account on 2026-08-04: the freshest report in all 107 devices was 78
// seconds old, no device had reported inside a minute, and the two chassis
// genuinely under way (89 and 91 km/h) had last reported 237 and 311 seconds
// earlier. The trackers speak in minutes, so polling in seconds fetches the same
// bytes over and over — six times the vendor calls for the same map. Thirty
// seconds is still comfortably inside the fastest cadence the fleet has ever
// shown, so no movement waits on the timer. If the trackers are ever configured
// to report faster, re-measure and lower this — do not lower it on a hunch.
const CHASSIS_LIVE_POLL_MS = 30_000;

async function renderChassisMapPage() {
  let snapshot = null;
  let error = null;
  try {
    snapshot = await loadChassisSnapshot();
  } catch (loadError) {
    error = loadError?.code || loadError?.message || 'ANYTREK_UNAVAILABLE';
  }

  let query = '';
  let statusFilter = 'all';
  let selectedDeviceId = '';
  const buildModel = () => chassisPageModel({
    snapshot, query, statusFilter, selectedDeviceId, error,
  });

  shell(chassisMapPageMarkup({ model: buildModel(), locale: getLocale() }));
  // The counts, the timestamp and Refresh live on the chrome bar, not in a hero
  // above the map (Alan, 2026-08-09). They are repainted from here on every model
  // change, because the page's own controller only owns what is inside the page.
  const paintChassisBar = () => {
    const slot = document.querySelector('[data-page-controls]');
    if (slot) slot.innerHTML = chassisMapBarMarkup({ model: buildModel(), locale: getLocale() });
  };
  paintChassisBar();

  // The rail repaints on its own so the search box keeps focus between
  // keystrokes and the Google Maps instance is never rebuilt mid-typing.
  let controller = null;
  let pollTimer = null;
  let disposed = false;
  const repaint = () => {
    controller?.update(buildModel());
    paintChassisBar();
  };

  const stopPolling = () => {
    if (pollTimer === null) return;
    window.clearTimeout(pollTimer);
    pollTimer = null;
  };

  const reload = async () => {
    try {
      snapshot = await loadChassisSnapshot();
      error = null;
    } catch (reloadError) {
      error = reloadError?.code || reloadError?.message || 'ANYTREK_UNAVAILABLE';
    }
    if (disposed) return;
    repaint();
  };

  // setTimeout rather than setInterval, chained after the response: a vendor
  // call slower than the interval would otherwise stack requests on itself.
  const schedulePoll = () => {
    stopPolling();
    if (disposed || document.hidden) return;
    if (!chassisHasLiveMovement(buildModel())) return;
    pollTimer = window.setTimeout(async () => {
      pollTimer = null;
      await reload();
      schedulePoll();
    }, CHASSIS_LIVE_POLL_MS);
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      stopPolling();
      return;
    }
    // Coming back to the tab is arriving at the page again: read once, then
    // resume only if something is moving.
    void reload().then(schedulePoll);
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  controller = mountChassisMapPage(root, {
    model: buildModel(),
    locale: getLocale(),
    onSelect: (deviceId) => { selectedDeviceId = deviceId; },
    onSearch: (value) => { query = value; repaint(); },
    onFilter: (value) => { statusFilter = value; repaint(); },
    onRefresh: async () => {
      await reload();
      schedulePoll();
    },
  });
  schedulePoll();

  // Refresh now sits on the chrome bar, outside the page the controller listens
  // on, so its click is handled here.
  const onBarRefresh = async (event) => {
    const button = event.target.closest?.('[data-page-controls] [data-chassis-refresh]');
    if (!button || button.disabled) return;
    button.disabled = true;
    const original = button.textContent;
    button.textContent = chassisCopy(getLocale()).refreshing;
    try {
      await reload();
      schedulePoll();
    } finally {
      if (!disposed && button.isConnected) {
        button.disabled = false;
        button.textContent = original;
      }
    }
  };
  document.addEventListener('click', onBarRefresh);

  registerActivePageCleanup(() => {
    disposed = true;
    stopPolling();
    document.removeEventListener('click', onBarRefresh);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    controller.destroy();
  });
}

// The office day an instant falls in. Null for anything unreadable or absent -
// "never released" must not accidentally equal "released today".
function officeDateKeyOf(value, timeZone = 'America/Los_Angeles') {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function officeTodayKey(timeZone = 'America/Los_Angeles') {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

async function loadAttendanceOverview() {
  const result = await attendanceGateway({ action: 'overview', payload: {} });
  if (result?.data?.ok !== true || !result.data.data) throw new Error(t('team.loadError'));
  return result.data.data;
}

async function loadTeamDirectoryUsers() {
  const snap = await getDocs(query(
    collection(db, 'users'),
    activeRestoreConstraint(),
    orderBy('display_name', 'asc'),
  ));
  return snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
}

function teamTodoCompletionRecords(data) {
  const source = Array.isArray(data?.totals)
    ? data.totals : Array.isArray(data?.items) ? data.items : [];
  const rankedRoles = new Set(['dispatcher', 'clerk', 'finance']);
  return source.flatMap((item) => {
    const worker = item?.worker && typeof item.worker === 'object' && !Array.isArray(item.worker)
      ? item.worker : {};
    const workerAccount = String(
      worker.account ?? item?.worker_account ?? item?.account ?? '',
    ).trim();
    const role = String(worker.role ?? item?.role ?? '').trim().toLowerCase();
    const count = [
      item?.completed_todo_total,
      item?.completedTodoTotal,
      item?.count,
      item?.total,
    ].find((value) => Number.isInteger(value));
    if (!workerAccount || !rankedRoles.has(role) || !Number.isInteger(count) || count < 0) {
      return [];
    }
    return [{ worker_account: workerAccount, role, count }];
  });
}

async function renderTeamAttendancePage() {
  const [overview, users, drivers, rows, todoTotals] = await Promise.all([
    loadAttendanceOverview().catch(() => ({
      office: { timezone: 'America/Los_Angeles' },
      employees: [],
      attendance: [],
    })),
    loadTeamDirectoryUsers(),
    loadDrivers(),
    loadContainers(),
    getTeamTodoTotals({}).catch((error) => {
      console.warn('Team Todo totals unavailable.', error);
      return null;
    }),
  ]);
  const timeZone = overview.office?.timezone || 'America/Los_Angeles';
  const todayKey = officeTodayKey(timeZone);
  shell(teamAttendancePageMarkup({
    overview,
    users,
    drivers,
    rows,
    operations: teamOperationalSnapshot({ rows, todayKey, timeZone }),
    todoCompletionRecords: teamTodoCompletionRecords(todoTotals?.data),
    role: state.role,
    todayKey,
  }));
  const controller = mountTeamAttendancePage(root, {
    onMutation: async (action, payload) => {
      const result = await attendanceGateway({ action, payload });
      if (result?.data?.ok !== true) throw new Error(t('team.mutationError'));
      toast(t('team.updated'));
    },
    onDriverUpdate: async (payload) => {
      const result = await updateBusinessDriverRecord(payload);
      if (result?.data?.updated !== true) throw new Error(t('team.mutationError'));
      toast('Driver profile updated.');
    },
    onReload: renderTeamAttendancePage,
  });
  registerActivePageCleanup(() => controller.destroy());
}

async function renderShipmentCreation() {
  await loadTerminals();
  await loadDrivers();
  shell(`
    <h1>Shipment Creation</h1>
    <form class="panel form-grid" id="shipmentForm">
      <label><span>Job No</span><input name="job_no" required></label>
      <label><span>Master BL</span><input name="master_bl_no" required></label>
      <label><span>House BL</span><input name="house_bl_no"></label>
      <label><span>SSL</span><input name="ssl" required></label>
      <label><span>Vessel</span><input name="vessel_name"></label>
      <label><span>Voyage</span><input name="voyage_no"></label>
      <label><span>Terminal</span><select name="terminal_id">${options(state.terminals)}</select></label>
      <label><span>Terminal Name</span><input name="terminal_name" required></label>
      <label><span>Driver</span><select name="driver_id">${driverSelectOptions()}</select></label>
      <label><span>ETA</span><input name="eta_at" type="datetime-local"></label>
      <label><span>LFD</span><input name="lfd_at" type="datetime-local" required></label>
      <label class="wide"><span>Remarks</span><textarea name="remarks"></textarea></label>
      <div class="actions wide"><button type="submit">Create Shipment</button></div>
    </form>
  `);
  document.getElementById('shipmentForm').addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.target).entries());
    const terminal = state.terminals.find((item) => item.id === form.terminal_id);
    // The document itself is assembled server-side. Everything this form used
    // to derive here -- lfd_date_key, the zeroed money columns, the fixed
    // opening counts -- now comes from the callable, which is the only place
    // that also stamps the tenant.
    const result = await createBusinessContainerRecord({
      request_id: crypto.randomUUID(),
      kind: 'shipment',
      payload: {
        job_no: form.job_no,
        master_bl_no: form.master_bl_no,
        house_bl_no: form.house_bl_no || '',
        ssl: form.ssl,
        vessel_name: form.vessel_name || '',
        voyage_no: form.voyage_no || '',
        terminal_id: terminal?.id || '',
        terminal_name: form.terminal_name || terminal?.name || '',
        driver_id: form.driver_id || '',
        eta_at: isoFromInput(form.eta_at),
        lfd_at: isoFromInput(form.lfd_at),
        remarks: form.remarks || '',
      },
    });
    toast('Shipment created.');
    routeTo(`#/add-container?shipment=${result.data.id}`);
  });
}

async function renderAddContainer() {
  await loadCustomers();
  const canCreateWithFinance = roleDashboardColumnPolicy(state.role).showFinance;
  shell(`
    <h1>Add Container</h1>
    <form class="panel form-grid" id="containerForm">
      <label><span>Shipment / Job</span><input name="job_no" required></label>
      <label><span>Container No</span><input name="container_no" required></label>
      <label><span>DC Container No</span><input name="dc_container_no"></label>
      <label><span>Master BL</span><input name="master_bl_no" required></label>
      <label><span>SSL</span><input name="ssl" required></label>
      <label><span>Terminal Name</span><input name="terminal_name" required></label>
      <label><span>Customer Account</span><select name="customer_id" required>${customerSelectOptions()}</select></label>
      <label><span>Size Type</span><input name="size_type" value="40HQ" required></label>
      <label><span>ETA</span><input name="eta_at" type="datetime-local"></label>
      <label><span>LFD</span><input name="lfd_at" type="datetime-local" required></label>
      <label><span>Due</span><input name="due_at" type="datetime-local"></label>
      <label><span>Pickup Appointment</span><input name="pickup_appointment_at" type="datetime-local"></label>
      <label><span>Delivery Appointment</span><input name="delivery_appointment_at" type="datetime-local"></label>
      ${canCreateWithFinance ? `
        <label><span>Pier Pass</span><input name="pier_pass_cents" type="number" step="0.01"></label>
        <label><span>CTF</span><input name="ctf_cents" type="number" step="0.01"></label>
        <label><span>Demurrage Fee</span><input name="demurrage_cents" type="number" step="0.01"></label>
        <label><span>Exam Fee</span><input name="exam_fee_cents" type="number" step="0.01"></label>
        <label><span>Service Fee</span><input name="service_fee_cents" type="number" step="0.01"></label>
        <label><span>Per Diem</span><input name="per_diem_cents" type="number" step="0.01"></label>
        <label class="wide"><span>Charge Remarks</span><textarea name="charge_remarks"></textarea></label>
      ` : ''}
      <label class="wide"><span>Remarks</span><textarea name="remarks"></textarea></label>
      <div class="actions wide"><button type="submit">Create Container</button></div>
    </form>
  `);
  document.getElementById('containerForm').addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.target).entries());
    const customer = findCustomerSelection(form.customer_id);
    if (!customer) {
      toast('Select a customer before creating container.');
      return;
    }
    const linkedCustomer = await resolveCustomerAccount(customer);
    if (!linkedCustomer?.accountUid) {
      toast('Selected customer is not linked to a login account yet. Add the customer account email or create the customer user first.');
      return;
    }
    // customer_name / customer_uid / customer_email and search_keywords are no
    // longer sent: the callable reads them off the customer document. A
    // container that names its own customer is a container that can be pointed
    // at someone else's dashboard.
    await createBusinessContainerRecord({
      request_id: crypto.randomUUID(),
      kind: 'container',
      payload: {
        customer_id: customer.id,
        job_no: form.job_no,
        master_bl_no: form.master_bl_no,
        ssl: form.ssl,
        terminal_name: form.terminal_name,
        container_no: form.container_no,
        dc_container_no: form.dc_container_no || '',
        size_type: form.size_type,
        eta_at: isoFromInput(form.eta_at),
        lfd_at: isoFromInput(form.lfd_at),
        due_at: isoFromInput(form.due_at),
        pickup_appointment_at: isoFromInput(form.pickup_appointment_at),
        delivery_appointment_at: isoFromInput(form.delivery_appointment_at),
        // Sent only when the role owns these columns. The callable drops them
        // again for any other role, so hiding the inputs stays cosmetic and the
        // permission lives on the server.
        pier_pass_cents: canCreateWithFinance ? centsFromInput(form.pier_pass_cents) : 0,
        ctf_cents: canCreateWithFinance ? centsFromInput(form.ctf_cents) : 0,
        demurrage_cents: canCreateWithFinance ? centsFromInput(form.demurrage_cents) : 0,
        exam_fee_cents: canCreateWithFinance ? centsFromInput(form.exam_fee_cents) : 0,
        service_fee_cents: canCreateWithFinance ? centsFromInput(form.service_fee_cents) : 0,
        per_diem_cents: canCreateWithFinance ? centsFromInput(form.per_diem_cents) : 0,
        charge_remarks: canCreateWithFinance ? (form.charge_remarks || '') : '',
        remarks: form.remarks || '',
      },
    });
    toast('Container created.');
    routeTo('#/dashboard');
  });
}

async function renderEditStatus(id) {
  const snap = await getDoc(doc(db, 'containers', id));
  const container = snap.exists() ? { id: snap.id, ref: snap.ref, ...snap.data() } : null;
  await loadCustomers();
  if (!container) {
    shell('<h1>Container not found</h1>');
    return;
  }
  if ((container.closed_at || container.locked) && state.role !== 'admin') {
    shell(`
      <h1>Container Locked</h1>
      <section class="panel">
        <p>This container is closed and cannot be edited.</p>
        <button class="secondary" onclick="location.hash='#/container/${id}'">Back</button>
      </section>
    `);
    return;
  }
  const selectedCustomerId = matchingCustomerId(container);
  const terminalStatus = container.status === 'empty_returned' && container.is_empty_returned === true;
  const customerField = state.role === 'admin'
    ? `<label><span>Customer Account</span><select name="customer_id" required>${customerSelectOptions(selectedCustomerId)}</select></label>`
    : `<label><span>Customer Account</span><input value="${escapeHtml(container.customer_name || container.customer_email || selectedCustomerId || '')}" readonly aria-readonly="true"><input name="customer_id" type="hidden" value="${escapeHtml(selectedCustomerId || '')}"></label>`;
  const statusField = terminalStatus
    ? `<label><span>Status</span><input value="${escapeHtml(statusLabel('empty_returned'))}" readonly aria-readonly="true"><input name="status" type="hidden" value="empty_returned"></label>`
    : `<label><span>Status</span><select name="status">${normalStatuses.map((status) => `<option value="${status}" ${status === container.status ? 'selected' : ''}>${statusLabel(status)}</option>`).join('')}</select></label>`;
  shell(`
    <h1>Edit Container Status</h1>
    <form class="panel form-grid" id="statusForm">
      <label><span>Shipment / Job</span><input name="job_no" value="${escapeHtml(container.job_no || container.shipment_name || '')}" required></label>
      <label><span>Container No</span><input name="container_no" value="${escapeHtml(container.container_no || '')}" required></label>
      <label><span>DC Container No</span><input name="dc_container_no" value="${escapeHtml(container.dc_container_no || '')}"></label>
      ${customerField}
      ${statusField}
      <label><span>Current Stage</span><input name="current_stage" value="${escapeHtml(container.current_stage || container.status || '')}"></label>
      <label><span>Event Time</span><input name="event_at" type="datetime-local" required></label>
      <label class="wide"><span>Note</span><textarea name="note" required>${escapeHtml(container.last_status_note || '')}</textarea></label>
      <div class="actions wide"><button type="submit">Save Status Update</button></div>
    </form>
  `);
  const eventInput = document.querySelector('[name="event_at"]');
  eventInput.value = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  document.getElementById('statusForm').addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.target).entries());
    const status = form.status;
    const eventAt = timestampFromInput(form.event_at);
    const canCorrectCustomerIdentity = state.role === 'admin';
    let customer = null;
    let linkedCustomer = null;
    if (canCorrectCustomerIdentity) {
      customer = findCustomerSelection(form.customer_id);
      if (!customer) {
        toast('Select a customer before saving container.');
        return;
      }
      linkedCustomer = await resolveCustomerAccount(customer);
      if (!linkedCustomer?.accountUid) {
        toast('Selected customer is not linked to a login account yet. Add the customer account email or create the customer user first.');
        return;
      }
    }
    const customerIdentityPatch = buildStatusCustomerIdentityPatch({
      role: state.role,
      customer,
      linkedCustomer,
    });
    const customerNameForSearch = canCorrectCustomerIdentity ? customer.name : container.customer_name;
    const customerEmailForSearch = canCorrectCustomerIdentity ? linkedCustomer.accountEmail : container.customer_email;
    const keywords = [form.container_no, form.dc_container_no, form.job_no, container.master_bl_no, container.ssl, container.terminal_name, customerNameForSearch, customerEmailForSearch]
      .map(uppercaseTrim)
      .filter(Boolean);
    const patch = {
      ...customerIdentityPatch,
      shipment_name: form.job_no,
      job_no: form.job_no,
      container_no: form.container_no,
      dc_container_no: form.dc_container_no || '',
      container_no_upper: uppercaseTrim(form.container_no),
      status,
      current_stage: form.current_stage || status,
      last_status_note: form.note,
      search_keywords: Array.from(new Set(keywords)),
      updated_at: serverTimestamp(),
      updated_by: state.user?.email || state.user?.uid || '',
      updated_by_uid: state.user?.uid || '',
      updated_by_email: state.user?.email || '',
    };
    if (['empty_returned', 'empty_returned_to_terminal'].includes(status)) {
      patch.is_empty_returned = true;
      patch.is_past_due = false;
      patch.urgent_handle = false;
      patch.current_stage = status === 'empty_returned_to_terminal' ? 'completed' : (form.current_stage || status);
      patch.returned_at = eventAt;
      patch.empty_returned_at = eventAt;
    } else if (status === 'past_due') {
      patch.is_empty_returned = false;
      patch.is_past_due = true;
      patch.urgent_handle = true;
    } else if (status === 'cancelled') {
      patch.is_empty_returned = false;
      patch.is_past_due = false;
      patch.urgent_handle = false;
    } else {
      patch.is_empty_returned = false;
      patch.is_past_due = false;
      patch.urgent_handle = false;
    }
    const batch = writeBatch(db);
    batch.update(container.ref, patch);
    batch.set(doc(collection(db, 'containers', id, 'events')), {
      container_ref: container.ref,
      status,
      current_stage: form.current_stage || status,
      note: form.note,
      event_at: eventAt,
      created_by: doc(db, 'users', state.user.uid),
      created_at: serverTimestamp(),
    });
    await batch.commit();
    toast('Status updated.');
    routeTo(`#/container/${id}`);
  });
}

async function renderAssignDriver(id) {
  let snap;
  try {
    snap = await getDoc(doc(db, 'containers', id));
  } catch (_) {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel" role="alert"><p data-i18n="dispatch.statusLoadError">${escapeHtml(t('dispatch.statusLoadError'))}</p></section>
    `);
    return;
  }
  const container = snap.exists() ? { id: snap.id, ref: snap.ref, ...snap.data() } : null;
  if (!container) {
    shell('<h1>Container not found</h1>');
    return;
  }
  let openingState;
  try {
    const taskSnap = await getDocs(query(
      collection(db, 'dispatch_tasks'),
      where('container_ref', '==', container.ref),
      where('archived_by_restore', '==', false),
      limit(101),
    ));
    openingState = assignmentOpeningState(
      container,
      taskSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
    );
  } catch (_) {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel" role="alert"><p data-i18n="dispatch.statusLoadError">${escapeHtml(t('dispatch.statusLoadError'))}</p></section>
    `);
    return;
  }
  if (openingState.state === 'terminal') {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel" role="status">
        <h2>Dispatch unavailable</h2>
        <p>This Container is already complete or cancelled and cannot receive a new Dispatch Task.</p>
        <button class="secondary" type="button" onclick="location.hash='#/container/${escapeHtml(id)}'">Back to Container</button>
      </section>
    `);
    return;
  }
  if (openingState.state === 'anomaly') {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel" role="alert">
        <h2 data-i18n="dispatch.anomalyTitle">${escapeHtml(t('dispatch.anomalyTitle'))}</h2>
        <p data-i18n="dispatch.error.ASSIGNMENT_DATA_ANOMALY">${escapeHtml(t('dispatch.error.ASSIGNMENT_DATA_ANOMALY'))}</p>
      </section>
    `);
    return;
  }
  if (openingState.state === 'active_conflict') {
    const activeTask = openingState.task;
    const canCancel = canManage() && ['admin', 'dispatcher'].includes(state.role);
    const updatedAt = activeTask?.updated_at?.toDate?.();
    const reasons = cancellationReasonOptions(getLocale());
    if (canCancel && (!(updatedAt instanceof Date) || !Number.isFinite(updatedAt.getTime()))) {
      shell(`
        <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
        <section class="panel" role="alert">
          <h2 data-i18n="dispatch.anomalyTitle">${escapeHtml(t('dispatch.anomalyTitle'))}</h2>
          <p data-i18n="dispatch.cancel.error.CANCELLATION_DATA_ANOMALY">${escapeHtml(t('dispatch.cancel.error.CANCELLATION_DATA_ANOMALY'))}</p>
        </section>`);
      return;
    }
    const cancellationRequestId = canCancel ? createCancellationRequestId(crypto) : null;
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel dispatch-cancellation" aria-labelledby="activeTaskHeading">
        <h2 data-i18n="dispatch.activeConflictTitle">${escapeHtml(t('dispatch.activeConflictTitle'))}</h2>
        <p data-i18n="dispatch.error.ASSIGNMENT_ACTIVE_CONFLICT">${escapeHtml(t('dispatch.error.ASSIGNMENT_ACTIVE_CONFLICT'))}</p>
        <div class="dispatch-current-task" id="activeTaskHeading">
          <strong>${escapeHtml(activeTask?.driver_name || t('common.unknown'))}</strong>
          <span>${escapeHtml(activeTask?.leg_type || '')}</span>
          <span>${escapeHtml(activeTask?.status || '')}</span>
        </div>
        ${canCancel && updatedAt instanceof Date && Number.isFinite(updatedAt.getTime()) ? `
          <form id="cancelDispatchForm" class="form-grid" novalidate>
            <p class="wide notice" id="cancelHistoryWarning" data-i18n="dispatch.cancel.historyWarning">${escapeHtml(t('dispatch.cancel.historyWarning'))}</p>
            <label class="wide"><span data-i18n="dispatch.cancel.reason">${escapeHtml(t('dispatch.cancel.reason'))}</span>
              <select name="cancel_reason_code" required aria-describedby="cancelHistoryWarning">
                <option value="">${escapeHtml(t('dispatch.cancel.selectReason'))}</option>
                ${reasons.map((reason) => `<option value="${escapeHtml(reason.code)}">${escapeHtml(reason.label)}</option>`).join('')}
              </select>
            </label>
            <label class="wide" id="cancelExplanationField" hidden><span data-i18n="dispatch.cancel.explanation">${escapeHtml(t('dispatch.cancel.explanation'))}</span>
              <textarea name="cancel_reason_explanation" maxlength="500"></textarea>
            </label>
            <p class="wide muted" id="cancelDispatchError" role="alert" aria-live="assertive"></p>
            <div class="actions wide">
              <button class="danger" id="cancelDispatchSubmit" type="submit" data-i18n="dispatch.cancel.confirm">${escapeHtml(t('dispatch.cancel.confirm'))}</button>
              <button class="secondary" type="button" onclick="location.hash='#/container/${escapeHtml(id)}'">${escapeHtml(t('dispatch.viewCurrentTask'))}</button>
            </div>
          </form>
        ` : `<p class="muted" data-i18n="dispatch.cancel.notAuthorized">${escapeHtml(t('dispatch.cancel.notAuthorized'))}</p>`}
      </section>
    `);
    const cancelForm = document.getElementById('cancelDispatchForm');
    if (cancelForm) {
      const reasonSelect = cancelForm.elements.cancel_reason_code;
      const explanationField = document.getElementById('cancelExplanationField');
      const explanationInput = cancelForm.elements.cancel_reason_explanation;
      const submitButton = document.getElementById('cancelDispatchSubmit');
      const errorElement = document.getElementById('cancelDispatchError');
      reasonSelect.addEventListener('change', () => {
        const isOther = reasonSelect.value === 'other';
        explanationField.hidden = !isOther;
        explanationInput.required = isOther;
        if (!isOther) explanationInput.value = '';
      });
      const controller = createCancellationRetryController(async (payload) => {
        const response = await cancelDispatchAssignment(payload);
        if (response?.data?.taskId !== activeTask.id || response?.data?.status !== 'cancelled') {
          throw new Error('Unconfirmed cancellation result');
        }
        return response;
      });
      cancelForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        errorElement.textContent = '';
        let payload = {};
        if (!controller.hasAmbiguousRetry) {
          try {
            payload = buildDispatchCancellationRequest({
              taskId: activeTask.id,
              cancelReasonCode: reasonSelect.value,
              cancelReasonExplanation: explanationInput.value,
              expectedStatus: activeTask.status,
              expectedUpdatedAt: updatedAt,
              requestId: cancellationRequestId,
            });
          } catch (_) {
            errorElement.textContent = t('dispatch.cancel.error.CANCELLATION_REASON_INVALID');
            setCancellationFormPending(cancelForm, false);
            submitButton.textContent = t('dispatch.cancel.confirm');
            return;
          }
        }
        setCancellationFormPending(cancelForm, true);
        submitButton.textContent = controller.hasAmbiguousRetry
          ? t('dispatch.cancel.retrying')
          : t('dispatch.cancel.cancelling');
        try {
          await controller.submit(payload);
          cancelForm.innerHTML = `
            <div class="wide dispatch-cancellation-success" role="status">
              <h3 data-i18n="dispatch.cancel.success">${escapeHtml(t('dispatch.cancel.success'))}</h3>
              <p data-i18n="dispatch.cancel.separateAssignment">${escapeHtml(t('dispatch.cancel.separateAssignment'))}</p>
              <button type="button" id="continueNewAssignment" data-i18n="dispatch.cancel.continue">${escapeHtml(t('dispatch.cancel.continue'))}</button>
            </div>`;
          document.getElementById('continueNewAssignment').addEventListener('click', () => {
            routeTo(`#/assign-driver/${id}`);
            void renderAssignDriver(id);
          });
        } catch (error) {
          const code = cancellationErrorCode(error);
          errorElement.textContent = t(`dispatch.cancel.error.${code}`);
          if (controller.hasAmbiguousRetry) {
            setCancellationFormPending(cancelForm, false, { retryOnly: true, submit: submitButton });
            submitButton.textContent = t('dispatch.cancel.retry');
          } else {
            setCancellationFormPending(cancelForm, false);
            submitButton.textContent = t('dispatch.cancel.confirm');
          }
        }
      });
    }
    return;
  }
  let drivers;
  let driverListTruncated = false;
  let driverNextCursor = null;
  const driverSearch = hashParams().get('driverSearch') || '';
  const driverCursorName = hashParams().get('driverCursorName') || '';
  const driverCursorId = hashParams().get('driverCursorId') || '';
  try {
    const response = await listEligibleDispatchDrivers({
      search: driverSearch,
      cursorName: driverCursorName,
      cursorId: driverCursorId,
    });
    drivers = Array.isArray(response?.data?.drivers)
      ? response.data.drivers.map((driver) => ({ id: driver.id, full_name: driver.fullName }))
      : [];
    driverListTruncated = response?.data?.truncated === true;
    driverNextCursor = response?.data?.nextCursor || null;
  } catch (_) {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      <section class="panel" role="alert">
        <p data-i18n="dispatch.driverListError">${escapeHtml(t('dispatch.driverListError'))}</p>
        <button class="secondary" type="button" onclick="location.reload()" data-i18n="common.retry">${escapeHtml(t('common.retry'))}</button>
      </section>
    `);
    return;
  }
  const driverSearchMarkup = `
    <form class="panel toolbar" id="driverSearchForm">
      <label><span data-i18n="dispatch.searchDrivers">${escapeHtml(t('dispatch.searchDrivers'))}</span><input name="driver_search" value="${escapeHtml(driverSearch)}" maxlength="80"></label>
      <button class="secondary" type="submit" data-i18n="common.search">${escapeHtml(t('common.search'))}</button>
      ${driverListTruncated ? `<p class="muted" data-i18n="dispatch.driverListTruncated">${escapeHtml(t('dispatch.driverListTruncated'))}</p>` : ''}
      ${driverNextCursor ? `<button class="secondary" type="button" id="driverNextPage" data-name="${escapeHtml(driverNextCursor.name)}" data-id="${escapeHtml(driverNextCursor.id)}" data-i18n="dispatch.nextDrivers">${escapeHtml(t('dispatch.nextDrivers'))}</button>` : ''}
    </form>
  `;
  if (drivers.length === 0) {
    shell(`
      <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
      ${driverSearchMarkup}
      <section class="panel" role="status">
        <p data-i18n="dispatch.noEligibleDrivers">${escapeHtml(t('dispatch.noEligibleDrivers'))}</p>
        <button class="secondary" type="button" onclick="history.back()">${escapeHtml(t('common.back'))}</button>
      </section>
    `);
    document.getElementById('driverSearchForm').addEventListener('submit', (event) => {
      event.preventDefault();
      const value = new FormData(event.target).get('driver_search') || '';
      location.hash = `#/assign-driver/${id}?driverSearch=${encodeURIComponent(value)}`;
    });
    document.getElementById('driverNextPage')?.addEventListener('click', (event) => {
      location.hash = `#/assign-driver/${id}?driverSearch=${encodeURIComponent(driverSearch)}&driverCursorName=${encodeURIComponent(event.currentTarget.dataset.name)}&driverCursorId=${encodeURIComponent(event.currentTarget.dataset.id)}`;
    });
    return;
  }
  const permittedLegs = legTypes.filter((leg) => (
    container.current_leg ? leg === container.current_leg : ['load_out_to_customer', 'load_out_to_yard'].includes(leg)
  ));
  shell(`
    <h1 data-i18n="dispatch.title">${escapeHtml(t('dispatch.title'))}</h1>
    <p class="notice" data-i18n="dispatch.authorityNotice">${escapeHtml(t('dispatch.authorityNotice'))}</p>
    ${driverSearchMarkup}
    <form class="panel form-grid" id="assignForm" novalidate>
      <label><span data-i18n="dispatch.driver">${escapeHtml(t('dispatch.driver'))}</span><select name="driver_id" required><option value="">${escapeHtml(t('dispatch.selectDriver'))}</option>${options(drivers, 'full_name')}</select><small data-field-error></small></label>
      <label><span data-i18n="dispatch.legType">${escapeHtml(t('dispatch.legType'))}</span><select name="leg_type" required>${permittedLegs.map((leg) => `<option value="${leg}">${escapeHtml(leg)}</option>`).join('')}</select><small data-field-error></small></label>
      <label><span>Origin Name</span><input name="origin_name" required><small data-field-error></small></label>
      <label><span>Origin Address</span><input name="origin_address" required><small data-field-error></small></label>
      <label><span>Origin Type</span><select name="origin_type"><option>terminal</option><option>yard</option><option>customer</option></select></label>
      <label><span>Destination Name</span><input name="destination_name" required><small data-field-error></small></label>
      <label><span>Destination Address</span><input name="destination_address" required><small data-field-error></small></label>
      <label><span>Destination Type</span><select name="destination_type"><option>customer</option><option>yard</option><option>terminal</option></select></label>
      <label><span data-i18n="dispatch.scheduledStart">${escapeHtml(t('dispatch.scheduledStart'))}</span><input name="scheduled_start_at" type="datetime-local" required><small data-field-error></small></label>
      <label class="wide"><span>Dispatcher Notes</span><textarea name="dispatcher_notes"></textarea></label>
      <p class="wide muted" id="assignError" role="alert" aria-live="assertive"></p>
      <div class="actions wide"><button type="submit" id="assignSubmit" data-i18n="dispatch.confirm">${escapeHtml(t('dispatch.confirm'))}</button></div>
    </form>
  `);
  const formElement = document.getElementById('assignForm');
  bindRequiredFieldValidation(formElement);
  document.getElementById('driverSearchForm').addEventListener('submit', (event) => {
    event.preventDefault();
    const value = new FormData(event.target).get('driver_search') || '';
    location.hash = `#/assign-driver/${id}?driverSearch=${encodeURIComponent(value)}`;
  });
  document.getElementById('driverNextPage')?.addEventListener('click', (event) => {
    location.hash = `#/assign-driver/${id}?driverSearch=${encodeURIComponent(driverSearch)}&driverCursorName=${encodeURIComponent(event.currentTarget.dataset.name)}&driverCursorId=${encodeURIComponent(event.currentTarget.dataset.id)}`;
  });
  const submitButton = document.getElementById('assignSubmit');
  const errorElement = document.getElementById('assignError');
  const guard = createSingleSubmissionGuard(async (payload) => createDispatchAssignment(payload));
  formElement.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearWorkflowValidation(formElement);
    if (!formElement.reportValidity()) return;
    const form = Object.fromEntries(new FormData(event.target).entries());
    errorElement.textContent = '';
    submitButton.disabled = true;
    submitButton.textContent = t('dispatch.creating');
    try {
      const payload = buildDispatchAssignmentRequest({
        containerId: id,
        driverId: form.driver_id,
        legType: form.leg_type,
        scheduledStartAt: form.scheduled_start_at,
        originName: form.origin_name,
        originAddress: form.origin_address,
        originType: form.origin_type,
        destinationName: form.destination_name,
        destinationAddress: form.destination_address,
        destinationType: form.destination_type,
        dispatcherNotes: form.dispatcher_notes,
      });
      const response = await guard.submit(payload);
      const result = response?.data;
      if (!result?.taskId || result.status !== 'assigned') throw new Error('Unconfirmed assignment result');
      const authoritativeTask = await getDoc(doc(db, 'dispatch_tasks', result.taskId));
      if (!authoritativeTask.exists() || authoritativeTask.data().driver_uid !== result.driverUid) {
        throw new Error('Unconfirmed assignment result');
      }
      toast(t('dispatch.success'));
      routeTo(`#/container/${id}`);
    } catch (error) {
      const code = assignmentErrorCode(error);
      errorElement.textContent = t(`dispatch.error.${code}`);
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = t('dispatch.confirm');
    }
  });
}

async function renderAdminUsers() {
  const snap = await getDocs(query(
    collection(db, 'users'),
    activeRestoreConstraint(),
    orderBy('display_name', 'asc'),
  ));
  const users = snap.docs.map((item) => ({ id: item.id, ref: item.ref, ...item.data() }));
  const drivers = await loadDrivers().catch(() => []);
  shell(settingsPageMarkup({ users, roles: userRoles, drivers }));
  const searchController = mountSettingsUserSearch(root);
  const driversController = mountSettingsDrivers(root, {
    // updateBusinessDriverRecord rewrites the record whole, so the payload carries every field it already has;
    // only the status moves.
    async onToggle({ driverId, enable }) {
      const driver = drivers.find(({ id }) => id === driverId);
      if (!driver) throw new Error('Driver not found.');
      const result = await updateBusinessDriverRecord({
        driver_id: driverId,
        full_name: String(driver.full_name || '').trim(),
        driver_code: String(driver.driver_code || driverId).trim(),
        phone: String(driver.phone || ''),
        real_email: String(driver.real_email || '').trim().toLowerCase(),
        wechat: String(driver.wechat || ''),
        truck_no: String(driver.truck_no || driver.tractor_no || ''),
        trailer_no: String(driver.trailer_no || ''),
        license_no: String(driver.license_no || ''),
        current_status: enable ? 'available' : 'inactive',
        notes: String(driver.notes || ''),
      });
      if (result?.data?.updated !== true) throw new Error('Driver was not updated.');
      toast(enable ? `${driver.full_name} 已启用，回到司机菜单` : `${driver.full_name} 已停用，不再出现在司机菜单`);
    },
    async onCreate(values) {
      const result = await createDriverAccount({
        login_name: String(values.login_name || ''),
        full_name: String(values.full_name || ''),
        password: String(values.password || ''),
        tractor_no: String(values.tractor_no || ''),
      });
      toast(`已添加司机 ${values.full_name}，登录 ${result?.data?.email || ''}`);
    },
    onDone: () => {
      renderAdminUsers().catch((error) => toast(error?.message || 'Reload failed.'));
    },
    onError: (error) => toast(error?.message || 'Driver update failed.'),
  });
  const roleController = mountSettingsRoleChanges(root, {
    onConfirm: async ({ uid, nextRole }) => setUserRole({ uid, role: nextRole }),
    onSuccess: () => {
      toast(t('admin.users.roleUpdated'));
      renderAdminUsers().catch((error) => toast(error?.message || t('admin.users.roleUpdateFailed')));
    },
    onError: (error) => {
      if (!['ROLE_UNCHANGED'].includes(error?.message)) {
        toast(error?.message || t('admin.users.roleUpdateFailed'));
      }
    },
  });
  const auditController = mountAdminAuditLog({
    container: document.getElementById('adminAuditLog'),
    source: createAdminAuditSource(),
    locale: getLocale(),
    setLocalePreference: selectLocale,
  });
  state.pageCleanup = () => {
    searchController.destroy();
    driversController.destroy();
    roleController.destroy();
    auditController.destroy();
  };
}

async function renderDispatchTaskEdit(id) {
  const snap = await getDoc(doc(db, 'dispatch_tasks', id));
  if (!snap.exists()) {
    shell(`<section class="panel" role="alert"><p>${escapeHtml(t('dispatch.edit.error.EDIT_TASK_NOT_FOUND'))}</p></section>`);
    return;
  }
  const task = { id: snap.id, ref: snap.ref, ...snap.data() };
  const expectedUpdatedAt = task.updated_at?.toDate?.();
  const containerId = task.container_ref?.id || '';
  const terminal = ['completed', 'cancelled', 'failed'].includes(task.status);
  if (!['admin', 'dispatcher'].includes(state.role) || terminal
    || !(expectedUpdatedAt instanceof Date) || !Number.isFinite(expectedUpdatedAt.getTime())) {
    shell(`<section class="panel" role="alert" data-testid="dispatch-edit-blocked">
      <h1>${escapeHtml(t('dispatch.edit.title'))}</h1>
      <p>${escapeHtml(t(terminal ? 'dispatch.edit.error.EDIT_TERMINAL_TASK' : 'dispatch.edit.error.EDIT_PERMISSION_DENIED'))}</p>
      <button class="secondary" onclick="location.hash='#/container/${escapeHtml(containerId)}'">${escapeHtml(t('common.back'))}</button>
    </section>`);
    return;
  }
  const routeEditable = ['assigned', 'accepted'].includes(task.status);
  const requestId = crypto.randomUUID();
  const origin = task.origin || {};
  const destination = task.destination || {};
  const readonly = routeEditable ? '' : 'readonly aria-readonly="true"';
  const selectDisabled = routeEditable ? '' : 'disabled aria-disabled="true"';
  shell(`
    <h1 data-i18n="dispatch.edit.title">${escapeHtml(t('dispatch.edit.title'))}</h1>
    <p class="notice" data-i18n="dispatch.edit.notice">${escapeHtml(t('dispatch.edit.notice'))}</p>
    <form class="panel form-grid" id="dispatchEditForm" data-testid="dispatch-edit-form" novalidate>
      <label><span data-i18n="dispatch.edit.scheduledStart">${escapeHtml(t('dispatch.edit.scheduledStart'))}</span><input data-testid="edit-scheduled-start" name="scheduled_start_at" type="datetime-local" value="${escapeHtml(datetimeLocalValue(task.scheduled_start_at))}" required ${readonly}></label>
      <label><span data-i18n="dispatch.edit.scheduledEnd">${escapeHtml(t('dispatch.edit.scheduledEnd'))}</span><input data-testid="edit-scheduled-end" name="scheduled_end_at" type="datetime-local" value="${escapeHtml(datetimeLocalValue(task.scheduled_end_at))}" ${readonly}></label>
      <label><span data-i18n="dispatch.edit.originName">${escapeHtml(t('dispatch.edit.originName'))}</span><input name="origin_name" value="${escapeHtml(origin.name)}" required ${readonly}></label>
      <label><span data-i18n="dispatch.edit.originAddress">${escapeHtml(t('dispatch.edit.originAddress'))}</span><input name="origin_address" value="${escapeHtml(origin.address)}" required ${readonly}></label>
      <label><span data-i18n="dispatch.edit.originType">${escapeHtml(t('dispatch.edit.originType'))}</span><select name="origin_type" ${selectDisabled}>${['terminal', 'yard', 'customer'].map((type) => `<option value="${type}" ${origin.type === type ? 'selected' : ''}>${escapeHtml(t(`dispatch.point.${type}`))}</option>`).join('')}</select></label>
      <label><span data-i18n="dispatch.edit.destinationName">${escapeHtml(t('dispatch.edit.destinationName'))}</span><input name="destination_name" value="${escapeHtml(destination.name)}" required ${readonly}></label>
      <label><span data-i18n="dispatch.edit.destinationAddress">${escapeHtml(t('dispatch.edit.destinationAddress'))}</span><input name="destination_address" value="${escapeHtml(destination.address)}" required ${readonly}></label>
      <label><span data-i18n="dispatch.edit.destinationType">${escapeHtml(t('dispatch.edit.destinationType'))}</span><select name="destination_type" ${selectDisabled}>${['terminal', 'yard', 'customer'].map((type) => `<option value="${type}" ${destination.type === type ? 'selected' : ''}>${escapeHtml(t(`dispatch.point.${type}`))}</option>`).join('')}</select></label>
      <label class="wide"><span data-i18n="dispatch.edit.notes">${escapeHtml(t('dispatch.edit.notes'))}</span><textarea data-testid="edit-dispatcher-notes" name="dispatcher_notes" maxlength="2000">${escapeHtml(task.dispatcher_notes)}</textarea></label>
      <p class="wide muted" id="dispatchEditError" role="alert" aria-live="assertive"></p>
      <div class="actions wide"><button data-testid="submit-dispatch-edit" type="submit">${escapeHtml(t('dispatch.edit.save'))}</button><button class="secondary" type="button" onclick="location.hash='#/container/${escapeHtml(containerId)}'">${escapeHtml(t('common.back'))}</button></div>
    </form>`);
  const form = document.getElementById('dispatchEditForm');
  const submit = form.querySelector('[data-testid="submit-dispatch-edit"]');
  const errorElement = document.getElementById('dispatchEditError');
  let pending = false;
  let retryPayload = null;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending) return;
    pending = true;
    errorElement.textContent = '';
    submit.disabled = true;
    submit.textContent = t('dispatch.edit.saving');
    try {
      if (!retryPayload) {
        const values = Object.fromEntries(new FormData(form).entries());
        retryPayload = buildDispatchEditRequest({
          taskId: id, requestId, expectedUpdatedAt,
          scheduledStartAt: values.scheduled_start_at,
          scheduledEndAt: values.scheduled_end_at,
          originName: values.origin_name, originAddress: values.origin_address,
          originType: values.origin_type || origin.type,
          destinationName: values.destination_name, destinationAddress: values.destination_address,
          destinationType: values.destination_type || destination.type,
          dispatcherNotes: values.dispatcher_notes,
        });
      }
      const response = await editDispatchTask(retryPayload);
      if (response?.data?.taskId !== id) throw new Error('Unconfirmed dispatch edit');
      retryPayload = null;
      toast(t('dispatch.edit.success'));
      routeTo(`#/container/${containerId}`);
    } catch (error) {
      const code = dispatchEditErrorCode(error);
      if (code !== 'EDIT_WRITE_FAILED') retryPayload = null;
      errorElement.textContent = t(`dispatch.edit.error.${code}`);
    } finally {
      pending = false;
      submit.disabled = false;
      submit.textContent = t('dispatch.edit.save');
    }
  });
}

async function renderDriverTaskDetail(id) {
  const workspaceRoute = Boolean(state.workspaceCaptureHost);
  const renderScope = state.workspaceCaptureHost || document;
  const queueView = isWorkQueueDetailView();
  let snap;
  try {
    snap = await getDoc(doc(db, 'dispatch_tasks', id));
  } catch {
    shell('<section class="workflow-page"><div class="workflow-empty is-error"><h1>Task unavailable</h1><p>This task does not exist or is not assigned to you.</p><button class="secondary" data-workflow-back>Back</button></div></section>');
    bindWorkflowBack();
    return;
  }
  if (!snap.exists()) {
    shell(`<section class="workflow-page"><div class="workflow-empty is-error"><h1>Task unavailable</h1><p>This task does not exist or is not assigned to you.</p><button class="secondary" data-workflow-back>Back</button></div></section>`);
    bindWorkflowBack();
    return;
  }
  const task = { id: snap.id, ref: snap.ref, ...snap.data() };
  const nextStatus = {
    assigned: ['accepted', 'dispatch.progress.accept'],
    accepted: ['en_route', 'dispatch.progress.start'],
    en_route: ['arrived', 'dispatch.progress.arrived'],
  }[task.status] || null;
  const active = ['assigned', 'accepted', 'en_route', 'arrived'].includes(task.status);
  const evidenceKind = evidenceKindForLeg(task.leg_type);
  const evidence = evidenceKind ? await loadEvidenceAssets(task.id) : { items: [], cleanup: () => {} };
  shell(`
    <section class="workflow-page transport-task-detail">
      ${queueView ? workQueueDetailNavigationMarkup() : ''}
      <header class="workflow-page-header"><div><p class="eyebrow">Transport Task</p><h1>${escapeHtml(task.container_no || 'Container')}</h1><p>${escapeHtml(String(task.leg_type || '').replaceAll('_', ' '))}</p></div>${queueView ? '' : '<button class="secondary" type="button" data-workflow-back>Back</button>'}</header>
      <section class="panel workflow-task-summary">
        <div><span>Status</span><strong class="status">${escapeHtml(task.status)}</strong></div>
        <div><span>Scheduled start</span><strong>${escapeHtml(formatLocalizedDate(dateValue(task.scheduled_start_at), getLocale()))}</strong></div>
        <div><span>Chassis #</span><strong>${escapeHtml(task.chassis_no || 'Not provided')}</strong></div>
        <div><span>Origin</span><strong>${escapeHtml(task.origin?.name || 'Not provided')}</strong><small>${escapeHtml(task.origin?.address || '')}</small></div>
        <div><span>Destination</span><strong>${escapeHtml(task.destination?.name || 'Not provided')}</strong><small>${escapeHtml(task.destination?.address || '')}</small></div>
        <div><span>Dispatcher notes</span><strong>${escapeHtml(task.dispatcher_notes || 'None')}</strong></div>
      </section>
      ${canUpdateDispatchTasks() && active ? `<section class="panel"><h2>Task progress</h2><p>Use the next available step. Completed steps cannot be selected again.</p><p class="workflow-form-error" id="driverProgressError" role="alert"></p><div class="actions" data-testid="driver-progress-actions">${nextStatus ? `<button data-next="${nextStatus[0]}" data-testid="progress-${nextStatus[0]}">${escapeHtml(t(nextStatus[1]))}</button>` : ''}<button class="secondary danger" data-next="failed" data-testid="progress-failed">${escapeHtml(t('dispatch.progress.failed'))}</button></div></section>` : ''}
      ${evidenceKind ? `<section class="panel workflow-evidence-section"><h2>${evidenceKind === 'empty_return' ? 'Empty Return evidence' : 'POD evidence'}</h2>${evidenceMarkup(evidence.items)}</section>` : ''}
      ${canUpdateDispatchTasks() && active && evidenceKind ? `<form class="panel workflow-form" id="driverEvidenceForm" novalidate><h2>Upload ${evidenceKind === 'empty_return' ? 'Empty Return evidence' : 'POD'}</h2><label>Evidence image<input name="evidence_file" type="file" accept="image/*" required><small data-field-error></small></label><label>Image note<textarea name="note" maxlength="500" placeholder="What does this image prove?"></textarea><small data-field-error></small></label><p class="workflow-form-error" id="driverEvidenceError" role="alert"></p><div class="actions"><button type="submit">Upload evidence</button></div></form>` : ''}
      ${canUpdateDispatchTasks() && task.status === 'arrived' ? `<form class="panel workflow-form" id="driverCompleteForm" novalidate><h2>Complete Transport Task</h2>${evidenceKind ? `<label class="workflow-confirmation"><input name="evidence_confirmed" type="checkbox" required><span>I confirm all required ${evidenceKind === 'empty_return' ? 'Empty Return' : 'POD'} evidence has been uploaded.</span><small data-field-error></small></label>` : ''}<label>Transport notes <span class="muted">Optional</span><textarea name="driver_notes" maxlength="2000"></textarea><small data-field-error></small></label><p class="workflow-form-error" id="driverCompleteError" role="alert"></p><div class="actions"><button type="submit">Complete Transport Task</button></div></form>` : ''}
      ${task.status === 'completed' ? '<div class="workflow-empty is-complete"><h2>Transport Task completed</h2><p>This task is read-only.</p></div>' : ''}
    </section>
  `);
  bindWorkflowBack();
  registerActivePageCleanup(evidence.cleanup);
  const refreshDriverTaskDetail = async () => {
    if (workspaceRoute && state.taskWorkspaceController) {
      await state.taskWorkspaceController.refreshRoute(`#/driver-task/${id}`);
      return;
    }
    await renderDriverTaskDetail(id);
  };
  let pending = false;
  renderScope.querySelectorAll('[data-next]').forEach((button) => {
    button.addEventListener('click', async () => {
      if (pending) return;
      const status = button.dataset.next;
      const patch = { status, updated_at: serverTimestamp() };
      if (status === 'accepted') patch.accepted_at = serverTimestamp();
      if (status === 'en_route') patch.started_at = serverTimestamp();
      if (status === 'arrived') patch.arrived_at = serverTimestamp();
      const errorHost = renderScope.querySelector('#driverProgressError');
      try {
        pending = true;
        renderScope.querySelectorAll('[data-next]').forEach((control) => { control.disabled = true; });
        button.textContent = 'Updating…';
        await updateDoc(task.ref, patch);
        toast(t('dispatch.progress.updated'));
        await refreshDriverTaskDetail();
      } catch (error) {
        showWorkflowError(null, errorHost, error);
        pending = false;
        renderScope.querySelectorAll('[data-next]').forEach((control) => { control.disabled = false; });
      }
    });
  });
  const evidenceForm = renderScope.querySelector('#driverEvidenceForm');
  bindRequiredFieldValidation(evidenceForm);
  if (evidenceForm) evidenceForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !evidenceForm.reportValidity()) return;
    const submit = evidenceForm.querySelector('[type="submit"]');
    try {
      pending = true;
      submit.disabled = true;
      submit.textContent = 'Uploading…';
      await uploadWorkflowEvidence(
        task,
        evidenceForm.elements.evidence_file.files[0],
        evidenceForm.elements.note.value,
      );
      toast('Evidence uploaded and verified.');
      await refreshDriverTaskDetail();
    } catch (error) {
      showWorkflowError(evidenceForm, renderScope.querySelector('#driverEvidenceError'), error);
      pending = false;
      submit.disabled = false;
      submit.textContent = 'Upload evidence';
    }
  });
  const completeForm = renderScope.querySelector('#driverCompleteForm');
  bindRequiredFieldValidation(completeForm);
  if (completeForm) completeForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || !completeForm.reportValidity()) return;
    if (evidenceKind && !completeForm.elements.evidence_confirmed.checked) {
      showWorkflowError(completeForm, renderScope.querySelector('#driverCompleteError'),
        new WorkflowUiError('CONFIRMATION_REQUIRED', 'evidence_confirmed'));
      return;
    }
    const submit = completeForm.querySelector('[type="submit"]');
    try {
      pending = true;
      submit.disabled = true;
      submit.textContent = 'Completing…';
      const updatedAt = dateValue(task.updated_at);
      if (!updatedAt) throw new Error('Task update time is unavailable. Refresh and try again.');
      await completeDriverTask({
        task_id: task.id,
        request_id: crypto.randomUUID(),
        expected_status: 'arrived',
        expected_updated_at: updatedAt.toISOString(),
        driver_notes: completeForm.elements.driver_notes.value.trim(),
      });
      toast('Transport Task completed.');
      await state.taskWorkspaceController?.confirmRoute(`#/driver-task/${task.id}`);
      routeTo('#/dashboard');
    } catch (error) {
      showWorkflowError(completeForm, renderScope.querySelector('#driverCompleteError'), error);
      pending = false;
      submit.disabled = false;
      submit.textContent = 'Complete Transport Task';
    }
  });
  applySystemInputGlow(renderScope);
}

function profileWorkTime(millis) {
  if (!Number.isSafeInteger(millis)) return '—';
  return new Date(millis).toLocaleString(getLocale() === 'zh-CN' ? 'zh-CN' : 'en-US', {
    // 24-hour everywhere (Alan, 2026-08-11): '2-digit' alone still renders
    // "09:05 PM" under en-US, because the hour cycle is the locale's, not the
    // field width's.
    timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
}

async function loadProfileWorkStatus() {
  try {
    const result = await listMyWorkQueue({});
    const items = Array.isArray(result?.data?.items) ? result.data.items : [];
    const stateCount = (key) => items.filter((item) => item.queue_state === key).length;
    return {
      items: items.slice(0, 30).map((item) => ({
        time: profileWorkTime(item.relevant_at_millis),
        item: item.container_no || item.container_id || '—',
        status: [item.queue_state, item.next_action].filter(Boolean).join(' · '),
      })),
      summary: [
        { label: 'Total', value: items.length },
        { label: 'Action Required', value: stateCount('action_required') },
        { label: 'In Progress', value: stateCount('progressing') },
        { label: 'Completed', value: stateCount('completed') },
      ],
    };
  } catch (_) {
    return { items: [], summary: [] };
  }
}

async function renderProfile() {
  shell('<div class="profile-page-host" id="profilePageHost"></div>');
  const controller = mountProfilePage({
    host: document.getElementById('profilePageHost'),
    deps: {
      loadProfile: async () => {
        if (!state.userProfile) {
          const snap = await getDoc(doc(db, 'users', state.user.uid));
          state.userProfile = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        }
        return {
          profile: state.userProfile ?? {},
          user: { email: state.user?.email ?? '' },
          role: state.role ?? '',
        };
      },
      saveContact: async (payload) => {
        await updateDoc(doc(db, 'users', state.user.uid), {
          ...payload,
          updated_at: serverTimestamp(),
        });
        if (state.userProfile) Object.assign(state.userProfile, payload);
      },
      loadWorkStatus: loadProfileWorkStatus,
      t,
      locale: getLocale(),
    },
  });
  registerActivePageCleanup(() => controller.destroy());
}

async function renderCustomerQuotesAdmin() {
  const snap = await getDocs(query(
    collection(db, 'customers'),
    activeRestoreConstraint(),
    orderBy('name', 'asc'),
  ));
  const customers = snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((customer) => customer.active !== false && customer.account_uid);
  shell(`
    <section class="quote-admin-page">
      ${customers.length
    ? '<div class="quote-admin-host" id="quoteAdminHost"></div>'
    : '<p class="quote-admin-page__empty">No active linked customers yet. 尚无已关联登录账号的客户。</p>'}
    </section>
  `);
  const quoteControls = document.querySelector('[data-page-controls]');
  if (quoteControls && customers.length) {
    quoteControls.innerHTML = `<label class="quote-admin-page__picker"><span>Customer / 客户</span>
      <select id="quoteAdminCustomerSelect">
        <option value="__all__" data-label="All Customers / 全部客户">All Customers / 全部客户</option>
        ${customers.map((customer) => `<option value="${escapeHtml(customer.account_uid)}" data-label="${escapeHtml(customer.name || customer.account_uid)}">${escapeHtml(customer.name || customer.account_uid)}</option>`).join('')}
      </select>
    </label>`;
  }
  const select = document.getElementById('quoteAdminSelect') || document.getElementById('quoteAdminCustomerSelect');
  const host = document.getElementById('quoteAdminHost');
  if (!select || !host) return;
  let controller = null;

  // All Customers reads every customer's list through the same per-customer
  // callable, one call each, and merges them. No new backend surface and no new
  // permission: whoever can open this page could already read each of these
  // lists one at a time. It is a reading view - the add form and the per-row
  // Deactivate belong to a single customer, so they are not drawn here.
  const renderAllCustomers = async () => {
    const results = await Promise.all(customers.map(async (customer) => {
      const label = customer.name || customer.account_uid;
      try {
        const result = await listCustomerQuoteLocationsForCustomer({
          customer_uid: customer.account_uid,
        });
        return quoteLocationRows(result?.data?.locations)
          .map((row) => ({ ...row, customer_label: label }));
      } catch (error) {
        // One customer failing must not blank the other twenty. The row says so.
        return [{
          location_id: `error:${customer.account_uid}`,
          selection_label: t('quoteAdmin.loadFailed') || 'Could not be loaded',
          delivery_warehouse_address: '',
          quote_display: '',
          quote_terms: error?.message ?? '',
          active: false,
          revision: 0,
          customer_label: label,
        }];
      }
    }));
    const rows = results.flat().sort((left, right) => (
      left.customer_label.localeCompare(right.customer_label, 'en', { sensitivity: 'base' })
      || Number(right.active) - Number(left.active)
      || left.selection_label.localeCompare(right.selection_label, 'en', { sensitivity: 'base' })
    ));
    host.innerHTML = quoteLocationAdminMarkup({
      customerLabel: `${customers.length} customers / ${customers.length} 个客户`,
      rows,
      role: state.role,
      allCustomers: true,
    }, { t });
  };

  const mountForSelection = () => {
    controller?.destroy();
    controller = null;
    const option = select.selectedOptions[0];
    if (!option) return;
    if (option.value === '__all__') {
      void renderAllCustomers();
      return;
    }
    controller = mountQuoteLocationAdmin({
      host,
      customerUid: option.value,
      customerLabel: option.dataset.label || '',
      role: state.role,
      deps: {
        listLocations: (payload) => listCustomerQuoteLocationsForCustomer(payload).then((result) => result.data),
        upsertLocation: (payload) => manageCustomerQuoteLocation({ action: 'upsert', ...payload }).then((result) => result.data),
        deactivateLocation: (payload) => manageCustomerQuoteLocation({ action: 'deactivate', ...payload }).then((result) => result.data),
        t,
      },
    });
  };
  select.addEventListener('change', mountForSelection);
  mountForSelection();
  registerActivePageCleanup(() => controller?.destroy());
}

async function renderRoute() {
  if (!state.user && window.location.hash !== '#/login') {
    renderLogin();
    return;
  }
  const hash = window.location.hash || '#/login';
  const [path, param] = hash.replace('#/', '').split('?')[0].split('/');
  // Every route below awaits its data before it paints, and the Dashboard waits
  // for the whole fleet. Say so rather than showing a white window (Alan,
  // 2026-08-09).
  if (path !== 'login') showRouteLoading(document);
  try {
    // `return await`, not `return`. Every branch below hands back a promise, and
    // `try { return somePromise } finally { hide() }` runs the finally the moment
    // the promise is CREATED - not when it settles. So the ring was shown and
    // hidden in the same tick and nobody ever saw it, on any page, including the
    // Dashboard that waits for the whole fleet (Alan, 2026-08-11: "加载的时候
    // user 可以感受到看到"). Awaiting here is the whole fix; do not "simplify" it
    // back to a bare return.
    return await routeToPage(path, param);
  } catch (error) {
    console.error(error);
    shell(`<h1 data-i18n="error.somethingWrong">${escapeHtml(t('error.somethingWrong'))}</h1><p class="error">${escapeHtml(error.message)}</p>`);
  } finally {
    hideRouteLoading(document);
  }
}

async function routeToPage(path, param) {
  if (path === 'login') return renderLogin();
  if (['customers', 'drivers', 'driver'].includes(path)) return routeTo('#/dashboard');
  if (!currentRolePolicy()?.readableRoutes.includes(path)) return routeAfterLogin();
  if (path === 'dashboard') return renderDashboard();
  // The Todo workspace was only ever reached from the queue card, which is
  // gone. Turned away rather than left dangling on a bookmark.
  if (path === 'queue-action') return routeTo('#/dashboard');
  if (path === 'container') return renderContainerDetail(param);
  if (path === 'add-container') return renderAddContainer();
  if (path === 'edit-status') return renderEditStatus(param);
  if (path === 'assign-driver') return renderAssignDriver(param);
  // #/status is Company Overview now (Alan, 2026-08-08). The old analytics page
  // stays where it is - renderStatusAnalyticsPage and status-operations-page.mjs
  // are untouched - but nothing routes to it. readableRoutes already grants
  // 'status' to admin, dispatcher, clerk and finance and to no one else, so the
  // customer who reaches this line is turned away two lines above.
  if (path === 'status') return renderCompanyOverviewPage();
  // Team is frozen and hidden alongside Status (Alan, 2026-08-07).
  // renderTeamAttendancePage and team-attendance-page.mjs are untouched.
  if (path === 'team') return routeTo('#/dashboard');
  if (path === 'chassis') return renderChassisMapPage();
  if (path === 'customer-portal') return renderCustomerPortal();
  if (path === 'add-task') {
    routeTo('#/create/task');
    return;
  }
  if (path === 'create') {
    if (!state.taskWorkspaceController) await renderDashboard();
    // routeToPage is handed path and param only; `hash` here was renderRoute's local, so
    // opening or reloading any #/create/... address threw "hash is not defined".
    await state.taskWorkspaceController?.open(window.location.hash);
    return;
  }
  if (path === 'action') return renderContainerActionDetail(param);
  if (path === 'profile') return renderProfile();
  if (path === 'customer-quotes') return renderCustomerQuotesAdmin();
  if (path === 'admin-users') return renderAdminUsers();
  if (path === 'shipment-create') return renderShipmentCreation();
  if (path === 'edit-dispatch-task') return renderDispatchTaskEdit(param);
  if (path === 'driver-task') return renderDriverTaskDetail(param);
  return routeAfterLogin();
}

subscribeLocale(refreshLocalizedDom);
refreshLocalizedDom();
window.addEventListener('hashchange', renderRoute);

onAuthStateChanged(auth, async (user) => {
  const session = sessionLoadCoordinator.begin(user?.uid ?? null, {
    preferenceRevision: getLocalePreferenceRevision(),
  });
  state.user = user;
  state.role = null;
  state.claims = {};
  state.userProfile = null;
  state.liveRefresh?.destroy();
  state.liveRefresh = null;
  if (!user) {
    // Session boundary. An unsaved preference belongs to the account that made
    // it, so it must not survive into the next sign-in -- and once tenant
    // switching exists, carrying it across would leak a choice from one tenant
    // into another. The same call must be added to the ACO-03 view-context
    // switch when that lands.
    clearUnsavedLocale();
    setLocale(detectLocale({ profileLocale: null }), { persist: false, source: 'system' });
    renderLogin();
    return;
  }
  try {
    // Force the refresh (Alan, 2026-08-07). This used to read whatever ID token
    // the SDK had cached, and a cached token can be a revoked one: every password
    // in the system was reset, which revokes the refresh token behind any session
    // a browser had already persisted. The stale token then failed the very first
    // Firestore read and the catch below reported it as "permissions could not be
    // loaded" - to every account at once, on a page they had not signed into yet.
    //
    // One extra round trip, once, at sign-in. It buys immunity to every stale or
    // revoked token, which is a trade worth making at the front door.
    if (!(await loadSession(user, true, session))) return;
    if (!sessionLoadCoordinator.isCurrent(session) || state.user?.uid !== session.uid) return;
    // Sign-in used to spend 280ms acknowledging itself - the two fields going
    // green in sequence, then the exit - before the full-screen wash on the far
    // side. All of it is gone (Alan, 2026-08-07): the table is what was asked
    // for, and every frame between the press and the table is a frame in the way.
    if (!window.location.hash || window.location.hash === '#/login') {
      await routeAfterLogin();
    } else {
      await renderRoute();
    }
  } catch (sessionError) {
    if (!sessionLoadCoordinator.isCurrent(session) || state.user?.uid !== session.uid) return;
    // The message the operator sees says "permissions", but this catch also
    // wraps the first render - so a render that throws reports itself as a
    // permission failure and sends everyone chasing the wrong thing. Log what
    // actually happened (Alan, 2026-08-07).
    console.error('Unable to load authenticated session.', {
      code: sessionError?.code ?? null,
      name: sessionError?.name ?? null,
      message: sessionError?.message ?? String(sessionError),
      stage: state.role ? 'render' : 'session',
    });
    state.pendingLoginErrorKey = 'login.sessionError';
    try {
      await signOut(auth);
    } catch (_) {
      // Local state is still cleared below so an incomplete session cannot enter the app.
    }
    state.user = null;
    state.role = null;
    state.claims = {};
    state.userProfile = null;
    renderLogin();
    toast(t('login.sessionError'));
  }
});
